import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import ContractModal from '../components/ContractModal';
import GmailComposeModal from '../components/GmailComposeModal';
import MissedOpportunityModal from '../components/MissedOpportunityModal';
import CompMap from '../components/CompMap';
import CompDetailModal from '../components/CompDetailModal';
import { proxyPhoto, proxyThumb } from '../utils/photo';
import { yesNoUnknown, fmtHoa, fmtLotSize, fmtParking, taxRecordsLink } from '../utils/propertyFacts';
import { useLists, RowCheckbox, SelectionBar, ListFilter, ListDots, AddToListMenu } from '../components/Lists';
import { useAuth } from '../contexts/AuthContext';
import { apiSend, NoMarketError } from '../utils/api';
import { ConnectMlsPanel } from '../components/EntitlementStates';
import { SaveSearchButton, SavedSearchesPanel, criteriaFromFilters, filtersFromCriteria } from '../components/SavedSearches';

// ── Constants ─────────────────────────────────────────────────────────────────

// Street-suffix hints used to tell a specific address ("123 Main St") apart from a natural-language
// query ("houses with pools") in the unified search bar.
const STREET_SUFFIX = /\b(st|street|ave|avenue|rd|road|dr|drive|ln|lane|blvd|boulevard|ct|court|way|pl|place|cir|circle|ter|terrace|hwy|highway|pkwy|parkway|trl|trail|cove|cv|run|row|loop|bend|pass|path|walk|sq|sqr|square)\b/i;

const STATUS_PILLS = [
  { key: 'Active',          label: 'Active' },
  { key: 'Offers Sent',     label: 'Offers Sent' },
  { key: 'Pending',         label: 'Pending' },
  { key: 'Back on Market',  label: 'Back on Market' },
  { key: 'Closed',          label: 'Closed (comps)' },
  { key: 'Expired',         label: 'Expired' },
  { key: 'Withdrawn',       label: 'Withdrawn' },
  { key: 'Dismissed',       label: 'Dismissed' },
];

// Values are the backend ?sort= keys (see listingsOrderBy in routes/listings.js) — the server does
// the ordering so "Newest to Market" actually works (there is no list_date; it sorts by fewest
// days_on_market). Sorting is server-side; client-side filters preserve that order (stable filter).
const SORT_OPTIONS = [
  { value: 'newest',            label: 'Newest to Market' },
  { value: 'opportunity_score', label: 'Deal Score' },
  { value: 'price_low',         label: 'Price: Low → High' },
  { value: 'price_high',        label: 'Price: High → Low' },
  { value: 'price_per_sqft',    label: 'Price / Sqft' },
  { value: 'sqft',              label: 'Largest Sqft' },
  { value: 'beds',              label: 'Most Beds' },
  { value: 'baths',             label: 'Most Baths' },
  { value: 'year_built',        label: 'Newest Built' },
  { value: 'days_on_market',    label: 'Most Days on Market' },
  { value: 'recently_added',    label: 'Recently Added' },
];

const BADGE = {
  'Hot':      { bg: 'rgba(184, 84, 80,0.18)',  color: '#B85450', border: 'rgba(184, 84, 80,0.4)',  emoji: '🔥' },
  'Hot Deal': { bg: 'rgba(184, 84, 80,0.18)',  color: '#B85450', border: 'rgba(184, 84, 80,0.4)',  emoji: '🔥' },
  'Watch':    { bg: 'rgba(224, 165, 38,0.15)', color: '#E0A526', border: 'rgba(224, 165, 38,0.4)', emoji: '👀' },
  'Maybe':    { bg: 'rgba(156, 150, 144,0.1)', color: '#6B6760', border: 'rgba(156, 150, 144,0.2)', emoji: '' },
  'Pass':     { bg: 'rgba(156, 150, 144,0.1)', color: '#6B6760', border: 'rgba(156, 150, 144,0.2)', emoji: '' },
};

const STATUS_COLOR = {
  'Active':         '#14504A', 'Pending':   '#E0A526',
  'Expired':        '#B85450', 'Withdrawn': '#6B6760',
  'Back on Market': '#14504A',
  'Offers Sent':    '#14504A',
};

const REHAB_BREAKDOWN = [
  { category: 'Kitchen',           pct: 0.28 },
  { category: 'Bathrooms',         pct: 0.18 },
  { category: 'Flooring',          pct: 0.12 },
  { category: 'HVAC',              pct: 0.14 },
  { category: 'Roof',              pct: 0.10 },
  { category: 'Paint & Drywall',   pct: 0.08 },
  { category: 'Electrical',        pct: 0.05 },
  { category: 'Plumbing',          pct: 0.03 },
  { category: 'Landscaping',       pct: 0.02 },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtMoney(n) {
  if (!n && n !== 0) return '—';
  if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000)    return `$${Math.round(n / 1000)}k`;
  return `$${n}`;
}

function fmtK(n) { return n ? `$${Math.round(n / 1000)}k` : '—'; }
function timeAgo(ts) {
  if (!ts) return '';
  const s = Math.max(0, (Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

function fmtRelative(iso) {
  if (!iso) return 'never';
  const dt = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
  const min = Math.round((Date.now() - dt.getTime()) / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
}

function initRehabItems(repairCost) {
  const total = repairCost || 30000;
  return REHAB_BREAKDOWN.map(r => ({ ...r, amount: Math.round(total * r.pct) }));
}

// ── "Add to Working?" decline memory ──────────────────────────────────────────
// Remembers which listings the user said "No" to, so we don't re-prompt them.
const WORKING_DECLINE_KEY = 'dealflow_working_decline';
function isWorkingDeclined(mlsId) {
  if (!mlsId) return false;
  try { return !!JSON.parse(localStorage.getItem(WORKING_DECLINE_KEY) || '{}')[mlsId]; }
  catch { return false; }
}
function rememberWorkingDecline(mlsId) {
  if (!mlsId) return;
  try {
    const m = JSON.parse(localStorage.getItem(WORKING_DECLINE_KEY) || '{}');
    m[mlsId] = true;
    localStorage.setItem(WORKING_DECLINE_KEY, JSON.stringify(m));
  } catch {}
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function MlsOpportunities() {
  const navigate = useNavigate();
  const { plan, role } = useAuth();
  // MLS sync is an ORG-level operation billed to the org's single API account — never per-user.
  // The server already restricts it (routes/integrations.js requires role 'owner'; routes/listings.js
  // additionally requires the market-owner org), so hiding it here keeps agents from spending the
  // org's request budget and from clicking a control that can only 403 for them.
  const canSyncMls = role === 'owner';
  const isDemo = plan?.key === 'demo';

  const [listings, setListings]       = useState([]);
  const [dismissedListings, setDismissedListings] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [statusFilter, setStatusFilter] = useState('Active');
  const [mode, setMode]               = useState('opportunities'); // 'opportunities' (is_opportunity=1) | 'all'
  const [sortBy, setSortBy]           = useState('newest');     // default: newest to market
  const [priceReducedOnly, setPriceReducedOnly] = useState(false); // current list price < original
  const [typeFilter, setTypeFilter] = useState('all'); // all | lots | new | well_priced | hot
  // "Today's Deals": null = off, else the lookback in hours. Narrows to opportunities that entered
  // THIS org's set within the window AND are pre-underwritten AND priced within the MAO tolerance.
  const [todaysWindowH, setTodaysWindowH] = useState(null);
  // Opportunity selector, feature-flagged so v1 and v2 can be compared live on the same data.
  //   v1 = today's keyword selection (description matched a signal), ranked by a score that is a
  //        hardcoded 75 on 91% of the set.
  //   v2 = economics first — exclude retail, require a plausible ARV, keep what is within reach of MAO
  //        (and further out when the seller is demonstrably moving), ranked by that gap.
  // Remembered per device so a comparison survives a refresh.
  const [selector, setSelector] = useState(() => {
    try { return localStorage.getItem('feebe_selector') === 'v2' ? 'v2' : 'v1'; } catch { return 'v1'; }
  });
  const chooseSelector = (v) => { setSelector(v); try { localStorage.setItem('feebe_selector', v); } catch {} };
  // Per-device default landing view (List/Detail/Map), remembered in localStorage.
  const [defaultView, setDefaultView] = useState(() => {
    try { const v = localStorage.getItem('feebe_mls_default_view'); return ['list', 'detail', 'map'].includes(v) ? v : 'list'; } catch { return 'list'; }
  });
  const [viewMode, setViewMode]       = useState(defaultView);
  const saveDefaultView = (v) => { setDefaultView(v); try { localStorage.setItem('feebe_mls_default_view', v); } catch {} };

  // AI natural-language search
  const [aiQuery, setAiQuery]         = useState('');
  const [aiSearching, setAiSearching] = useState(false);
  const [aiResult, setAiResult]       = useState(null); // { keywords:[], explanation:'' }
  const [suggestions, setSuggestions] = useState([]);   // A3 address typeahead (local cache)
  const [showSuggest, setShowSuggest] = useState(false);

  // Quick Search — live per-address / MLS# MLS lookup (Phase-1 endpoint). Result shown in a modal.
  const [quickResult, setQuickResult] = useState(null); // { loading } | { found, listing, source } | { found:false, fallback } | { error }

  // Custom-list selection + filter
  const [selectedMlsIds, setSelectedMlsIds] = useState(new Set());
  const [listFilter, setListFilter]         = useState(null);
  const { memberSet } = useLists();
  const [filters, setFilters] = useState({
    priceMin: '', priceMax: '',
    bedsMin: '', bathsMin: '',
    sqftMin: '', sqftMax: '',
    lotMin: '', lotMax: '',
    yearMin: '', yearMax: '',
    domMin: '', domMax: '',
    garageMin: '', garageMax: '',
    scoreMin: '',
    pool: '',          // '' any | 'yes' | 'no' (backed by mls_listings.pool tri-state)
    distressMin: '',   // min distress_level 0–100 (from AI photo scoring)
    cashOnly: false,   // financing filter — cash-sale signal (structured feed field is a fast-follow)
    closeWithin: '',   // Closed status only: sold within the past N days (30-day increments → 365)
    zips: [],          // multi-select
    propertyTypes: ['Single Family'], // default to Single Family (D4) — changeable in the rail
  });
  // Saved searches — the agent's standing hunts. The badge counts alerts they haven't looked at yet
  // (a new listing matching, or a price cut bringing one into range); both are raised server-side by
  // services/savedSearchAlerts.js off the MLS event log, so nothing here polls the MLS.
  const [savedOpen, setSavedOpen]   = useState(false);
  const [savedUnseen, setSavedUnseen] = useState(0);
  const [savedBump, setSavedBump]   = useState(0);   // forces the drawer to refetch after a save
  useEffect(() => {
    let dead = false;
    fetch('/api/saved-searches/hits?unseen=1&limit=1')
      .then(r => r.json())
      .then(d => { if (!dead) setSavedUnseen(Number(d.unseen) || 0); })
      .catch(() => {});
    return () => { dead = true; };
  }, [savedBump, savedOpen]);

  const [railCollapsed, setRailCollapsed] = useState(false); // Flexmls-style left parameter rail
  const [selected, setSelected]       = useState(null);
  const [detailTab, setDetailTab]     = useState(null); // null|'arv'|'rehab'|'mao'

  // ARV panel
  const [comps, setComps]             = useState([]);
  const [marketComps, setMarketComps] = useState([]);     // active + pending nearby (market context, not ARV)
  const [compsArv, setCompsArv]       = useState(null);   // live ARV from the /comps fetch (fallback before cached financials)
  const [compsLoading, setCompsLoading] = useState(false);
  // A /comps fetch has COMPLETED for this listing. Distinct from `comps.length > 0`: a legitimate
  // zero-comp result must still render the comps panel (with the engine's reason), otherwise the UI
  // falls back to the "Run comps" prompt and the button reads as broken.
  const [compsFetched, setCompsFetched] = useState(false);
  const [compsWarning, setCompsWarning] = useState(null);   // engine's reason when the result is empty/thin
  const [compsMode, setCompsMode]       = useState(null);   // 'flip' | 'teardown' — basis actually used
  const [compsSignal, setCompsSignal]   = useState(null);   // description phrase that auto-triggered teardown
  const [compsModeForced, setCompsModeForced] = useState(false);  // basis pinned by the user, not auto-detected
  // Every nearby closed sale, UNQUALIFIED by the comp engine — the manual escape hatch when the
  // engine's subject-relative ceilings return few or no comps.
  const [areaSales, setAreaSales]       = useState([]);
  const [areaMeta, setAreaMeta]         = useState({ radius_mi: 1, days: 365 });
  // The other valuation basis WOULD have found comps: { mode, comp_count, arv }.
  const [compsFallback, setCompsFallback] = useState(null);
  const [compVotes, setCompVotes]     = useState({});
  const [compReasons, setCompReasons] = useState({});
  const [pendingReason, setPendingReason] = useState({});
  const [recalculating, setRecalculating] = useState(false);
  const [refinedArv, setRefinedArv]   = useState(null);
  const [excludedCount, setExcludedCount] = useState(0);

  // Rehab panel
  const [rehabItems, setRehabItems]   = useState([]);

  // Buyers
  const [matchedBuyers, setMatchedBuyers] = useState([]);
  const [buyersLoading, setBuyersLoading] = useState(false);
  const [buyerModal, setBuyerModal]   = useState(null);

  // Actions
  const [savedDealId, setSavedDealId] = useState(null);
  const [contractOpen, setContractOpen] = useState(false);
  const [agentOutreachOpen, setAgentOutreachOpen] = useState(false);
  const [savedDealForModal, setSavedDealForModal] = useState(null);

  // "Add to Working?" prompt — fires once per listing on the first deliberate metric edit.
  const [workingPrompt, setWorkingPrompt] = useState(null);   // { mlsId, address } | null
  const workingResolvers = useRef([]);                        // resolvers awaiting the user's choice

  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [missedOpenMo, setMissedOpenMo] = useState(false);

  const loadListings = useCallback(() => {
    setRefreshing(true);
    // Sorting is done server-side via ?sort= so "Newest to Market" works (no client list_date).
    // showAll=1 broadens the set from opportunities-only to every scored listing (the "Show All
    // Results" mode). Refetches whenever sortBy or mode changes (both are deps below).
    // deals=1 asks for the bottom-quintile pricing signals. Gated because that percentile pass costs
    // ~144ms over every active listing — worth it for Today's Deals, not for every page load.
    fetch(`/api/listings?sort=${encodeURIComponent(sortBy)}${mode === 'all' ? '&showAll=1' : ''}${todaysWindowH ? '&deals=1' : ''}${selector === 'v2' ? '&selector=v2' : ''}`)
      .then(r => r.json())
      .then(data => {
        setListings(data.listings || []);
        setSyncStatus({
          last_synced_at: data.last_synced_at,
          beta_mode: data.beta_mode,
          beta_zips: data.beta_zips || [],
          total: data.total,
          pending: data.pending || 0,
          filtered_out: data.filtered_out || 0,
          cached_total: data.cached_total || 0,
        });
        setLoading(false);
        setRefreshing(false);
      })
      .catch(err => { setError(err.message); setLoading(false); setRefreshing(false); });
  }, [sortBy, mode, todaysWindowH, selector]);

  const loadDismissed = useCallback(() => {
    fetch('/api/listings/dismissed')
      .then(r => r.json())
      .then(data => setDismissedListings(data.listings || []))
      .catch(() => setDismissedListings([]));
  }, []);

  // "Not for me" — hide a listing from opportunities (persists across syncs) and drop its photos.
  const dismissListing = useCallback(async (listing) => {
    try {
      await apiSend(`/api/listings/${encodeURIComponent(listing.listing_key)}/dismiss`);
      setSelected(null);
      loadListings();
    } catch (e) { alert('Could not dismiss: ' + e.message); }
  }, [loadListings]);

  const restoreListing = useCallback(async (listing) => {
    try {
      await apiSend(`/api/listings/${encodeURIComponent(listing.listing_key)}/undismiss`);
      setSelected(null);
      loadDismissed();
      loadListings();
    } catch (e) { alert('Could not restore: ' + e.message); }
  }, [loadDismissed, loadListings]);

  // Load the dismissed set whenever the user views that pill.
  useEffect(() => { if (statusFilter === 'Dismissed') loadDismissed(); }, [statusFilter, loadDismissed]);

  // Closed sales (for comps) come from a different source than the active opportunity set, so they
  // load separately — like Dismissed. Backend returns closings from the past year; the "Closed within"
  // rail filter narrows client-side.
  const [closedComps, setClosedComps] = useState([]);
  const loadClosed = useCallback(() => {
    fetch(`/api/listings?status=Closed&sort=${encodeURIComponent(sortBy)}`)
      .then(r => r.json())
      .then(data => setClosedComps(data.listings || []))
      .catch(() => setClosedComps([]));
  }, [sortBy]);
  useEffect(() => { if (statusFilter === 'Closed') loadClosed(); }, [statusFilter, loadClosed]);

  async function triggerSyncNow() {
    // Confirmation gate — manual sync forces an out-of-schedule MLS pull that
    // re-runs the AI opportunity scorer on any new listings. Each scored listing
    // costs Anthropic API tokens (Claude vision call on the photos). On a fresh
    // sync of 100+ new active listings this can be a few dollars in tokens.
    // The auto-scheduled daily 10am sync is free of this prompt — only this
    // manual button has the warning, because it's the off-schedule expensive case.
    const ok = window.confirm(
      'Run a manual MLS sync now?\n\n' +
      '⚠ This re-pulls active listings from MLS Grid and may trigger AI scoring on new ones. ' +
      'Each scored listing uses Anthropic API tokens (estimated $0.01–$0.05 per ambiguous listing).\n\n' +
      'The auto-scheduled daily sync at 10am does this once a day at no extra cost beyond your normal usage. ' +
      'Use this manual button only when you specifically need fresh data NOW.'
    );
    if (!ok) return;
    setSyncing(true);
    try {
      await apiSend('/api/listings/sync/active', { method: 'POST' });
      // Wait a moment for scoring to start, then reload
      setTimeout(() => { loadListings(); setSyncing(false); }, 1500);
    } catch (err) {
      setSyncing(false);
      // Non-market-owner orgs can't trigger a scan — they already see the Connect-MLS empty state,
      // so just surface a brief note instead of an error.
      if (err instanceof NoMarketError) {
        window.alert('No MLS market is connected for your organization yet. Connecting your market is coming soon.');
      }
    }
  }

  const EMPTY_RANGE_FILTERS = {
    priceMin: '', priceMax: '', bedsMin: '', bathsMin: '',
    sqftMin: '', sqftMax: '', lotMin: '', lotMax: '',
    yearMin: '', yearMax: '', domMin: '', domMax: '', garageMin: '', garageMax: '', scoreMin: '',
    pool: '', distressMin: '', cashOnly: false, closeWithin: '',
  };

  // Unified search: one bar handles both natural-language AI search and a live address / MLS# lookup.
  // We route by the shape of the input — a specific address or MLS number goes to the live Quick
  // Search; anything else is treated as a natural-language query for the AI filter search.
  function looksLikeAddressOrId(q) {
    const s = String(q || '').trim();
    if (!s) return false;
    const compact = s.replace(/\s+/g, '');
    if (/^[A-Za-z]{0,5}\d{4,}$/.test(compact)) return true;            // MLS# / ListingId / bare ZIP
    if (/^\d{1,6}\s+[A-Za-z]/.test(s) && STREET_SUFFIX.test(s)) return true; // "123 Main St"
    return false;
  }
  function runSearch(raw) {
    const q = String(raw ?? aiQuery).trim();
    if (!q) return;
    // A specific search should surface everything that matches, not just the AI-scored opportunities —
    // the user can re-narrow with "Scrape for Deals". More satisfying than a search that hides results.
    setMode('all');
    if (looksLikeAddressOrId(q)) runQuickSearch(q);
    else runAiSearch(q);
  }

  // Translate a natural-language query into structured filters via the backend, then apply them.
  async function runAiSearch(raw) {
    const q = String(raw ?? aiQuery).trim();
    if (!q) return;
    setAiSearching(true);
    try {
      const r = await fetch('/api/listings/ai-search', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: q }),
      });
      const data = await r.json();
      setFilters(f => ({ ...EMPTY_RANGE_FILTERS, zips: f.zips, propertyTypes: f.propertyTypes, ...(data.filters || {}) }));
      setAiResult({ keywords: data.keywords || [], explanation: data.explanation || '' });
    } catch (err) {
      setAiResult({ keywords: [], explanation: 'Search failed: ' + err.message });
    }
    setAiSearching(false);
  }

  function clearAiSearch() {
    setAiQuery('');
    setAiResult(null);
    setFilters(f => ({ ...EMPTY_RANGE_FILTERS, zips: f.zips, propertyTypes: f.propertyTypes }));
  }

  // "Today's Deals" — the highest-scored deals that became actionable in the last 24 hours.
  //
  // It used to be a clear-all button with NO date filter at all: it reset every filter and showed all
  // ~3,500 active opportunities regardless of age, so the name promised a recency filter that did not
  // exist and clicking it from the default state changed nothing on screen.
  //
  // "A deal" here means all three of:
  //   • entered THIS org's opportunity set within the window (o.created_at) — new to YOU, which is not
  //     the same as new to market; the scorer may only just have approved a 60-day-old listing;
  //   • pre-underwritten (financials_at set, so ARV/MAO actually exist); and
  //   • well_priced — list <= MAO x (1 + well_priced_tol), the comp engine's own "within a reasonable
  //     margin of MAO" signal (comp_config, default 10%).
  // Ordered by deal score, highest first.
  function showTodaysDeals(hours = 24) {
    setMode('opportunities');
    setStatusFilter('Active');
    setTypeFilter('all');          // the window + well-priced test below IS the filter
    setPriceReducedOnly(false);
    setAiQuery('');
    setAiResult(null);
    setListFilter(null);           // a custom-list filter otherwise survives and keeps narrowing
    setFilters(f => ({ ...EMPTY_RANGE_FILTERS, zips: [], propertyTypes: ['Single Family'] }));
    setSortBy('opportunity_score');
    setTodaysWindowH(hours);
    setViewMode('list');
  }

  // The three things that can make a listing a deal. Each returns a TAG so the row says WHY the tool
  // flagged it — an unexplained "deal" is not actionable. A listing qualifies on ANY of them (they are
  // independent signals, not a combined score), and carries every tag it earns.
  //   • MAO      — list <= MAO x (1 + well_priced_tol). Requires underwriting (needs an ARV/MAO).
  //   • SUB 20%  — cheapest fifth of its SUBDIVISION by list price, vs every active listing there.
  //   • PSF 20%  — cheapest fifth of its ZIP by price per sqft.
  // Minimum peer group sizes. PERCENT_RANK() is 0 for the lowest row in ANY partition, so a group of
  // one ALWAYS reads as "bottom 20%" — a lone listing in a zip would qualify against nothing. On prod,
  // 151 zips hold exactly one active listing. Subdivisions are far thinner than zips (7,751 of them
  // averaging 2 listings; only 443 have 5+), so the subdivision signal is deliberately narrow — it
  // fires only where there is a real local comparison set.
  const MIN_SUBDIVISION_PEERS = 5;
  const MIN_ZIP_PEERS = 10;          // zips average 42 active listings; below 10 the ranking is noise
  const dealTags = useCallback((l) => {
    const tags = [];
    if (l.well_priced && l.financials_at) tags.push({ key: 'mao', label: 'Within MAO', color: 'var(--teal)' });
    if (l.sub_price_pctile != null && l.sub_price_pctile <= 0.20 && (l.subdivision_peers || 0) >= MIN_SUBDIVISION_PEERS) {
      tags.push({ key: 'sub', label: 'Bottom 20% in subdivision', color: '#8B5CF6' });
    }
    if (l.psf_pctile != null && l.psf_pctile <= 0.20 && (l.zip_peers || 0) >= MIN_ZIP_PEERS) {
      tags.push({ key: 'psf', label: 'Bottom 20% $/sqft in zip', color: 'var(--gold)' });
    }
    return tags;
  }, []);

  // How many would qualify at each lookback — lets the banner offer to widen instead of dead-ending on
  // a quiet day (a strict 24h window legitimately returns only a handful).
  const todaysCounts = useMemo(() => {
    const qualifies = (l, h) => {
      if (dealTags(l).length === 0) return false;
      const t = l.opportunity_added_at ? new Date(l.opportunity_added_at).getTime() : NaN;
      return Number.isFinite(t) && t >= Date.now() - h * 3600000;
    };
    const base = listings.filter(l => (l.status || 'Active') === 'Active');
    return { 24: base.filter(l => qualifies(l, 24)).length,
             48: base.filter(l => qualifies(l, 48)).length,
             168: base.filter(l => qualifies(l, 168)).length };
  }, [listings, dealTags]);

  // Quick Search — live MLS lookup by address / ZIP / MLS #. Bypasses the price band and routes to
  // this org's provider (Grid/Spark); falls back to public records when the feed returns nothing.
  async function runQuickSearch(raw) {
    const query = String(raw || '').trim();
    if (!query) return;
    setQuickResult({ loading: true, query });
    try {
      // A bare MLS #/ListingId (optional short alpha prefix + digits) also goes in as listing_key so
      // the backend's fast path can match it exactly before any fuzzy address search.
      const compact = query.replace(/\s+/g, '');
      const looksLikeId = /^[A-Za-z]{0,5}\d{5,}$/.test(compact);
      const r = await fetch('/api/listings/lookup-by-address', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: query, listing_key: looksLikeId ? compact : undefined }),
      });
      const data = await r.json();
      setQuickResult({ ...data, query, loading: false });
    } catch (e) {
      setQuickResult({ found: false, error: e.message, query, loading: false });
    }
  }

  useEffect(() => { loadListings(); }, [loadListings]);

  // A3 — debounced address typeahead from the local cache. Runs for any query ≥3 chars; the suggestion
  // dropdown lets the user pick an exact address, which then routes to the live Quick Search lookup.
  useEffect(() => {
    const q = aiQuery.trim();
    if (q.length < 3) { setSuggestions([]); return; }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`/api/listings/suggest?q=${encodeURIComponent(q)}`, { credentials: 'include' });
        const data = await r.json();
        if (alive) setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
      } catch { if (alive) setSuggestions([]); }
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [aiQuery]);

  // Pick a typeahead suggestion → fill the box and run the live Quick Search on that exact address.
  function pickSuggestion(s) {
    const full = [s.address, s.city, s.zip].filter(Boolean).join(', ');
    setAiQuery(full);
    setShowSuggest(false);
    setSuggestions([]);
    runQuickSearch(full);
  }

  // Active filter count for the badge
  const activeFilterCount = useMemo(() => {
    let n = 0;
    for (const k of ['priceMin','priceMax','bedsMin','bathsMin','sqftMin','sqftMax','lotMin','lotMax','yearMin','yearMax','domMin','domMax','garageMin','garageMax','scoreMin']) {
      if (filters[k] !== '' && filters[k] != null) n++;
    }
    if (filters.pool) n++;
    if (filters.distressMin !== '' && filters.distressMin != null) n++;
    if (filters.cashOnly) n++;
    if (filters.zips.length) n++;
    if (filters.propertyTypes.length) n++;
    if (typeFilter !== 'all') n++; // opportunity-type chip now lives inside the Filters rail
    if (statusFilter !== 'Active') n++; // status moved into the rail as a filter
    return n;
  }, [filters, typeFilter, statusFilter]);

  // Per-status counts for the rail's Status filter (same rules the old status tabs used).
  const statusCounts = useMemo(() => {
    const c = {};
    for (const pill of STATUS_PILLS) {
      c[pill.key] = pill.key === 'Offers Sent' ? listings.filter(l => l.contracted).length
        : pill.key === 'Back on Market' ? listings.filter(l => l.is_back_on_market).length
        : pill.key === 'Pending' ? listings.filter(l => l.status === 'Pending' || l.status === 'Active Under Contract').length
        : pill.key === 'Dismissed' ? dismissedListings.length
        : pill.key === 'Closed' ? (closedComps.length || null)
        : listings.filter(l => l.status === pill.key && (pill.key !== 'Active' || !l.is_back_on_market)).length;
    }
    return c;
  }, [listings, dismissedListings, closedComps]);

  // Filtered + sorted listings
  const filtered = useMemo(() => {
    // Dismissed view reads its own set (these are excluded from /api/listings by design).
    if (statusFilter === 'Dismissed') return dismissedListings;
    const n = v => v === '' || v == null ? null : Number(v);
    const pMin = n(filters.priceMin), pMax = n(filters.priceMax);
    const sMin = n(filters.sqftMin),  sMax = n(filters.sqftMax);
    const lMin = n(filters.lotMin),   lMax = n(filters.lotMax);
    const yMin = n(filters.yearMin),  yMax = n(filters.yearMax);
    const dMin = n(filters.domMin),   dMax = n(filters.domMax);
    const gMin = n(filters.garageMin), gMax = n(filters.garageMax);
    const bedsMin  = n(filters.bedsMin);
    const bathsMin = n(filters.bathsMin);
    const scoreMin = n(filters.scoreMin);
    const distressMin = n(filters.distressMin);

    // Closed sales (comps) — their own source; apply the close-date window + shared range filters.
    if (statusFilter === 'Closed') {
      const within = n(filters.closeWithin);
      const cutoff = within != null ? Date.now() - within * 86400000 : null;
      return closedComps.filter(l => {
        if (cutoff != null) { const t = l.close_date ? new Date(l.close_date).getTime() : NaN; if (!(t >= cutoff)) return false; }
        const price = l.list_price || l.close_price || 0;
        if (pMin != null && price < pMin) return false;
        if (pMax != null && price > pMax) return false;
        if (bedsMin != null && (l.beds || 0) < bedsMin) return false;
        if (bathsMin != null && (l.baths || 0) < bathsMin) return false;
        if (sMin != null && (l.sqft || 0) < sMin) return false;
        if (sMax != null && (l.sqft || 0) > sMax) return false;
        if (yMin != null && (l.year_built || 0) < yMin) return false;
        if (yMax != null && (l.year_built || 0) > yMax) return false;
        if (filters.zips.length && !filters.zips.includes(l.zip)) return false;
        return true;
      });
    }

    let result = listings.filter(l => {
      // Status pill ('All' = every status in the active market set — no status narrowing)
      if (statusFilter === 'All')                  { /* no status filter */ }
      else if (statusFilter === 'Offers Sent')     { if (!l.contracted) return false; }
      else if (statusFilter === 'Back on Market') { if (!l.is_back_on_market) return false; }
      else if (statusFilter === 'Active')         { if (!(l.status === 'Active' && !l.is_back_on_market)) return false; }
      // "Pending" folds in the MLS "Active Under Contract" status: both mean under contract / not
      // truly available, and a wholesaler treats them the same (watch for fall-through). Without this
      // AUC listings counted toward the header total but matched no pill, so the tabs never summed to it.
      else if (statusFilter === 'Pending')        { if (l.status !== 'Pending' && l.status !== 'Active Under Contract') return false; }
      else                                         { if (l.status !== statusFilter) return false; }

      // Range filters
      if (pMin != null && (l.list_price || 0) < pMin) return false;
      if (pMax != null && (l.list_price || 0) > pMax) return false;
      if (bedsMin != null && (l.beds || 0) < bedsMin) return false;
      if (bathsMin != null && (l.baths || 0) < bathsMin) return false;
      if (sMin != null && (l.sqft || 0) < sMin) return false;
      if (sMax != null && (l.sqft || 0) > sMax) return false;
      if (lMin != null && (l.lot_size_sqft || 0) < lMin) return false;
      if (lMax != null && (l.lot_size_sqft || 0) > lMax) return false;
      if (yMin != null && (l.year_built || 0) < yMin) return false;
      if (yMax != null && (l.year_built || 0) > yMax) return false;
      if (dMin != null && (l.days_on_market || 0) < dMin) return false;
      if (dMax != null && (l.days_on_market || 0) > dMax) return false;
      // Garage spaces (min/max). Null garage_spaces is treated as 0 for the floor.
      if (gMin != null && (l.garage_spaces || 0) < gMin) return false;
      if (gMax != null && l.garage_spaces != null && l.garage_spaces > gMax) return false;
      if (scoreMin != null && (l.opportunity_score || 0) < scoreMin) return false;

      // Pool (tri-state: mls_listings.pool is 1 = has, 0 = none, NULL = unknown).
      if (filters.pool === 'yes' && l.pool !== 1) return false;
      if (filters.pool === 'no'  && l.pool !== 0) return false;
      // Distress — minimum AI distress_level (0–100 from photo scoring).
      if (distressMin != null && (l.distress_level || 0) < distressMin) return false;
      // Financing — cash-sale signal. Structured feed field (buyer-financing terms on closed
      // comps) is a fast-follow; until then we match the same cash-language the scorer keys on
      // (migration 045) across the listing's free text so the filter is useful today.
      if (filters.cashOnly) {
        const terms = `${l.description || ''} ${l.opportunity_reason || ''} ${l.listing_terms || ''}`.toLowerCase();
        if (!/\bcash\b/.test(terms)) return false;
      }

      // Price Reduced — current list price below original (ANDs with the status pill).
      if (priceReducedOnly && !(l.original_list_price && l.list_price < l.original_list_price)) return false;

      // Opportunity-type chip (single-select; ANDs with the status pill).
      if (typeFilter === 'lots'        && !l.is_lot_scrape) return false;
      if (typeFilter === 'new'         && !l.is_new)        return false;
      if (typeFilter === 'well_priced' && !l.well_priced)   return false;
      if (typeFilter === 'hot'         && !l.hot_market)    return false;

      // Today's Deals — recency + pre-underwritten + priced within the MAO tolerance.
      if (todaysWindowH) {
        const added = l.opportunity_added_at ? new Date(l.opportunity_added_at).getTime() : NaN;
        if (!Number.isFinite(added) || added < Date.now() - todaysWindowH * 3600000) return false;
        if (dealTags(l).length === 0) return false;   // no qualifying signal — MAO, subdivision or $/sqft
      }

      // Multi-selects
      if (filters.zips.length && !filters.zips.includes(l.zip)) return false;
      if (filters.propertyTypes.length && !filters.propertyTypes.includes(dwellingType(l.property_subtype))) return false;

      return true;
    });

    // AI search keywords — match against address/description/subtype
    if (aiResult?.keywords?.length) {
      result = result.filter(l => {
        const hay = `${l.address || ''} ${l.description || ''} ${l.property_subtype || ''}`.toLowerCase();
        return aiResult.keywords.some(kw => hay.includes(kw));
      });
    }

    // Custom-list filter
    if (listFilter) {
      const s = memberSet(listFilter);
      result = result.filter(l => s.has(`mls:${l.mls_id}`));
    }

    // No client-side sort: the server already ordered by ?sort=, and .filter above is stable, so the
    // server order is preserved through all client refinements.
    return result;
  }, [listings, dismissedListings, closedComps, statusFilter, priceReducedOnly, typeFilter, todaysWindowH, filters, aiResult, listFilter, memberSet, dealTags]);

  const toggleSelect = (mlsId, on) => setSelectedMlsIds(prev => {
    const next = new Set(prev);
    if (on) next.add(mlsId); else next.delete(mlsId);
    return next;
  });
  const selectionItems = [...selectedMlsIds].map(mid => ({ item_type: 'mls', item_id: String(mid) }));

  // Resolve the checkbox selection (keyed by card mls_id) to real listing_keys via the displayed
  // set. Works in both the active view (mls_id === listing_key) and the Dismissed view (mls_id is the
  // true MLS number, so we must map through the row to get listing_key).
  const selectedListingKeys = useCallback(
    () => filtered.filter(l => selectedMlsIds.has(l.mls_id)).map(l => l.listing_key).filter(Boolean),
    [filtered, selectedMlsIds]
  );
  const bulkDismiss = useCallback(async () => {
    const keys = selectedListingKeys();
    if (!keys.length) return;
    try {
      await apiSend('/api/listings/bulk-dismiss', { body: { listing_keys: keys } });
      setSelectedMlsIds(new Set());
      setSelected(null);
      loadListings();
    } catch (e) { alert('Could not remove: ' + e.message); }
  }, [selectedListingKeys, loadListings]);
  const bulkRestore = useCallback(async () => {
    const keys = selectedListingKeys();
    if (!keys.length) return;
    try {
      await apiSend('/api/listings/bulk-restore', { body: { listing_keys: keys } });
      setSelectedMlsIds(new Set());
      setSelected(null);
      loadDismissed();
      loadListings();
    } catch (e) { alert('Could not restore: ' + e.message); }
  }, [selectedListingKeys, loadDismissed, loadListings]);

  // Add the checked opportunities to Deals → Working (one deal each, no contract/outbound).
  const bulkAddToWorking = useCallback(async () => {
    const chosen = filtered.filter(l => selectedMlsIds.has(l.mls_id));
    if (!chosen.length) return;
    try {
      const d = await apiSend('/api/listings/bulk-save', { body: { listings: chosen, stage: 'Tagged' } });
      setSelectedMlsIds(new Set());
      loadListings();
      const created = d?.created ?? 0, existed = d?.existed ?? 0;
      alert(`Added ${created} deal${created === 1 ? '' : 's'} to Tagged${existed ? ` · ${existed} already in your Deals` : ''}.`);
    } catch (e) { alert('Could not add to Tagged: ' + e.message); }
  }, [filtered, selectedMlsIds, loadListings]);

  // Unique zips & property types for the filter panel dropdowns
  const zipOptions = useMemo(() => Array.from(new Set(listings.map(l => l.zip).filter(Boolean))).sort(), [listings]);
  const typeOptions = useMemo(() => Array.from(new Set(listings.map(l => dwellingType(l.property_subtype)).filter(Boolean))).sort(), [listings]);

  // Handle listing selection
  const handleSelect = useCallback(async (listing) => {
    setSelected(listing);
    setDetailTab(null);
    setRefinedArv(null);
    setExcludedCount(0);
    setCompVotes({});
    setCompReasons({});
    setComps([]);
    setMarketComps([]);
    setCompsArv(null);
    setCompsFetched(false);
    setCompsWarning(null);
    setCompsMode(null);
    setCompsSignal(null);
    setCompsModeForced(false);
    setAreaSales([]);
    setCompsFallback(null);
    setSavedDealId(null);
    setRehabItems(initRehabItems(listing.repair_cost));
    setMatchedBuyers([]);

    // Log viewed
    fetch('/api/mls-opportunities/interact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mls_id: listing.mls_id, type: 'viewed' }),
    }).catch(() => {});

    // Fetch matched buyers
    setBuyersLoading(true);
    try {
      const r = await fetch(
        `/api/mls-opportunities/match?city=${encodeURIComponent(listing.city || 'Austin')}&beds=${listing.beds || 3}&price=${listing.list_price || 300000}&zip=${listing.zip || ''}`
      );
      const data = await r.json();
      setMatchedBuyers(data.buyers || []);
    } catch {}
    setBuyersLoading(false);
  }, []);

  // Open a listing straight into the standalone Detail view (from a table row or a map pin's
  // "open full detail").
  const openDetail = useCallback((listing) => {
    handleSelect(listing);
    setViewMode('detail');
  }, [handleSelect]);

  // Switch view modes from the List/Detail/Map toggle. Detail needs a subject — if none is
  // selected yet, open the first result so the view is never empty.
  const openView = useCallback((v) => {
    if (v === 'detail' && !selected) {
      const first = filtered[0];
      if (!first) return; // nothing to show
      handleSelect(first);
    }
    setViewMode(v);
  }, [selected, filtered, handleSelect]);

  // If Detail is the saved default view, auto-select the first result once listings arrive so the
  // page never opens on an empty detail pane.
  useEffect(() => {
    if (viewMode === 'detail' && !selected && filtered.length) handleSelect(filtered[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, filtered.length]);

  // Open ARV tab → load comps
  // forceMode: 'flip' | 'teardown' pins the valuation basis (and always re-fetches). Omit it to reuse
  // whatever we already pulled for this listing, auto-detecting the basis server-side.
  async function openArvTab(forceMode) {
    setDetailTab('arv');
    if (forceMode === undefined && (comps.length > 0 || compsFetched)) return;
    setCompsLoading(true);
    try {
      const params = new URLSearchParams({
        zip: selected.zip || '',
        sqft: selected.sqft || 1200,
        year_built: selected.year_built || '',
        subject_mls_id: selected.listing_key || selected.mls_id || '',
        // Fallback subject coords for the market-comp proximity + demo map centering when the
        // mls_listings lookup can't resolve them (e.g. demo listings live in demo_listings).
        lat: selected.lat || '',
        lng: selected.lng || '',
      });
      if (forceMode) params.set('mode', forceMode);
      const r = await fetch(`/api/mls-opportunities/comps?${params}`);
      if (!r.ok) throw new Error(`server responded ${r.status}`);
      const data = await r.json();
      setComps(data.comps || []);
      setMarketComps(data.market_comps || []);
      setCompsArv(data.arv || null);   // live comp-engine ARV (shows immediately, before the cached drain runs)
      setCompsWarning(data.warning || null);
      setCompsMode(data.mode || null);
      setCompsSignal(data.teardown_signal || null);
      setCompsModeForced(!!data.mode_forced);
      setAreaSales(data.area_sales || []);
      setAreaMeta({ radius_mi: data.area_radius_mi || 1, days: data.area_days || 365 });
      setCompsFallback(data.fallback || null);
    } catch (err) {
      // Surface it. A swallowed error and an empty result used to render identically (the prompt just
      // came back), so a failing request was indistinguishable from "no comps here".
      setComps([]);
      setMarketComps([]);
      setCompsWarning(`Couldn't load comps — ${err.message || 'request failed'}.`);
      setCompsMode(null);
      setCompsSignal(null);
      setAreaSales([]);
      setCompsFallback(null);
    }
    setCompsFetched(true);
    setCompsLoading(false);
  }

  // Ensure listing is saved as deal, return dealId
  async function ensureSaved() {
    if (savedDealId) return savedDealId;
    const r = await fetch('/api/listings/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(selected),
    });
    const data = await r.json();
    const id = data.id;
    if (id) setSavedDealId(id);
    return id;
  }

  // Comp vote
  async function handleThumbsDown(comp) {
    if (compVotes[comp.id] === 'down') return;
    setCompVotes(v => ({ ...v, [comp.id]: 'down' }));
    setPendingReason(r => ({ ...r, [comp.id]: '' }));
  }

  function handleThumbsUp(comp) {
    const next = compVotes[comp.id] === 'up' ? null : 'up';
    setCompVotes(v => { const n = { ...v }; if (next) n[comp.id] = next; else delete n[comp.id]; return n; });
  }

  async function submitCompVote(comp, vote, reason, dealId) {
    if (!dealId) return;   // not in working list — keep the down-vote local-only, skip backend persist
    setRecalculating(true);
    try {
      const result = await fetch('/api/comps/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deal_id: dealId, comp_deal_id: comp.id, comp_address: comp.address, vote, reason: reason || null }),
      }).then(r => r.json());
      if (result.updated_arv) setRefinedArv(result.updated_arv);
      const votes = { ...compVotes, [comp.id]: vote };
      setExcludedCount(Object.values(votes).filter(v => v === 'down').length);
    } catch {}
    setRecalculating(false);
  }

  async function handleReasonSubmit(comp) {
    const reason = pendingReason[comp.id] || '';
    setCompReasons(r => ({ ...r, [comp.id]: reason }));
    // Deliberate metric edit: gate the deal creation behind the "Add to Working?" prompt.
    const dealId = await requestWorking();
    await submitCompVote(comp, 'down', reason, dealId);
  }

  // Deliberate metric edit (comp down-vote, rehab number change). Returns a Promise<dealId|null>:
  //   - already in working → resolves immediately with the existing deal id
  //   - listing previously declined → resolves null (no prompt, no save)
  //   - otherwise → opens the prompt once; Yes resolves the new deal id, No resolves null.
  function requestWorking() {
    if (savedDealId) return Promise.resolve(savedDealId);
    if (!selected) return Promise.resolve(null);
    if (selected.deal_id) return Promise.resolve(selected.deal_id);   // already in the pipeline
    if (isWorkingDeclined(selected.mls_id)) return Promise.resolve(null);
    return new Promise(resolve => {
      workingResolvers.current.push(resolve);
      // setWorkingPrompt is a no-op if a prompt is already open (ask once at a time).
      setWorkingPrompt(p => p || { mlsId: selected.mls_id, address: selected.address });
    });
  }

  function resolveWorking(id) {
    const resolvers = workingResolvers.current;
    workingResolvers.current = [];
    setWorkingPrompt(null);
    resolvers.forEach(r => r(id));
  }

  async function confirmWorking() {
    let id = null;
    try { id = await ensureSaved(); } catch {}
    resolveWorking(id);
  }

  function declineWorking() {
    if (workingPrompt) rememberWorkingDecline(workingPrompt.mlsId);
    resolveWorking(null);
  }

  // Send offer email — opens AgentOutreachModal with saved deal context
  async function handleSendEmail() {
    try {
      const dealId = await ensureSaved();
      // Re-fetch the deal to get agent info attached during save
      const r = await fetch(`/api/deals/${dealId}`);
      const dealRecord = await r.json();
      setSavedDealForModal({ ...dealRecord, _dealId: dealId });
      setAgentOutreachOpen(true);
      fetch('/api/mls-opportunities/interact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mls_id: selected.mls_id, deal_id: dealId, type: 'emailed' }),
      }).catch(() => {});
    } catch (err) {
      alert('Could not send email: ' + err.message);
    }
  }

  // Promote a contracted listing's deal to Under Contract (manual confirmation that
  // the property is actually under contract). Surfaces it in the Deal Pipeline.
  // Move an MLS opportunity into the pipeline at the CHOSEN stage. If a deal already exists, it's a
  // manual stage move (PUT → chokepoint). If not, the deal record is CREATED at that stage (/save).
  async function handleMoveListingToStage(listing, stage) {
    try {
      if (listing.deal_id) {
        const r = await fetch(`/api/deals/${listing.deal_id}/stage`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ stage }),
        });
        if (!r.ok) throw new Error((await r.json()).error || 'Failed to update stage');
        setSelected(s => (s && s.mls_id === listing.mls_id ? { ...s, deal_stage: stage } : s));
      } else {
        const r = await fetch('/api/listings/save', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...listing, stage }),
        });
        const d = await r.json();
        if (!r.ok || d.error) throw new Error(d.error || 'Failed to create deal');
        setSelected(s => (s && s.mls_id === listing.mls_id ? { ...s, deal_id: d.id, deal_stage: stage } : s));
      }
      loadListings();
    } catch (err) {
      alert('Could not move into pipeline: ' + err.message);
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  // Context-aware noun for result counts: opportunities (default), all listings, closed sales, etc.
  const [nounSing, nounPlur] =
    statusFilter === 'Closed'    ? ['closed sale', 'closed sales'] :
    statusFilter === 'Dismissed' ? ['dismissed listing', 'dismissed listings'] :
    mode === 'all'               ? ['listing', 'listings'] :
                                   ['opportunity', 'opportunities'];
  const loadedCount = statusFilter === 'Closed' ? closedComps.length
    : statusFilter === 'Dismissed' ? dismissedListings.length
    : listings.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden', margin: '-32px -36px' }}>
      {/* Top Bar */}
      <div style={{ padding: '20px 28px 0', background: 'var(--navy)', borderBottom: '1px solid var(--navy-border)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--white)', letterSpacing: '-0.02em', marginBottom: 2 }}>MLS Opportunities</h1>
            <p style={{ fontSize: 12, color: 'var(--gray-500)' }}>
              {loading
                ? 'Loading…'
                : (
                  <>
                    Last synced {fmtRelative(syncStatus?.last_synced_at)} · {loadedCount} {loadedCount === 1 ? nounSing : nounPlur} found
                    {syncStatus?.pending > 0 && (
                      <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 4, background: 'rgba(224, 165, 38,0.15)', color: 'var(--gold)', fontSize: 10, fontWeight: 700 }}>{syncStatus.pending} pending AI scoring</span>
                    )}
                    {syncStatus?.beta_mode && <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 4, background: 'rgba(20, 80, 74,0.15)', color: '#14504A', fontSize: 10, fontWeight: 700 }}>BETA · {syncStatus.beta_zips.join(', ')}</span>}
                  </>
                )}
            </p>
          </div>
          {/* Toolbar controls. Wraps (never clips) and the List/Map toggle is pinned right in a
              flex-shrink:0 group so it's always visible. Secondary actions live in an ⋯ overflow menu;
              the opportunity-type chips moved into the Filters panel. */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', rowGap: 8 }}>
            {/* Today's Deals — jump to every pre-underwritten opportunity from any view (D7). */}
            <button
              onClick={() => (todaysWindowH ? setTodaysWindowH(null) : showTodaysDeals(24))}
              title={todaysWindowH
                ? 'Showing deals added in the last ' + todaysWindowH + 'h, priced within the MAO tolerance — click to clear'
                : "Highest-scored deals added to your set in the last 24h that are pre-underwritten and priced within the MAO tolerance"}
              style={{
                padding: '7px 12px', borderRadius: 8,
                border: `1px solid ${todaysWindowH ? 'var(--gold)' : 'rgba(224, 165, 38,0.4)'}`,
                background: todaysWindowH ? 'rgba(224, 165, 38,0.28)' : 'rgba(224, 165, 38,0.12)',
                color: 'var(--gold)',
                fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              🎯 Today's Deals
              {/* Only once the filter is on. Before that the bottom-quintile signals have not been
                  fetched (they are gated behind ?deals=1), so a count here would show the MAO-only
                  subset and then jump when clicked. No number beats a wrong number. */}
              {todaysWindowH ? <span style={{ fontSize: 11, opacity: 0.85, fontWeight: 800 }}>{todaysCounts[todaysWindowH] ?? filtered.length}</span> : null}
            </button>
            {/* Selector v1/v2 — feature flag, so the old and new opportunity logic can be compared on the
                same data without a deploy. Remove this control (and the flag) once v2 is adopted. */}
            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--navy-border)', borderRadius: 8, overflow: 'hidden' }}>
              {[
                { k: 'v1', label: 'Keyword', hint: "Today's selector: description matched a signal. Ranked by a score that is a flat 75 on most listings." },
                { k: 'v2', label: 'Economics', hint: 'New selector: excludes retail and implausible ARVs, keeps what is within reach of your MAO (and further out when the seller is clearly moving). Ranked by that gap.' },
              ].map(t => (
                <button key={t.k} onClick={() => chooseSelector(t.k)} title={t.hint}
                  style={{
                    padding: '7px 11px', border: 'none', cursor: 'pointer', fontSize: 11.5, fontWeight: 700,
                    background: selector === t.k ? 'var(--teal)' : 'transparent',
                    color: selector === t.k ? '#fff' : 'var(--gray-500)',
                  }}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* List filter (the Filters button + top Sort dropdown were removed — the rail IS the filter
                surface, and Sort now lives next to Columns in the list header). */}
            <ListFilter value={listFilter} onChange={setListFilter} />

            {/* Right-pinned group: overflow menu + the always-visible List/Map toggle. */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 'auto', flexShrink: 0 }}>
              <ToolbarOverflow active={priceReducedOnly || viewMode === 'hotsheet'}>
                {/* Did we miss one? — manual rescue + teach */}
                <button
                  onClick={() => setMissedOpenMo(true)}
                  title="Add a listing the system filtered out — and teach the scorer to catch similar ones next time"
                  style={{
                    padding: '7px 12px', borderRadius: 8,
                    border: '1px solid rgba(139,92,246,0.4)',
                    background: 'rgba(139,92,246,0.12)',
                    color: '#A78BFA', fontSize: 12,
                    cursor: 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  Did we miss one?
                </button>
                {/* Sync now — owner only (org-level operation, org-level API budget) */}
                {canSyncMls && <button
                  onClick={triggerSyncNow}
                  disabled={syncing}
                  title="Pull latest from MLS Grid"
                  style={{
                    padding: '7px 12px', borderRadius: 8,
                    border: '1px solid rgba(224, 165, 38,0.4)',
                    background: 'rgba(224, 165, 38,0.12)',
                    color: 'var(--gold)', fontSize: 12,
                    cursor: syncing ? 'not-allowed' : 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: syncing ? 'spin 0.8s linear infinite' : 'none' }}>
                    <path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M3 22v-6h6"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/>
                  </svg>
                  {syncing ? 'Syncing…' : 'Sync Now'}
                </button>}
                {/* Refresh */}
                <button
                  onClick={loadListings}
                  disabled={refreshing}
                  title="Reload from local cache"
                  style={{
                    padding: '7px 12px', borderRadius: 8,
                    border: '1px solid var(--navy-border)', background: 'var(--navy-card)',
                    color: refreshing ? 'var(--teal)' : 'var(--gray-300)', fontSize: 12,
                    cursor: refreshing ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600,
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: refreshing ? 'spin 0.8s linear infinite' : 'none' }}>
                    <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                  </svg>
                  {refreshing ? '…' : 'Refresh'}
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </button>
                {/* Hot Sheet (24h changes) */}
                <button
                  onClick={() => setViewMode(v => v === 'hotsheet' ? 'list' : 'hotsheet')}
                  title="Price changes, status changes, new listings and closings from the last 24 hours"
                  style={{
                    padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    border: viewMode === 'hotsheet' ? '1px solid var(--gold)' : '1px solid var(--navy-border)',
                    background: viewMode === 'hotsheet' ? 'rgba(224,165,38,0.15)' : 'var(--navy-card)',
                    color: viewMode === 'hotsheet' ? 'var(--gold)' : 'var(--gray-300)',
                  }}
                >
                  🔥 Hot Sheet
                </button>
                {/* Price Reduced toggle — ANDs with the active status pill */}
                <button
                  onClick={() => {
                    const next = !priceReducedOnly;
                    setPriceReducedOnly(next);
                    // priceReducedOnly only feeds `filtered`, which the Hot Sheet does not read — so
                    // toggling it there highlighted the button and changed nothing on screen. Turning
                    // it on from the Hot Sheet returns to the results it actually filters.
                    if (next && (viewMode === 'hotsheet' || viewMode === 'presets')) setViewMode('list');
                  }}
                  title="Show only listings whose current price is below their original list price"
                  style={{
                    padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    border: priceReducedOnly ? '1px solid var(--teal)' : '1px solid var(--navy-border)',
                    background: priceReducedOnly ? 'var(--teal-dim)' : 'var(--navy-card)',
                    color: priceReducedOnly ? 'var(--teal)' : 'var(--gray-300)',
                  }}
                >
                  ↓ Price Reduced
                </button>
                {/* Presets — the settings that drive every scan / comp pull / score */}
                <button
                  onClick={() => setViewMode('presets')}
                  title="View the presets that drive scraping, comps, and scoring — plus your default view"
                  style={{
                    padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    border: viewMode === 'presets' ? '1px solid var(--teal)' : '1px solid var(--navy-border)',
                    background: viewMode === 'presets' ? 'var(--teal-dim)' : 'var(--navy-card)',
                    color: viewMode === 'presets' ? 'var(--teal)' : 'var(--gray-300)',
                  }}
                >
                  ⚙ Presets
                </button>
                {/* Saved searches — every standing hunt in one place, with unread alert count. */}
                <button
                  onClick={() => setSavedOpen(true)}
                  title="Your saved searches and their new matches"
                  style={{
                    padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    border: savedUnseen ? '1px solid var(--gold)' : '1px solid var(--navy-border)',
                    background: savedUnseen ? 'var(--gold-dim)' : 'var(--navy-card)',
                    color: savedUnseen ? '#8a6314' : 'var(--gray-300)',
                    display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
                  }}
                >
                  ☆ Saved Searches
                  {savedUnseen > 0 && (
                    <span style={{ background: 'var(--gold)', color: '#3A2C08', borderRadius: 999, fontSize: 10, fontWeight: 700, padding: '1px 6px' }}>
                      {savedUnseen}
                    </span>
                  )}
                </button>
              </ToolbarOverflow>
            </div>
          </div>
        </div>
        {/* Quick Search — one bar for both AI natural-language search AND a live address / MLS#
            lookup. A specific address or MLS number routes to the live lookup; anything else is
            treated as a natural-language AI query. */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--teal)', letterSpacing: '0.02em' }}>Quick Search</span>
            <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>ask in plain English, or look up a specific address / MLS #</span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <svg style={{ position: 'absolute', left: 12, top: 10, opacity: 0.7 }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#14504A" strokeWidth="2"><path d="M12 2v6M12 16v6M4.93 4.93l4.24 4.24M14.83 14.83l4.24 4.24M2 12h6M16 12h6M4.93 19.07l4.24-4.24M14.83 9.17l4.24-4.24"/></svg>
              <input
                value={aiQuery}
                onChange={e => { setAiQuery(e.target.value); setShowSuggest(true); }}
                onFocus={() => setShowSuggest(true)}
                onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
                onKeyDown={e => { if (e.key === 'Enter') { setShowSuggest(false); runSearch(); } else if (e.key === 'Escape') setShowSuggest(false); }}
                placeholder="Ask AI (“available houses with pools in 78704”) or look up an address / MLS # (“123 Main St”)"
                style={{
                  width: '100%', padding: '9px 12px 9px 38px', borderRadius: 10, boxSizing: 'border-box',
                  border: '1px solid rgba(20, 80, 74,0.4)', background: 'rgba(20, 80, 74,0.06)',
                  color: 'var(--white)', fontSize: 13, outline: 'none',
                }}
              />
              <span style={{ position: 'absolute', right: 12, top: 11, fontSize: 9, fontWeight: 800, color: 'var(--teal)', letterSpacing: '0.05em', pointerEvents: 'none' }}>AI + LIVE MLS</span>
              {/* A3 — address autosuggest dropdown (local cache). */}
              {showSuggest && suggestions.length > 0 && (
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 4000, background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 10, boxShadow: '0 12px 32px rgba(26,23,20,0.22)', overflow: 'hidden', maxHeight: 320, overflowY: 'auto' }}>
                  {suggestions.map((s, i) => (
                    <div key={s.listing_key || i}
                      onMouseDown={e => { e.preventDefault(); pickSuggestion(s); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', borderBottom: i < suggestions.length - 1 ? '1px solid var(--navy-border)' : 'none' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--teal-dim)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--gray-500)" strokeWidth="2" style={{ flexShrink: 0 }}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--white)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{streetLine(s)}</div>
                        <div style={{ fontSize: 10.5, color: 'var(--gray-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {addressSub(s)}{s.status ? ` · ${s.status}` : ''}{(s.mls_number || s.mls_id) ? ` · MLS# ${s.mls_number || s.mls_id}` : ''}
                        </div>
                      </div>
                      {s.list_price ? <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--teal)', flexShrink: 0 }}>{fmtMoney(s.list_price)}</div> : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => runSearch()}
              disabled={aiSearching || !aiQuery.trim()}
              style={{
                padding: '9px 18px', borderRadius: 10, border: 'none',
                background: (aiSearching || !aiQuery.trim()) ? 'rgba(20, 80, 74,0.4)' : '#14504A',
                color: '#fff', fontSize: 13, fontWeight: 700,
                cursor: (aiSearching || !aiQuery.trim()) ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
              }}
            >
              {aiSearching ? 'Searching…' : 'Search'}
            </button>
            {aiResult && (
              <button onClick={clearAiSearch} style={{ padding: '9px 14px', borderRadius: 10, border: '1px solid var(--navy-border)', background: 'var(--navy-card)', color: 'var(--gray-300)', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Clear
              </button>
            )}
          </div>
          {aiResult?.explanation && (
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#14504A' }}>
              <span style={{ fontWeight: 700 }}>AI:</span>
              <span style={{ color: 'var(--gray-300)' }}>{aiResult.explanation}</span>
              {aiResult.keywords?.length > 0 && (
                <span style={{ color: 'var(--gray-500)' }}>· keywords: {aiResult.keywords.join(', ')}</span>
              )}
              <span style={{ color: 'var(--gray-500)' }}>· {filtered.length} match{filtered.length === 1 ? '' : 'es'}</span>
            </div>
          )}
        </div>
        {/* Barrow — scrape-mode tabs (left) + view toggle & default-view (right).
            The old status tabs moved into the filter rail as a Status filter. */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingBottom: 12, flexWrap: 'wrap', rowGap: 8 }}>
          {/* Mode: AI-scored opportunities only vs every scored listing in the market */}
          <div style={{ display: 'flex', gap: 4, background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 10, padding: 4 }}>
            {[
              { k: 'opportunities', label: '🎯 Scrape for Deals', count: Math.max(0, (syncStatus?.cached_total || 0) - (syncStatus?.filtered_out || 0)), hint: 'AI-scored opportunities only' },
              { k: 'all',           label: 'Show All Results',    count: syncStatus?.cached_total || 0, hint: 'Every scored listing in your market (opportunities + the rest)' },
            ].map(t => (
              <button key={t.k} onClick={() => setMode(t.k)} title={t.hint} style={{
                padding: '7px 13px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 700,
                background: mode === t.k ? 'var(--teal)' : 'transparent',
                color: mode === t.k ? '#fff' : 'var(--gray-500)',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                {t.label} <span style={{ fontSize: 11, fontWeight: 700, opacity: 0.8 }}>{t.count}</span>
              </button>
            ))}
          </div>
          {/* Default landing view + List/Detail/Map toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select
              value={defaultView}
              // Also switch NOW. Sitting inches from the live List/Detail/Map toggle and looking
              // identical to it, a control that silently only affected the NEXT visit read as broken.
              // It still saves the default; it just no longer does nothing visible.
              onChange={e => { saveDefaultView(e.target.value); openView(e.target.value); }}
              title="Switch to this view and make it the one that opens first next time (saved on this device)"
              style={{ padding: '7px 8px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy-card)', color: 'var(--gray-300)', fontSize: 11, cursor: 'pointer', outline: 'none' }}
            >
              <option value="list">⌂ Opens in: List</option>
              <option value="detail">⌂ Opens in: Detail</option>
              <option value="map">⌂ Opens in: Map</option>
            </select>
            <div style={{ display: 'flex', border: '1px solid var(--navy-border)', borderRadius: 8, overflow: 'hidden' }}>
              {[
                { v: 'list',   icon: <ListIcon />,   label: 'List' },
                { v: 'detail', icon: <DetailIcon />, label: 'Detail' },
                { v: 'map',    icon: <MapPinIcon />, label: 'Map' },
              ].map(({ v, icon, label }) => (
                <button key={v} title={`${label} view`} onClick={() => openView(v)} style={{
                  padding: '7px 13px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700,
                  display: 'flex', alignItems: 'center', gap: 6,
                  background: viewMode === v ? 'var(--teal)' : 'var(--navy-card)',
                  color: viewMode === v ? '#fff' : 'var(--gray-500)',
                }}>
                  {icon}<span>{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {selectedMlsIds.size > 0 && (
        <div style={{ padding: '12px 28px 0', flexShrink: 0 }}>
          <SelectionBar
            count={selectedMlsIds.size}
            items={selectionItems}
            onClear={() => setSelectedMlsIds(new Set())}
            extra={
              statusFilter === 'Dismissed' ? (
                <button onClick={bulkRestore} style={{ padding: '7px 12px', borderRadius: 8, background: 'transparent', border: '1px solid var(--teal)', color: 'var(--teal)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  Restore selected
                </button>
              ) : (
                <>
                  <button onClick={bulkAddToWorking} title="Add the selected opportunities to your Deals → Working tab"
                    style={{ padding: '7px 12px', borderRadius: 8, background: 'var(--teal)', border: '1px solid var(--teal)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    + Add to Working
                  </button>
                  <button onClick={bulkDismiss} style={{ padding: '7px 12px', borderRadius: 8, background: 'transparent', border: '1px solid #B85450', color: '#B85450', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    Remove selected
                  </button>
                </>
              )
            }
          />
        </div>
      )}

      {/* Content — parameter rail (list/map) OR property-list (detail) on the left + main pane */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
        {/* Detail swaps the filter rail for a property-list sidebar; Presets is a full-width page. */}
        {viewMode !== 'detail' && viewMode !== 'presets' && (
          <FilterRail
            filters={filters}
            /* D8: filters/status/type apply LIVE to the current set (immediate feedback), but no longer
               auto-switch to "Show All Results" on every keystroke. The explicit "Apply filters" button
               (onApply) is what runs the current filters against the whole market. */
            setFilters={setFilters}
            zipOptions={zipOptions}
            typeOptions={typeOptions}
            typeFilter={typeFilter}
            setTypeFilter={setTypeFilter}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            statusCounts={statusCounts}
            activeCount={activeFilterCount}
            savedSearchCriteria={criteriaFromFilters({ filters, statusFilter, mode, priceReducedOnly, typeFilter })}
            onSearchSaved={() => setSavedBump(b => b + 1)}
            onApply={() => setMode('all')}
            appliedAll={mode === 'all'}
            collapsed={railCollapsed}
            onToggleCollapse={() => setRailCollapsed(c => !c)}
          />
        )}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', minWidth: 0 }}>
        {viewMode === 'presets' ? (
          <PresetsPage defaultView={defaultView} onDefaultViewChange={saveDefaultView} onClose={() => setViewMode('list')} />
        ) : loading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
            <div style={{ width: 36, height: 36, border: '3px solid var(--navy-border)', borderTopColor: 'var(--teal)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>Loading MLS listings…</div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : error ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#B85450', fontSize: 13 }}>{error}</div>
        ) : (!isDemo && listings.length === 0) ? (
          // A non-demo org with no per-org opportunity set yet → "Connect your MLS" prompt (the
          // actual connect flow is a later layer). Demo orgs always have their seeded sample set.
          <ConnectMlsPanel />
        ) : viewMode === 'list' && todaysWindowH ? (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 12px', marginBottom: 10,
                          borderRadius: 8, background: 'rgba(224,165,38,0.09)', border: '1px solid rgba(224,165,38,0.32)' }}>
              <span style={{ fontSize: 12.5, color: 'var(--gray-300)', lineHeight: 1.45, flex: 1, minWidth: 240 }}>
                <strong style={{ color: 'var(--gold)' }}>🎯 {filtered.length} deal{filtered.length === 1 ? '' : 's'}</strong>
                {' '}added in the last {todaysWindowH}h · pre-underwritten · priced within the MAO tolerance · highest score first.
              </span>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {[24, 48, 168].map(h => (
                  <button key={h} onClick={() => setTodaysWindowH(h)}
                    style={{ padding: '4px 9px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                             border: `1px solid ${todaysWindowH === h ? 'var(--gold)' : 'var(--navy-border)'}`,
                             background: todaysWindowH === h ? 'rgba(224,165,38,0.2)' : 'var(--navy-card)',
                             color: todaysWindowH === h ? 'var(--gold)' : 'var(--gray-500)' }}>
                    {h === 168 ? '7d' : h + 'h'} <span style={{ opacity: 0.75 }}>({todaysCounts[h]})</span>
                  </button>
                ))}
                <button onClick={() => setTodaysWindowH(null)}
                  style={{ padding: '4px 9px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                           border: '1px solid var(--navy-border)', background: 'var(--navy-card)', color: 'var(--gray-300)' }}>
                  ✕ Clear
                </button>
              </div>
            </div>
            {filtered.length === 0 && (
              <div style={{ padding: '18px 12px', fontSize: 12.5, color: 'var(--gray-500)', lineHeight: 1.6 }}>
                Nothing met all three tests in this window — that is a normal outcome on a quiet day, not an
                error. Widen to 48h or 7d above, or clear the filter to browse every opportunity.
              </div>
            )}
            <ListView
            dealTags={dealTags}
            listings={filtered}
            nounSing={nounSing}
            nounPlur={nounPlur}
            selected={selected}
            onSelect={openDetail}
            checkedIds={selectedMlsIds}
            onToggleSelect={toggleSelect}
            sortBy={sortBy}
            setSortBy={setSortBy}
            onPromote={handleMoveListingToStage}
            onOpenContract={() => setContractOpen(true)}
            detailTab={detailTab}
            setDetailTab={setDetailTab}
            openArvTab={openArvTab}
            comps={comps}
            marketComps={marketComps}
            compsArv={compsArv}
            compsLoading={compsLoading}
            compsMeta={{ fetched: compsFetched, warning: compsWarning, mode: compsMode, signal: compsSignal, forced: compsModeForced, fallback: compsFallback, area: areaMeta }}
            areaSales={areaSales}
            compVotes={compVotes}
            compReasons={compReasons}
            pendingReason={pendingReason}
            setPendingReason={setPendingReason}
            handleThumbsUp={handleThumbsUp}
            handleThumbsDown={handleThumbsDown}
            handleReasonSubmit={handleReasonSubmit}
            recalculating={recalculating}
            refinedArv={refinedArv}
            excludedCount={excludedCount}
            rehabItems={rehabItems}
            setRehabItems={setRehabItems}
            matchedBuyers={matchedBuyers}
            buyersLoading={buyersLoading}
            onBuyerClick={setBuyerModal}
            onSendEmail={handleSendEmail}
            onMetricEdit={requestWorking}
            navigate={navigate}
            isDemo={isDemo}
            onDismiss={dismissListing}
            onRestore={restoreListing}
          />
          </div>
        ) : viewMode === 'list' ? (
          <ListView
            listings={filtered}
            nounSing={nounSing}
            nounPlur={nounPlur}
            selected={selected}
            onSelect={openDetail}
            checkedIds={selectedMlsIds}
            onToggleSelect={toggleSelect}
            sortBy={sortBy}
            setSortBy={setSortBy}
            onPromote={handleMoveListingToStage}
            onOpenContract={() => setContractOpen(true)}
            detailTab={detailTab}
            setDetailTab={setDetailTab}
            openArvTab={openArvTab}
            comps={comps}
            marketComps={marketComps}
            compsArv={compsArv}
            compsLoading={compsLoading}
            compsMeta={{ fetched: compsFetched, warning: compsWarning, mode: compsMode, signal: compsSignal, forced: compsModeForced, fallback: compsFallback, area: areaMeta }}
            areaSales={areaSales}
            compVotes={compVotes}
            compReasons={compReasons}
            pendingReason={pendingReason}
            setPendingReason={setPendingReason}
            handleThumbsUp={handleThumbsUp}
            handleThumbsDown={handleThumbsDown}
            handleReasonSubmit={handleReasonSubmit}
            recalculating={recalculating}
            refinedArv={refinedArv}
            excludedCount={excludedCount}
            rehabItems={rehabItems}
            setRehabItems={setRehabItems}
            matchedBuyers={matchedBuyers}
            buyersLoading={buyersLoading}
            onBuyerClick={setBuyerModal}
            onSendEmail={handleSendEmail}
            onMetricEdit={requestWorking}
            navigate={navigate}
            isDemo={isDemo}
            onDismiss={dismissListing}
            onRestore={restoreListing}
          />
        ) : viewMode === 'detail' ? (
          <DetailView
            listings={filtered}
            selected={selected}
            onSelectProperty={handleSelect}
            onBackToList={() => setViewMode('list')}
            sortBy={sortBy}
            setSortBy={setSortBy}
            onPromote={handleMoveListingToStage}
            onOpenContract={() => setContractOpen(true)}
            detailTab={detailTab}
            setDetailTab={setDetailTab}
            openArvTab={openArvTab}
            comps={comps}
            marketComps={marketComps}
            compsArv={compsArv}
            compsLoading={compsLoading}
            compsMeta={{ fetched: compsFetched, warning: compsWarning, mode: compsMode, signal: compsSignal, forced: compsModeForced, fallback: compsFallback, area: areaMeta }}
            areaSales={areaSales}
            compVotes={compVotes}
            compReasons={compReasons}
            pendingReason={pendingReason}
            setPendingReason={setPendingReason}
            handleThumbsUp={handleThumbsUp}
            handleThumbsDown={handleThumbsDown}
            handleReasonSubmit={handleReasonSubmit}
            recalculating={recalculating}
            refinedArv={refinedArv}
            excludedCount={excludedCount}
            rehabItems={rehabItems}
            setRehabItems={setRehabItems}
            matchedBuyers={matchedBuyers}
            buyersLoading={buyersLoading}
            onBuyerClick={setBuyerModal}
            onSendEmail={handleSendEmail}
            onMetricEdit={requestWorking}
            navigate={navigate}
            isDemo={isDemo}
            onDismiss={dismissListing}
            onRestore={restoreListing}
          />
        ) : viewMode === 'hotsheet' ? (
          <HotSheet onOpenListing={openDetail} />
        ) : (
          <MapView listings={filtered} onOpenDetail={openDetail} />
        )}
        </div>
      </div>

      {/* Email the agent from the user's own Gmail — they review and send; replies track onto the deal */}
      {agentOutreachOpen && savedDealForModal && (
        <GmailComposeModal
          deal={{ ...savedDealForModal, id: savedDealForModal._dealId }}
          mode="new"
          onClose={() => setAgentOutreachOpen(false)}
          onSent={() => { /* keep the modal on its sent confirmation; the user closes it */ }}
        />
      )}

      {/* Buyer Modal */}
      {buyerModal && <BuyerDetailModal buyer={buyerModal} onClose={() => setBuyerModal(null)} />}

      {/* "Add to Working?" — fires once per listing on the first deliberate metric edit */}
      {workingPrompt && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 16, width: '100%', maxWidth: 400, padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--white)' }}>Add this to your working deals list?</div>
              <button onClick={declineWorking} style={{ background: 'none', border: 'none', color: 'var(--gray-500)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--gray-300)', lineHeight: 1.6, marginBottom: 22 }}>
              You just adjusted the numbers on{' '}
              <span style={{ color: 'var(--teal)', fontWeight: 600 }}>{workingPrompt.address || 'this listing'}</span>.
              Track it as an active deal in your <strong style={{ color: 'var(--white)' }}>Working</strong> pipeline?
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={declineWorking} style={{ padding: '8px 14px', borderRadius: 8, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--gray-300)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>No, thanks</button>
              <button onClick={confirmWorking} style={{ padding: '8px 14px', borderRadius: 8, background: 'var(--teal)', border: '1px solid var(--teal)', color: 'var(--navy)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Add to Working</button>
            </div>
          </div>
        </div>
      )}

      {/* Contract Modal — backend creates Under Contract deal from MLS data */}
      {contractOpen && selected && (
        <ContractModal
          deal={selected}
          dealId={savedDealId}
          onClose={() => setContractOpen(false)}
          onGenerated={(data) => {
            if (data.deal_id) setSavedDealId(data.deal_id);
            // Refresh listings so contracted badge shows up
            loadListings();
          }}
        />
      )}

      {/* "Did we miss one?" — manual rescue + teach the scorer */}
      {missedOpenMo && (
        <MissedOpportunityModal
          onClose={() => setMissedOpenMo(false)}
          onSaved={() => {
            // Refresh so the rescued listing (and any background-rescued similar ones) appear
            loadListings();
          }}
        />
      )}

      {/* Saved searches — every standing hunt, its new matches, and what matches right now.
          Applying one puts its criteria back on the rail so the board shows exactly that search. */}
      {savedOpen && (
        <SavedSearchesPanel
          reloadKey={savedBump}
          onClose={() => { setSavedOpen(false); setSavedBump(b => b + 1); }}
          onApply={(criteria) => {
            const next = filtersFromCriteria(criteria);
            setFilters(next.filters);
            setStatusFilter(next.statusFilter);
            setMode(next.mode);
            // A saved search is about the whole market, not whatever narrowing happened to be on
            // screen — clear the chips that would silently shrink its result set.
            setTypeFilter(next.typeFilter);
            setPriceReducedOnly(next.priceReducedOnly);
            // Today's Deals is a recency VIEW, not a saved criterion — clear it so the saved search
            // shows everything it covers rather than today's slice of it.
            setTodaysWindowH(null);
          }}
        />
      )}

      {/* Quick Search result — live MLS lookup (found card or public-records fallback) */}
      {quickResult && (
        <QuickSearchModal
          result={quickResult}
          isDemo={isDemo}
          onClose={() => setQuickResult(null)}
          onOpenDetail={(listing) => { setQuickResult(null); openDetail(listing); }}
        />
      )}
    </div>
  );
}

// ── QuickSearchModal ──────────────────────────────────────────────────────────
// Renders the result of a live MLS lookup: a spinner, a found-listing card (with basics + Zillow /
// tax-records links + "Open full detail"), or a public-records fallback when the feed returns nothing.
function QuickSearchModal({ result, isDemo, onClose, onOpenDetail }) {
  const { loading, found, listing, source, fallback, error, query, was_filtered_out, fuzzy } = result || {};
  const tax = taxRecordsLink(found ? listing : { address: query });
  const backdrop = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
  const card = { background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 16, width: '100%', maxWidth: 520, overflow: 'hidden', maxHeight: '88vh', overflowY: 'auto' };
  const head = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--navy-border)' }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--white)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 9, fontWeight: 800, color: 'var(--teal)', background: 'var(--teal-dim)', padding: '2px 7px', borderRadius: 5, letterSpacing: '0.04em' }}>LIVE MLS</span>
        Quick Search
      </div>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--gray-500)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>×</button>
    </div>
  );

  return (
    <div style={backdrop} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={card}>
        {head}
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>
            <div style={{ width: 30, height: 30, border: '3px solid var(--navy-border)', borderTopColor: 'var(--teal)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 14px' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            Looking up “{query}” in the live MLS…
          </div>
        ) : found ? (
          <div style={{ padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--teal)', fontWeight: 600, marginBottom: 12 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#4A7C59', display: 'inline-block' }} />
              Found {source === 'local' ? 'in your cache' : source === 'demo' ? 'in your sample set' : `live on ${String(source || 'MLS').toUpperCase()}`}
              {listing.status ? ` · ${listing.status}` : ''}{(listing.mls_number || listing.mls_id) ? ` · MLS #${listing.mls_number || listing.mls_id}` : ''}
              {source && source !== 'local' && source !== 'demo' && <span style={{ color: 'var(--gray-500)' }}>· price band bypassed</span>}
            </div>
            {fuzzy && <div style={{ fontSize: 11, color: 'var(--gold)', marginBottom: 10 }}>Closest match to “{query}” — we ignored a stray comma / partial ZIP to find it.</div>}
            {was_filtered_out && <div style={{ fontSize: 11, color: '#E0A526', marginBottom: 10 }}>Note: your scorer had filtered this one out of opportunities.</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: 14, alignItems: 'start' }}>
              <div style={{ height: 100, borderRadius: 10, overflow: 'hidden', background: 'var(--navy)', display: 'grid', placeItems: 'center' }}>
                {(listing.photo_url || (listing.photo_urls && listing.photo_urls[0]))
                  ? <img src={proxyThumb(listing.photo_url || listing.photo_urls[0])} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.currentTarget.style.visibility = 'hidden'; }} />
                  : <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(156,150,144,0.4)" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--white)' }}>{listing.address}</div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 1 }}>{addressSub(listing)}</div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: 'var(--gray-300)', marginTop: 8 }}>
                  {listing.beds != null && <span><b style={{ color: 'var(--gray-500)', fontWeight: 500 }}>Beds</b> {listing.beds}</span>}
                  {listing.baths != null && <span><b style={{ color: 'var(--gray-500)', fontWeight: 500 }}>Baths</b> {listing.baths}</span>}
                  {listing.sqft != null && <span><b style={{ color: 'var(--gray-500)', fontWeight: 500 }}>Sq Ft</b> {Number(listing.sqft).toLocaleString()}</span>}
                  {listing.year_built != null && <span><b style={{ color: 'var(--gray-500)', fontWeight: 500 }}>Year</b> {listing.year_built}</span>}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  {!isDemo && <a href={`https://www.zillow.com/homes/${encodeURIComponent(String(listing.address || '').replace(/\s+/g, '-'))}_rb/`} target="_blank" rel="noopener noreferrer" style={qsLink('#1277e1')}>↗ Zillow</a>}
                  {tax && <a href={tax.url} target="_blank" rel="noopener noreferrer" style={qsLink('var(--teal)')}>{tax.label}</a>}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--navy-border)' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--white)' }}>{fmtMoney(listing.list_price)}</div>
              <button onClick={() => onOpenDetail(listing)} style={{ padding: '10px 18px', borderRadius: 10, border: 'none', background: 'var(--gold)', color: '#3a2c07', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Open full detail →
              </button>
            </div>
          </div>
        ) : (
          <div style={{ padding: 18 }}>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(224,165,38,0.15)', color: 'var(--gold)', display: 'grid', placeItems: 'center', fontSize: 20, flexShrink: 0 }}>↩</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--white)', marginBottom: 4 }}>No live MLS match for “{query}”</div>
                <div style={{ fontSize: 12.5, color: 'var(--gray-300)', lineHeight: 1.55 }}>{error ? `Lookup failed: ${error}` : (fallback?.message || 'Not found in the live MLS feed.')}</div>
              </div>
            </div>
            {!error && (
              <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                {fallback?.zillow_url && <a href={fallback.zillow_url} target="_blank" rel="noopener noreferrer" style={qsLink('#1277e1')}>↗ Zillow public record</a>}
                {tax && <a href={tax.url} target="_blank" rel="noopener noreferrer" style={qsLink('var(--teal)')}>{tax.label}</a>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
function qsLink(color) {
  return { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, textDecoration: 'none', color, background: 'var(--navy)', border: '1px solid var(--navy-border)', borderRadius: 8, padding: '7px 11px' };
}

// ── HotSheet ──────────────────────────────────────────────────────────────────
// Last-24h MLS changes grouped: new listings, price changes, status changes, closings.
// onOpenListing was already being passed by the parent (<HotSheet onOpenListing=... />) but the
// component declared NO props, so it was silently dropped and every row was a dead end — you could see
// that a listing changed but not act on it. The endpoint also returns photo_url/beds/baths/sqft
// specifically so this can render cards ("so the UI can render cards", routes/listings.js), and all of
// it was being discarded in favour of a plain text row.
function HotSheet({ onOpenListing }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    fetch('/api/listings/hot-sheet?hours=24')
      .then(r => r.json())
      .then(d => { if (alive) { setData(d); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);
  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)' }}>Loading the last 24 hours…</div>;
  if (!data || !data.groups) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)' }}>Could not load the hot sheet.</div>;
  const sections = [
    { key: 'new', label: '✨ New Listings', color: 'var(--teal)' },
    { key: 'price_change', label: '↕ Price Changes', color: 'var(--gold)' },
    { key: 'status_change', label: '🔄 Status Changes', color: '#818cf8' },
    { key: 'back_on_market', label: '↩ Back on Market', color: 'var(--teal)' },
    { key: 'closed', label: '✓ Closed (now comps)', color: '#4A7C59' },
    { key: 'off_market', label: '✗ Off Market', color: 'var(--gray-500)' },
  ];
  const total = Object.values(data.counts || {}).reduce((a, b) => a + b, 0);
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '4px 2px' }}>
      <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 14 }}>{total} change{total === 1 ? '' : 's'} in your market over the last {data.hours}h</div>
      {total === 0 && <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)' }}>No MLS changes in the last 24 hours.</div>}
      {sections.filter(s => (data.groups[s.key] || []).length).map(s => (
        <div key={s.key} style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: s.color, marginBottom: 8 }}>
            {s.label} <span style={{ color: 'var(--gray-500)', fontWeight: 600 }}>({data.groups[s.key].length})</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.groups[s.key].map((e, i) => {
              const thumb = e.photo_url || (e.photo_urls && e.photo_urls[0]);
              // A row is only openable if we still hold the listing (the LEFT JOIN can miss for a
              // listing that has since been retired) — don't offer a click that goes nowhere.
              const openable = !!(onOpenListing && e.listing_key && e.address);
              const specs = [
                e.beds ? `${e.beds} bd` : null,
                e.baths ? `${e.baths} ba` : null,
                e.sqft ? `${Number(e.sqft).toLocaleString()} sqft` : null,
              ].filter(Boolean).join(' · ');
              return (
                <div key={e.listing_key ? `${e.listing_key}-${i}` : i}
                  onClick={openable ? () => onOpenListing(e) : undefined}
                  title={openable ? 'Open this listing' : undefined}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8,
                    background: 'var(--navy-card)', border: '1px solid var(--navy-border)',
                    cursor: openable ? 'pointer' : 'default',
                  }}>
                  <div style={{ width: 56, height: 48, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: 'var(--navy)', display: 'grid', placeItems: 'center' }}>
                    {thumb
                      ? <img src={proxyThumb(thumb)} alt="" loading="lazy" onError={ev => { ev.currentTarget.style.visibility = 'hidden'; }} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(156,150,144,0.4)" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, color: 'var(--gray-200)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.address || e.listing_key}</div>
                    <div style={{ fontSize: 11, color: 'var(--gray-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {[[e.city, e.zip].filter(Boolean).join(', '), specs].filter(Boolean).join(' · ')}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                      {e.detail}
                      {e.event_type === 'price_change' && e.old_value && e.new_value ? ` — ${fmtK(+e.old_value)} → ${fmtK(+e.new_value)}` : ''}
                      {e.event_type === 'status_change' && e.old_value && e.new_value ? ` — ${e.old_value} → ${e.new_value}` : ''}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    {e.list_price ? <div style={{ fontSize: 13, color: 'var(--white)', fontWeight: 700 }}>{fmtK(e.list_price)}</div> : null}
                    <div style={{ fontSize: 10, color: 'var(--gray-600)' }}>{timeAgo(e.created_at)}</div>
                    {openable && <div style={{ fontSize: 10, color: 'var(--teal)', fontWeight: 700, marginTop: 2 }}>Open →</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── MlsTable (list view) ────────────────────────────────────────────────────
// Full-width, horizontally-scrolling MLS results table with user-managed columns
// (show/hide + drag-reorder), per the #list mockup. Photo + Address are locked
// leftmost; Price sits immediately right of Address; the Agent columns ship off by
// default. Column layout persists per-device in localStorage (Billy's choice).

const MLS_COL_DEFS = [
  { key: 'photo',      label: 'Photo',       on: true, lock: true },
  { key: 'address',    label: 'Address',     on: true, lock: true },
  { key: 'price',      label: 'Price',       on: true },
  { key: 'yr',         label: 'Yr Built',    on: true },
  { key: 'bd',         label: 'Beds',        on: true },
  { key: 'ba',         label: 'Baths',       on: true },
  { key: 'sqft',       label: 'Sq Ft',       on: true },
  { key: 'psf',        label: '$/SqFt',      on: true },
  { key: 'lot',        label: 'Lot Size',    on: true },
  { key: 'gar',        label: 'Garage',      on: true },
  { key: 'pool',       label: 'Pool',        on: true },
  { key: 'dom',        label: 'DOM',         on: true },
  { key: 'closed',     label: 'Closed Date', on: true },
  // Underwriting estimates for pre-scored opportunities — off by default (like the agent columns).
  { key: 'arv',        label: 'ARV',         on: false },
  { key: 'mao',        label: 'MAO',         on: false },
  { key: 'score',      label: 'Deal Score',  on: false },
  { key: 'agentName',  label: 'Agent Name',  on: false },
  { key: 'agentEmail', label: 'Agent Email', on: false },
  { key: 'agentPhone', label: 'Agent Phone', on: false },
];
const MLS_COLS_LS_KEY = 'feebe_mls_columns';

function loadMlsCols() {
  try {
    const saved = JSON.parse(localStorage.getItem(MLS_COLS_LS_KEY) || 'null');
    if (!Array.isArray(saved)) return MLS_COL_DEFS.map(c => ({ ...c }));
    const byKey = Object.fromEntries(MLS_COL_DEFS.map(c => [c.key, c]));
    const seen = new Set(); const out = [];
    for (const s of saved) {
      const def = byKey[s && s.key];
      if (!def || seen.has(def.key)) continue;
      seen.add(def.key);
      out.push({ ...def, on: def.lock ? true : !!s.on });
    }
    for (const c of MLS_COL_DEFS) if (!seen.has(c.key)) out.push({ ...c }); // cols added in later versions
    return out;
  } catch { return MLS_COL_DEFS.map(c => ({ ...c })); }
}

// "City, ST ZIP" — provider-agnostic; only renders the parts we actually have.
function addressSub(l) {
  const cityState = [l.city, l.state].filter(Boolean).join(', ');
  return [cityState, l.zip].filter(Boolean).join(' ') || '—';
}

// Street line ONLY (line 1 everywhere: list / detail / map). Spark's UnparsedAddress bundles
// "Street, City, ST ZIP" into l.address, which duplicated the city/zip subline — take just the street.
// Grid/demo already store the street alone, so they pass through unchanged.
function streetLine(l) {
  const a = String((l && l.address) || '').trim();
  if (!a) return '';
  if (a.includes(',')) return a.split(',')[0].trim();            // "5819 W Puget Ave, Glendale, AZ 85302" → street
  if (l && l.city) {                                             // no comma but city embedded → strip it + trailing
    const re = new RegExp('\\s+' + String(l.city).replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b.*$', 'i');
    const stripped = a.replace(re, '').trim();
    if (stripped) return stripped;
  }
  return a;
}

// Baths, showing a half-bath as ".5" (RESO BathroomsTotalInteger is usually whole, but render decimals
// faithfully when the feed carries them).
function fmtBaths(b) {
  if (b == null || b === '') return null;
  const n = Number(b);
  if (!Number.isFinite(n)) return String(b);
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(/\.0$/, '');
}
function statusTagLabel(l) {
  if (l.is_back_on_market) return 'BOM';
  if (l.status === 'Active Under Contract') return 'UC';
  return l.status;
}

// Canonical dwelling-type bucket. Providers spell the RESO PropertySubType differently ('Single Family
// Residence' on ACTRIS, 'Single Family' elsewhere, null on demo) — collapse them to one label so the
// Dwelling Type filter (and its "Single Family" default) matches across every market.
function dwellingType(subtype) {
  const s = String(subtype || '').toLowerCase();
  if (!s || s.includes('single family') || s === 'sfr') return 'Single Family';
  if (s.includes('condo')) return 'Condo';
  if (s.includes('town')) return 'Townhouse';
  if (s.includes('manufactured') || s.includes('mobile') || s.includes('modular')) return 'Manufactured/Mobile';
  if (s.includes('multi') || s.includes('duplex') || s.includes('triplex') || s.includes('fourplex') || s.includes('plex')) return 'Multi-Family';
  if (s.includes('land') || s.includes('lot') || s.includes('acre')) return 'Land';
  return subtype;  // preserve anything unusual (e.g. 'See Remarks') as its own option
}

// One table cell for a given column key. Returns a <td>.
function MlsCell({ l, colKey, tags }) {
  const num = { fontVariantNumeric: 'tabular-nums' };
  const td = (children, extra) => <td style={{ padding: '9px 10px', borderBottom: '1px solid var(--navy-border)', whiteSpace: 'nowrap', verticalAlign: 'middle', color: 'var(--gray-300)', ...extra }}>{children}</td>;
  switch (colKey) {
    case 'photo': {
      const thumb = (l.photo_urls && l.photo_urls[0]) || l.photo_url || (l.photos && l.photos[0]);
      const n = (l.photo_urls && l.photo_urls.length) || (l.photos && l.photos.length) || (thumb ? 1 : 0);
      const sc = STATUS_COLOR[l.is_back_on_market ? 'Back on Market' : l.status] || '#6B6760';
      return td(
        <div style={{ position: 'relative', width: 96, height: 72, borderRadius: 8, overflow: 'hidden', background: 'var(--navy)', display: 'grid', placeItems: 'center' }}>
          {thumb
            ? <img src={proxyThumb(thumb)} alt="" loading="lazy" onError={e => { e.currentTarget.style.visibility = 'hidden'; }} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(156,150,144,0.4)" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>}
          {n > 0 && <span style={{ position: 'absolute', top: 2, left: 3, fontSize: 8, color: '#fff', background: 'rgba(26,23,20,0.7)', padding: '1px 4px', borderRadius: 8 }}>1/{n}</span>}
          <span style={{ position: 'absolute', top: 3, right: 3, fontSize: 8, fontWeight: 800, padding: '2px 5px', borderRadius: 5, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.02em', background: sc, boxShadow: '0 1px 2px rgba(0,0,0,0.28)' }}>{statusTagLabel(l)}</span>
        </div>
      );
    }
    case 'address': {
      const showBadge = l.badge && l.badge !== 'Pass';
      const b = BADGE[l.badge] || BADGE['Maybe'];
      return td(
        <div style={{ minWidth: 168 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--white)', display: 'flex', alignItems: 'center' }}>
            {streetLine(l)}
            {showBadge && <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.03em', padding: '2px 6px', borderRadius: 5, textTransform: 'uppercase', marginLeft: 6, background: b.bg, color: b.color }}>{l.badge}</span>}
          </div>
          <div style={{ color: 'var(--gray-500)', fontSize: 11, marginTop: 1 }}>{addressSub(l)}</div>
          {/* WHY the tool flagged this — an unexplained "deal" is not actionable. Only rendered while
              Today's Deals is on, where the tags are the point of the view. */}
          {tags && tags.length > 0 && (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
              {tags.map(t => (
                <span key={t.key} title="Why this was flagged as a deal"
                  style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.02em', padding: '2px 6px',
                           borderRadius: 5, color: t.color, background: `${t.color}1f`, whiteSpace: 'nowrap' }}>
                  {t.label}
                </span>
              ))}
            </div>
          )}
        </div>
      );
    }
    case 'price': {
      const pr = (l.status === 'Closed' && (l.sold_price || l.close_price)) ? (l.sold_price || l.close_price) : l.list_price;
      return td(<span style={{ fontWeight: 700, color: 'var(--white)' }}>{fmtMoney(pr)}</span>, num);
    }
    case 'yr':   return td(l.year_built || '—', num);
    case 'bd':   return td(l.beds ?? '—', num);
    case 'ba':   return td(l.baths ?? '—', num);
    case 'sqft': return td(l.sqft ? l.sqft.toLocaleString() : '—', num);
    case 'psf':  return td(l.sqft && l.list_price ? '$' + Math.round(l.list_price / l.sqft) : '—', num);
    case 'lot':  return td(l.lot_size_sqft ? Math.round(Number(l.lot_size_sqft)).toLocaleString() : '—', num);
    case 'gar':  return td(l.garage_spaces ?? '—', num);
    case 'pool': return td(l.pool === 1 ? 'Yes' : l.pool === 0 ? 'No' : '—');
    case 'dom':  return td(l.days_on_market ?? '—', num);
    case 'closed': {
      if (!l.close_date) return td('—', num);
      const d = new Date(l.close_date);
      return td(isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US'), num);
    }
    case 'arv':   return td(l.ai_arv != null ? <span style={{ color: 'var(--teal)', fontWeight: 700 }}>{fmtMoney(l.ai_arv)}</span> : (l.financials_pending ? '…' : '—'), num);
    case 'mao':   return td(l.max_offer != null ? <span style={{ fontWeight: 700 }}>{fmtMoney(l.max_offer)}</span> : (l.financials_pending ? '…' : '—'), num);
    case 'score': return td(l.opportunity_score != null ? l.opportunity_score : '—', num);
    case 'agentName':  return td(l.agent_name || '—');
    case 'agentEmail': return td(l.agent_email ? <a href={`mailto:${l.agent_email}`} onClick={e => e.stopPropagation()} style={{ color: 'var(--teal)', textDecoration: 'none' }}>{l.agent_email}</a> : '—');
    case 'agentPhone': return td(l.agent_phone ? <a href={`tel:${String(l.agent_phone).replace(/[^0-9]/g, '')}`} onClick={e => e.stopPropagation()} style={{ color: 'var(--teal)', textDecoration: 'none' }}>{l.agent_phone}</a> : '—', num);
    default: return td('');
  }
}

function MlsTable({ listings, nounSing = 'opportunity', nounPlur = 'opportunities', selected, onSelect, checkedIds, onToggleSelect, sortBy, setSortBy, dealTags }) {
  const [cols, setCols] = useState(loadMlsCols);
  const [panelOpen, setPanelOpen] = useState(false);
  const [dragKey, setDragKey] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const panelWrapRef = useRef(null);

  // Persist column layout per-device.
  useEffect(() => {
    try { localStorage.setItem(MLS_COLS_LS_KEY, JSON.stringify(cols.map(c => ({ key: c.key, on: c.on })))); } catch {}
  }, [cols]);

  // Close the columns panel on outside click.
  useEffect(() => {
    if (!panelOpen) return;
    const onDoc = e => { if (panelWrapRef.current && !panelWrapRef.current.contains(e.target)) setPanelOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [panelOpen]);

  const visible = cols.filter(c => c.on);
  const toggleCol = key => setCols(cs => cs.map(c => (c.key === key && !c.lock) ? { ...c, on: !c.on } : c));
  const moveCol = (src, tgt) => {
    if (!src || src === tgt) return;
    setCols(cs => {
      const from = cs.findIndex(c => c.key === src);
      const to = cs.findIndex(c => c.key === tgt);
      if (from < 0 || to < 0) return cs;
      const next = cs.slice();
      const [it] = next.splice(from, 1);
      next.splice(to, 0, it);
      return next;
    });
  };

  const thStyle = { position: 'sticky', top: 0, textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--gray-500)', fontWeight: 700, padding: '10px', background: 'var(--navy-card)', borderBottom: '1px solid var(--navy-border)', whiteSpace: 'nowrap', zIndex: 1 };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden', background: 'var(--navy)' }}>
      {/* Result meta + column manager */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '12px 16px 10px', flexShrink: 0, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>
          <b style={{ color: 'var(--white)' }}>{listings.length}</b> {listings.length === 1 ? nounSing : nounPlur} · click any row for full details
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* Sort by — left of Columns (D6). Server does the ordering via ?sort=. */}
        {setSortBy && (
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} title="Sort by"
            style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy-card)', color: 'var(--gray-300)', fontSize: 12, fontWeight: 700, cursor: 'pointer', outline: 'none' }}>
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>Sort: {o.label}</option>)}
          </select>
        )}
        <div ref={panelWrapRef} style={{ position: 'relative' }}>
          <button onClick={() => setPanelOpen(o => !o)} style={{ padding: '7px 12px', borderRadius: 8, border: `1px solid ${panelOpen ? 'var(--teal)' : 'var(--navy-border)'}`, background: panelOpen ? 'var(--teal-dim)' : 'var(--navy-card)', color: panelOpen ? 'var(--teal)' : 'var(--gray-300)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            ▤ Columns ▾
          </button>
          {panelOpen && (
            <div style={{ position: 'absolute', right: 0, top: 36, zIndex: 3000, width: 220, maxHeight: 360, overflowY: 'auto', background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 10, boxShadow: '0 12px 32px rgba(26,23,20,0.22)', padding: 8 }}>
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--gray-500)', padding: '4px 8px 6px' }}>Show &amp; reorder · drag ⠿ to sort</div>
              {cols.map(c => (
                <div key={c.key}
                  draggable
                  onDragStart={() => setDragKey(c.key)}
                  onDragEnd={() => { setDragKey(null); setDragOverKey(null); }}
                  onDragOver={e => { e.preventDefault(); if (dragOverKey !== c.key) setDragOverKey(c.key); }}
                  onDrop={e => { e.preventDefault(); moveCol(dragKey, c.key); setDragKey(null); setDragOverKey(null); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 9, padding: '6px 8px', borderRadius: 7, fontSize: 12, cursor: 'grab',
                    opacity: dragKey === c.key ? 0.4 : 1,
                    background: (dragOverKey === c.key && dragKey && dragKey !== c.key) ? 'var(--teal-dim)' : 'transparent',
                    boxShadow: (dragOverKey === c.key && dragKey && dragKey !== c.key) ? 'inset 0 2px 0 var(--teal)' : 'none',
                  }}
                >
                  <span title="Drag to reorder" style={{ color: 'var(--gray-500)', fontSize: 13 }}>⠿</span>
                  <span
                    onClick={() => toggleCol(c.key)}
                    style={{ width: 16, height: 16, borderRadius: 4, flexShrink: 0, display: 'grid', placeItems: 'center', fontSize: 10, color: '#fff', cursor: c.lock ? 'default' : 'pointer', border: `2px solid ${c.on ? 'var(--teal)' : 'var(--navy-border)'}`, background: c.on ? 'var(--teal)' : 'transparent', opacity: c.lock ? 0.55 : 1 }}
                  >{c.on ? '✓' : ''}</span>
                  <span style={{ flex: 1, color: 'var(--gray-300)' }}>{c.label}</span>
                  {c.lock && <span style={{ fontSize: 9, color: 'var(--gray-500)' }}>always</span>}
                </div>
              ))}
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Scrollable table */}
      <div style={{ flex: 1, overflow: 'auto', margin: '0 16px 12px', border: '1px solid var(--navy-border)', borderRadius: 12, background: 'var(--navy-card)' }}>
        {listings.length === 0 ? (
          <div style={{ padding: 40, color: 'var(--gray-500)', fontSize: 13, textAlign: 'center' }}>No listings match this filter.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 980 }}>
            <thead>
              <tr>
                {onToggleSelect && <th style={{ ...thStyle, width: 34 }} />}
                {visible.map(c => <th key={c.key} style={thStyle}>{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {listings.map(l => {
                const isSel = selected?.mls_id === l.mls_id;
                return (
                  <tr key={l.mls_id} onClick={() => onSelect(l)} style={{ cursor: 'pointer', background: isSel ? 'var(--teal-dim)' : 'transparent' }}>
                    {onToggleSelect && (
                      <td style={{ padding: '9px 8px', borderBottom: '1px solid var(--navy-border)' }} onClick={e => e.stopPropagation()}>
                        <RowCheckbox checked={!!checkedIds?.has(l.mls_id)} onChange={on => onToggleSelect(l.mls_id, on)} />
                      </td>
                    )}
                    {visible.map(c => <MlsCell key={c.key} l={l} colKey={c.key} tags={c.key === 'address' && dealTags ? dealTags(l) : null} />)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ fontSize: 11, color: 'var(--gray-500)', padding: '0 16px 12px' }}>
        Toggle, reorder (drag ⠿), and add agent columns with <b style={{ color: 'var(--teal)' }}>▤ Columns</b> (saved on this device). Garage &amp; Pool populate from the MLS sync (fast-follow).
      </div>
    </div>
  );
}

// ── ListView ──────────────────────────────────────────────────────────────────
// Full-width results table. A row click opens the standalone Detail view (onSelect =
// openDetail from the parent), so no inline detail pane here.

function ListView({ listings, nounSing, nounPlur, selected, onSelect, checkedIds, onToggleSelect, sortBy, setSortBy, dealTags }) {
  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      <MlsTable listings={listings} nounSing={nounSing} nounPlur={nounPlur} selected={selected} onSelect={onSelect} checkedIds={checkedIds} onToggleSelect={onToggleSelect} sortBy={sortBy} setSortBy={setSortBy} dealTags={dealTags} />
    </div>
  );
}

// ── DetailView ────────────────────────────────────────────────────────────────
// Standalone Detail view (mockup #detail): a scrollable property-list sidebar on the
// left + the full DetailPanel on the right. Replaces the filter rail while active.

function DetailView({ listings, selected, onSelectProperty, onBackToList, sortBy, setSortBy, ...detailProps }) {
  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      <PropertyList listings={listings} selected={selected} onSelect={onSelectProperty} onBackToList={onBackToList} sortBy={sortBy} setSortBy={setSortBy} />
      <div style={{ flex: 1, overflow: 'auto', background: 'var(--navy)', minWidth: 0 }}>
        {selected
          ? <DetailPanel listing={selected} {...detailProps} />
          : <div style={{ padding: 40, color: 'var(--gray-500)', fontSize: 13, textAlign: 'center' }}>Select a property on the left to see its details.</div>}
      </div>
    </div>
  );
}

// Left sidebar for the Detail view: one row per result (cover photo + full address +
// price · status). Click to switch the subject property.
function PropertyList({ listings, selected, onSelect, onBackToList, sortBy, setSortBy }) {
  return (
    <div style={{ width: 300, flexShrink: 0, overflowY: 'auto', background: 'var(--navy-card)', borderRight: '1px solid var(--navy-border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid var(--navy-border)', position: 'sticky', top: 0, background: 'var(--navy-card)', zIndex: 1 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--gray-500)' }}>Results · {listings.length}</div>
        <button onClick={onBackToList} title="Back to the results table" style={{ background: 'none', border: '1px solid var(--navy-border)', borderRadius: 6, color: 'var(--gray-300)', fontSize: 11, fontWeight: 700, padding: '4px 9px', cursor: 'pointer' }}>≣ List</button>
      </div>
      {/* Sort lived ONLY in the list-view table header, so in Detail view there was no way to reorder
          results at all — the order was whatever the last visit to the list had set. Same server-side
          ?sort=; this just makes it reachable from the view the user is actually in. */}
      {setSortBy && (
        <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--navy-border)', position: 'sticky', top: 45, background: 'var(--navy-card)', zIndex: 1 }}>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} title="Sort results"
            style={{ width: '100%', padding: '6px 8px', borderRadius: 7, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--gray-300)', fontSize: 11, fontWeight: 700, cursor: 'pointer', outline: 'none' }}>
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>Sort: {o.label}</option>)}
          </select>
        </div>
      )}
      {listings.length === 0 ? (
        <div style={{ padding: 24, color: 'var(--gray-500)', fontSize: 12, textAlign: 'center' }}>No listings match this filter.</div>
      ) : listings.map(l => {
        const on = selected?.mls_id === l.mls_id;
        const thumb = (l.photo_urls && l.photo_urls[0]) || l.photo_url || (l.photos && l.photos[0]);
        return (
          <div key={l.mls_id} onClick={() => onSelect(l)} style={{
            display: 'flex', gap: 10, padding: 10, cursor: 'pointer', borderBottom: '1px solid var(--navy-border)',
            background: on ? 'var(--teal-dim)' : 'transparent', borderLeft: `3px solid ${on ? 'var(--teal)' : 'transparent'}`,
          }}>
            <div style={{ width: 56, height: 48, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: 'var(--navy)', display: 'grid', placeItems: 'center' }}>
              {thumb
                ? <img src={proxyThumb(thumb)} alt="" loading="lazy" onError={e => { e.currentTarget.style.visibility = 'hidden'; }} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(156,150,144,0.4)" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: on ? 'var(--teal)' : 'var(--white)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{streetLine(l)}</div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{addressSub(l)}</div>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--gray-300)', marginTop: 2 }}>{fmtMoney(l.list_price)} · {l.status}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── MapView ───────────────────────────────────────────────────────────────────

// Color-coded pins by status (matches spec)
const STATUS_PIN_COLORS = {
  'Active':         '#14504A',
  'Pending':        '#14504A',
  'Back on Market': '#E0A526',
  'Expired':        '#9C9690',
  'Withdrawn':      '#9C9690',
};

// CartoDB Positron — clean white/gray minimal tiles
const STREET_TILES = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
const STREET_ATTR  = '© OpenStreetMap contributors © CARTO';
const SAT_TILES    = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';

// Reusable Leaflet map (markers + tile toggle + legend). Used both as the full-width
// "map view" and as the default right-pane content of the list view.
// Coord + geometry helpers for the draw-to-bound map (all pure, no Leaflet needed).
function hasCoords(l) {
  const lat = Number(l.lat), lng = Number(l.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 && lng !== 0;
}
function haversineM(aLat, aLng, bLat, bLng) {
  const R = 6371000, toRad = d => d * Math.PI / 180;
  const dLat = toRad(bLat - aLat), dLng = toRad(bLng - aLng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}
function pointInPolygon(lat, lng, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const yi = ring[i].lat, xi = ring[i].lng, yj = ring[j].lat, xj = ring[j].lng;
    const intersect = ((yi > lat) !== (yj > lat)) && (lng < (xj - xi) * (lat - yi) / ((yj - yi) || 1e-12) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}
// True if a listing's coords fall inside the drawn search area (circle / rectangle / polygon).
function insideBound(l, bound) {
  const lat = Number(l.lat), lng = Number(l.lng);
  if (bound.type === 'circle') return haversineM(bound.center.lat, bound.center.lng, lat, lng) <= bound.radius;
  if (bound.type === 'rectangle') return bound.bounds && bound.bounds.contains([lat, lng]);
  if (bound.type === 'polygon') return pointInPolygon(lat, lng, bound.latlngs);
  return true;
}

// Reusable Leaflet map with draw-to-bound tools + rich pin popups. Drawing a circle / rectangle /
// polygon bounds the visible pins (and the count) to that area; a pin popup shows a photo + basics and
// "Open full detail →" hands off to the Detail view.
function LeafletMap({ listings, onOpenDetail }) {
  const mapRef        = useRef(null);
  const mapObjRef     = useRef(null);
  const tileRef       = useRef(null);
  const markersRef    = useRef([]);
  const drawnLayerRef = useRef(null);   // the drawn search-area shape (one at a time)
  const activeDrawRef = useRef(null);   // the L.Draw handler currently enabled
  const openDetailRef = useRef(onOpenDetail);
  const [tileType, setTileType] = useState('street');
  const [mapReady, setMapReady] = useState(false);
  const [drawMode, setDrawMode] = useState(null);      // null | 'circle' | 'rectangle' | 'polygon'
  const [bound, setBound] = useState(null);            // drawn search area geometry, or null
  const [drawAvailable, setDrawAvailable] = useState(false);
  openDetailRef.current = onOpenDetail;

  // Listings bounded to the drawn area (or all geocoded listings when nothing is drawn).
  const visible = useMemo(() => {
    const geocoded = listings.filter(hasCoords);
    return bound ? geocoded.filter(l => insideBound(l, bound)) : geocoded;
  }, [listings, bound]);

  // Initialize Leaflet (+ leaflet-draw) from CDN, then create the map.
  useEffect(() => {
    let cancelled = false;
    const injectCss = (id, href) => { if (!document.querySelector('#' + id)) { const el = document.createElement('link'); el.id = id; el.rel = 'stylesheet'; el.href = href; document.head.appendChild(el); } };
    const loadScript = (src, id) => new Promise((res) => {
      if (id && document.querySelector('#' + id)) return res();
      const s = document.createElement('script'); if (id) s.id = id; s.src = src; s.onload = res; s.onerror = res; document.head.appendChild(s);
    });
    async function ensureLibs() {
      injectCss('leaflet-css', 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css');
      injectCss('leaflet-draw-css', 'https://cdnjs.cloudflare.com/ajax/libs/leaflet.draw/1.0.4/leaflet.draw.css');
      if (!window.L) await loadScript('https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js', 'leaflet-js');
      if (window.L && !window.L.Draw) await loadScript('https://cdnjs.cloudflare.com/ajax/libs/leaflet.draw/1.0.4/leaflet.draw.js', 'leaflet-draw-js');
    }
    function init() {
      if (cancelled || !mapRef.current || mapObjRef.current) return;
      const L = window.L;
      if (!L || !L.map) return; // a cached <script> can resolve before the global is ready
      const map = L.map(mapRef.current, { center: [30.267, -97.743], zoom: 12, attributionControl: false, scrollWheelZoom: true });
      tileRef.current = L.tileLayer(STREET_TILES, { attribution: STREET_ATTR, detectRetina: true, maxZoom: 19 }).addTo(map);
      // Drawing finished → keep exactly one shape and bound the results to it.
      map.on('draw:created', (e) => {
        if (drawnLayerRef.current) { drawnLayerRef.current.remove(); drawnLayerRef.current = null; }
        const layer = e.layer;
        layer.addTo(map);
        drawnLayerRef.current = layer;
        activeDrawRef.current = null;
        setDrawMode(null);
        if (e.layerType === 'circle') setBound({ type: 'circle', center: layer.getLatLng(), radius: layer.getRadius() });
        else if (e.layerType === 'rectangle') setBound({ type: 'rectangle', bounds: layer.getBounds() });
        else setBound({ type: 'polygon', latlngs: layer.getLatLngs()[0].map(p => ({ lat: p.lat, lng: p.lng })) });
      });
      mapObjRef.current = map;
      setDrawAvailable(!!L.Draw);
      setMapReady(true);
    }
    ensureLibs().then(async () => {
      // A cached <script> tag can resolve its onload before window.L is actually defined, so poll
      // for the global before creating the map. Bounded so we never spin forever.
      for (let i = 0; i < 60 && !cancelled && !(window.L && window.L.map); i++) await new Promise(r => setTimeout(r, 40));
      init();
    });
    return () => { cancelled = true; if (mapObjRef.current) { mapObjRef.current.remove(); mapObjRef.current = null; } };
  }, []);

  // Switch tile layer
  useEffect(() => {
    const L = window.L;
    if (!L || !mapObjRef.current) return;
    if (tileRef.current) tileRef.current.remove();
    const opts = tileType === 'satellite'
      ? { attribution: '© Esri World Imagery', maxZoom: 19 }
      : { attribution: STREET_ATTR, detectRetina: true, maxZoom: 19 };
    tileRef.current = L.tileLayer(tileType === 'satellite' ? SAT_TILES : STREET_TILES, opts).addTo(mapObjRef.current);
  }, [tileType]);

  // Render markers for the currently-visible (bounded) listings, with a rich popup each.
  useEffect(() => {
    const L = window.L;
    if (!L || !mapObjRef.current) return;
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    visible.forEach(listing => {
      const stKey = listing.is_back_on_market ? 'Back on Market' : listing.status;
      const color = STATUS_PIN_COLORS[stKey] || '#6B6760';
      const size = 12;
      const html = `<div style="position:relative;width:${size + 4}px;height:${size + 4}px;display:flex;align-items:center;justify-content:center;"><div style="position:relative;width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 1px 5px rgba(0,0,0,0.5);cursor:pointer"></div></div>`;
      const icon = L.divIcon({ className: '', html, iconSize: [size + 6, size + 6], iconAnchor: [(size + 6) / 2, (size + 6) / 2] });
      const marker = L.marker([Number(listing.lat), Number(listing.lng)], { icon })
        .addTo(mapObjRef.current)
        .bindPopup(buildPinPopup(listing, () => openDetailRef.current && openDetailRef.current(listing)), { minWidth: 216, closeButton: true });
      markersRef.current.push(marker);
    });

    // Fit to the visible pins. This effect re-runs only on data/bound change (not on user pan/zoom),
    // so a drawn area zooms to itself and clearing returns to the full set. Fit to the P10–P90 core so
    // a few far-flung outliers don't zoom a metro market out to the whole state.
    if (markersRef.current.length) {
      const pts = markersRef.current.map(m => m.getLatLng()).filter(p => p && Number.isFinite(p.lat) && Number.isFinite(p.lng) && !(p.lat === 0 && p.lng === 0));
      if (pts.length) {
        let bounds;
        if (pts.length >= 10 && !bound) {
          const lats = pts.map(p => p.lat).sort((a, b) => a - b);
          const lngs = pts.map(p => p.lng).sort((a, b) => a - b);
          const q = (arr, f) => arr[Math.min(arr.length - 1, Math.max(0, Math.floor(arr.length * f)))];
          bounds = L.latLngBounds([q(lats, 0.10), q(lngs, 0.10)], [q(lats, 0.90), q(lngs, 0.90)]);
        } else {
          bounds = L.latLngBounds(pts);
        }
        try { if (bounds && bounds.isValid()) mapObjRef.current.fitBounds(bounds, { padding: [45, 45], maxZoom: 15 }); } catch { /* degenerate bounds */ }
      }
    }
  }, [visible, bound, mapReady]);

  const startDraw = (mode) => {
    const L = window.L, map = mapObjRef.current;
    if (!L || !map || !L.Draw) return;
    if (activeDrawRef.current) { activeDrawRef.current.disable(); activeDrawRef.current = null; }
    if (mode === null) { setDrawMode(null); return; } // Pan / cancel
    const shapeOptions = { color: '#14504A', weight: 2, fillColor: '#14504A', fillOpacity: 0.12, dashArray: '6 4' };
    let handler;
    if (mode === 'circle') handler = new L.Draw.Circle(map, { shapeOptions });
    else if (mode === 'rectangle') handler = new L.Draw.Rectangle(map, { shapeOptions });
    else if (mode === 'polygon') handler = new L.Draw.Polygon(map, { shapeOptions, allowIntersection: false });
    if (handler) { try { handler.enable(); activeDrawRef.current = handler; setDrawMode(mode); } catch { /* draw unavailable */ } }
  };
  const clearDrawn = () => {
    if (activeDrawRef.current) { activeDrawRef.current.disable(); activeDrawRef.current = null; }
    if (drawnLayerRef.current) { drawnLayerRef.current.remove(); drawnLayerRef.current = null; }
    setDrawMode(null);
    setBound(null);
  };

  const toolBtn = (active) => ({
    width: 34, height: 34, borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 15,
    display: 'grid', placeItems: 'center',
    background: active ? 'var(--teal)' : 'transparent', color: active ? '#fff' : 'var(--gray-500)',
  });

  return (
    <div style={{ width: '100%', height: '100%', flex: 1, position: 'relative', overflow: 'hidden' }}>
      <div ref={mapRef} style={{ position: 'absolute', inset: 0 }} />

      {/* Draw tools (top-left) — pan / circle / rectangle / polygon / clear */}
      {drawAvailable && (
        <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 1000, display: 'flex', flexDirection: 'column', gap: 3, padding: 5, background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 10, boxShadow: '0 4px 14px rgba(26,23,20,0.16)' }}>
          <button title="Pan (stop drawing)" onClick={() => startDraw(null)} style={toolBtn(drawMode === null)}>✋</button>
          <div style={{ height: 1, background: 'var(--navy-border)', margin: '1px 4px' }} />
          <button title="Draw a circle" onClick={() => startDraw('circle')} style={toolBtn(drawMode === 'circle')}>◯</button>
          <button title="Draw a rectangle" onClick={() => startDraw('rectangle')} style={toolBtn(drawMode === 'rectangle')}>▭</button>
          <button title="Draw a polygon" onClick={() => startDraw('polygon')} style={toolBtn(drawMode === 'polygon')}>⬡</button>
          <div style={{ height: 1, background: 'var(--navy-border)', margin: '1px 4px' }} />
          <button title="Clear the drawn area" onClick={clearDrawn} disabled={!bound && drawMode === null} style={{ ...toolBtn(false), color: bound ? '#B85450' : 'var(--gray-500)', opacity: bound ? 1 : 0.5 }}>🗑</button>
        </div>
      )}

      {/* Tile toggle (top-right) */}
      <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 1000, display: 'flex', background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 8, overflow: 'hidden' }}>
        {['street', 'satellite'].map(t => (
          <button key={t} onClick={() => setTileType(t)} style={{
            padding: '6px 14px', fontSize: 11, fontWeight: 700,
            background: tileType === t ? 'var(--teal)' : 'transparent',
            color: tileType === t ? '#fff' : 'var(--gray-300)',
            border: 'none', cursor: 'pointer', textTransform: 'capitalize',
          }}>{t}</button>
        ))}
      </div>

      {/* Draw hint / bounded-count CTA (bottom-center) */}
      <div style={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px', background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, boxShadow: '0 4px 14px rgba(26,23,20,0.16)', fontSize: 12 }}>
        {bound ? (
          <>
            <span style={{ color: 'var(--gray-300)', fontWeight: 600 }}><b style={{ color: 'var(--teal)' }}>{visible.length}</b> in drawn area</span>
            <button onClick={clearDrawn} style={{ padding: '5px 11px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--gray-300)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>✕ Show all results</button>
          </>
        ) : (
          <span style={{ color: 'var(--gray-500)' }}>{drawAvailable ? 'Draw a search area — ◯ circle, ▭ rectangle, or ⬡ polygon. Click a pin for details.' : 'Click a pin for photos + details.'}</span>
        )}
      </div>

      {/* Legend (bottom-left) */}
      <div style={{ position: 'absolute', bottom: 12, left: 12, zIndex: 1000, padding: '10px 14px', background: 'rgba(250, 247, 242,0.92)', border: '1px solid var(--navy-border)', borderRadius: 8, fontSize: 10, color: 'var(--gray-300)' }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Status</div>
        {Object.entries(STATUS_PIN_COLORS).map(([label, color]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, border: '1px solid white', display: 'inline-block' }} />
            <span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Build a Leaflet popup (a real DOM node so the button can carry a React callback) with a photo,
// address/status/basics, price, and an "Open full detail →" hand-off to the Detail view.
function buildPinPopup(l, onOpen) {
  const el = document.createElement('div');
  el.style.width = '214px';
  const thumb = (l.photo_urls && l.photo_urls[0]) || l.photo_url || (l.photos && l.photos[0]);
  const n = (l.photo_urls && l.photo_urls.length) || (l.photos && l.photos.length) || (thumb ? 1 : 0);
  const stKey = l.is_back_on_market ? 'Back on Market' : l.status;
  const facts = [l.beds && `${l.beds} bd`, l.baths && `${l.baths} ba`, l.sqft && `${Number(l.sqft).toLocaleString()} sqft`].filter(Boolean).join(' · ');
  const esc = s => String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  el.innerHTML =
    `<div style="height:118px;background:#1a1714;border-radius:8px;overflow:hidden;position:relative;display:grid;place-items:center;">
       ${thumb ? `<img src="${esc(proxyThumb(thumb))}" alt="" style="width:100%;height:100%;object-fit:cover" onerror="this.style.visibility='hidden'"/>` : '🏠'}
       ${n > 0 ? `<span style="position:absolute;top:6px;right:6px;background:rgba(26,23,20,0.72);color:#fff;font-size:10px;padding:2px 7px;border-radius:12px">📷 1/${n}</span>` : ''}
     </div>
     <div style="padding:9px 2px 2px">
       <div style="font-weight:600;font-size:13px;color:#1A1714">${esc(streetLine(l))}</div>
       <div style="color:#6B6760;font-size:11px;margin-top:1px">${esc(addressSub(l))} · ${esc(stKey)}</div>
       ${facts ? `<div style="font-size:11.5px;color:#3d3833;margin-top:6px">${esc(facts)}</div>` : ''}
       <div style="font-weight:700;font-size:15px;margin-top:5px;color:#14504A">${esc(fmtMoney(l.list_price))}</div>
       <button type="button" data-open style="width:100%;margin-top:9px;padding:8px;border-radius:8px;border:none;background:#14504A;color:#fff;font-size:12px;font-weight:700;cursor:pointer">Open full detail →</button>
     </div>`;
  const btn = el.querySelector('[data-open]');
  if (btn) btn.addEventListener('click', () => onOpen && onOpen());
  return el;
}

// Full-width map view. Pins bound to a drawn area; a pin popup's "Open full detail" enters the Detail
// view via onOpenDetail (no inline overlay — Detail is its own view since Slice 3).
function MapView({ listings, onOpenDetail }) {
  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
      <LeafletMap listings={listings} onOpenDetail={onOpenDetail} />
    </div>
  );
}

// ── PresetsPage ───────────────────────────────────────────────────────────────
// The settings that drive scraping / comps / scoring, made visible (mockup #presets). Values shown are
// the platform defaults actually used by the backend (comp_config / mlsSync / opportunityScorer), so
// this is a truthful surface, not the mockup's illustrative numbers. Most are managed centrally; the
// one interactive, per-device control here is the default landing view.
function PresetSection({ icon, title, desc, children }) {
  return (
    <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, marginBottom: 15, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '13px 16px', borderBottom: '1px solid var(--navy-border)' }}>
        <div style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--teal-dim)', display: 'grid', placeItems: 'center', fontSize: 16, flexShrink: 0 }}>{icon}</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--white)' }}>{title}</div>
          {desc && <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginTop: 1 }}>{desc}</div>}
        </div>
      </div>
      <div style={{ padding: '4px 16px 8px' }}>{children}</div>
    </div>
  );
}
function PresetRow({ title, desc, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '12px 0', borderBottom: '1px solid var(--navy-border)' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--gray-300)' }}>{title}</div>
        {desc && <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginTop: 2 }}>{desc}</div>}
      </div>
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>{children}</div>
    </div>
  );
}
function ValPill({ children, tone }) {
  const c = tone === 'gold' ? { bg: 'rgba(224,165,38,0.12)', fg: 'var(--gold)', bd: 'rgba(224,165,38,0.4)' } : { bg: 'var(--navy)', fg: 'var(--gray-300)', bd: 'var(--navy-border)' };
  return <span style={{ fontSize: 12, fontWeight: 600, color: c.fg, background: c.bg, border: `1px solid ${c.bd}`, borderRadius: 8, padding: '6px 11px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{children}</span>;
}

function PresetsPage({ defaultView, onDefaultViewChange, onClose }) {
  const chips = ['Price: $50k–$1.5M (TX) · $25k–$3M (AZ)', 'Homes need sqft + beds', 'Comps: sold ≤400d · tiered ≤1mi', 'ARV: top-of-market 25% band', 'MAO ×80% − rehab', 'Year built ≤ 2010'];
  return (
    <div style={{ flex: 1, overflowY: 'auto', background: 'var(--navy)' }}>
      <div style={{ maxWidth: 940, margin: '0 auto', padding: '20px 24px 44px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--white)', margin: 0, letterSpacing: '-0.02em' }}>Presets</h1>
            <p style={{ fontSize: 12.5, color: 'var(--gray-500)', marginTop: 3, maxWidth: 620, lineHeight: 1.5 }}>
              The settings that drive every scan, comp pull, and score. These are the active defaults for your market — managed centrally so every org scans consistently. Your default view is saved on this device.
            </p>
          </div>
          <button onClick={onClose} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy-card)', color: 'var(--gray-300)', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>≣ Back to results</button>
        </div>

        {/* Summary chips */}
        <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: '14px 16px', marginBottom: 15 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)' }}>Active presets</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
            {chips.map(c => <span key={c} style={{ fontSize: 12, color: 'var(--teal)', background: 'var(--teal-dim)', border: '1px solid var(--navy-border)', borderRadius: 999, padding: '5px 12px' }}>{c}</span>)}
          </div>
        </div>

        {/* Your preferences (interactive) */}
        <PresetSection icon="🖥" title="Your preferences" desc="Saved on this device — just for you">
          <PresetRow title="Default view" desc="Which view opens first next time you open MLS Opportunities.">
            <div style={{ display: 'flex', border: '1px solid var(--navy-border)', borderRadius: 8, overflow: 'hidden' }}>
              {['list', 'detail', 'map'].map(v => (
                <button key={v} onClick={() => onDefaultViewChange(v)} style={{
                  padding: '7px 14px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, textTransform: 'capitalize',
                  background: defaultView === v ? 'var(--teal)' : 'var(--navy-card)', color: defaultView === v ? '#fff' : 'var(--gray-500)',
                }}>{v}</button>
              ))}
            </div>
          </PresetRow>
        </PresetSection>

        {/* Deal scraping filters */}
        <PresetSection icon="🎯" title="Deal scraping filters" desc="What the tool stores & scores from each sync">
          <PresetRow title="Price band" desc="Listings outside this asking-price range are skipped at scrape time (per market).">
            <ValPill>Grid/TX $50k – $1.5M</ValPill>
            <ValPill>Spark/AZ $25k – $3M</ValPill>
          </PresetRow>
          <PresetRow title="Homes require sqft + beds" desc="Protects the ARV math — houses/condos need sqft ≥ 400 and beds ≥ 1.">
            <ValPill>On</ValPill>
          </PresetRow>
          <PresetRow title="Property types" desc="Dwelling types pulled and scored.">
            <ValPill>SFR · Condo · Townhouse · Multi</ValPill>
          </PresetRow>
        </PresetSection>

        {/* Comp engine */}
        <PresetSection icon="📐" title="Comp engine" desc="How ARV comps are chosen & pre-selected (comp_config)">
          <PresetRow title="Comp window" desc="Widest closed-sale window pulled as candidates.">
            <ValPill>sold ≤ 400 days</ValPill>
          </PresetRow>
          <PresetRow title="Tier 1 (closest / best)" desc="Nearest, most-recent comps — pre-checked first.">
            <ValPill>≤ 90 days · ≤ 1.0 mi</ValPill>
          </PresetRow>
          <PresetRow title="Sqft / lot / year ceilings" desc="A comp must be within these of the subject.">
            <ValPill>sqft ×1.20 · lot ×1.80 · year +15</ValPill>
          </PresetRow>
          <PresetRow title="ARV method" desc="Top-of-market: average $/sqft of the top band, × subject sqft.">
            <ValPill>top 25% band · 2–6 comps</ValPill>
          </PresetRow>
          <PresetRow title="MAO" desc="Max allowable offer. A per-zip override set on a deal wins over this default.">
            <ValPill tone="gold">ARV × 80% − rehab</ValPill>
          </PresetRow>
        </PresetSection>

        {/* Opportunity scoring */}
        <PresetSection icon="⚡" title="Opportunity scoring" desc="What qualifies as a deal">
          <PresetRow title="Year-built cutoff" desc="Newer builds are disqualified as flips.">
            <ValPill>≤ 2010</ValPill>
          </PresetRow>
          <PresetRow title="Distress / qualify keywords" desc="As-is, fixer, estate sale, TLC, cash-only… drive the Layer-1 pass.">
            <ValPill>managed centrally</ValPill>
          </PresetRow>
          <PresetRow title="AI photo review (Layer 2)" desc="A vision check on borderline listings only, to control cost.">
            <ValPill>borderline only</ValPill>
          </PresetRow>
        </PresetSection>

        <div style={{ fontSize: 11.5, color: 'var(--gray-500)', lineHeight: 1.5, padding: '0 2px' }}>
          Scraping, comp-engine and scoring presets are managed centrally to keep every org's results consistent — inline editing is a fast-follow. The one setting you own per-deal is <strong style={{ color: 'var(--gray-300)' }}>MAO %</strong>, editable on each deal's underwriting.
        </div>
      </div>
    </div>
  );
}

// ── ToolbarOverflow ─────────────────────────────────────────────────────────

// "⋯ More" overflow menu for secondary toolbar actions. The menu renders in a document.body portal so
// it isn't clipped by the top bar's overflow:hidden; it closes on outside-click / scroll / resize and
// after any action inside it. Children are the action buttons, stacked vertically.
function ToolbarOverflow({ children, active = false }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const place = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const W = 210, margin = 8;
    let left = r.right - W;
    if (left < margin) left = margin;
    setCoords({ left, top: r.bottom + 6 });
  }, []);
  useEffect(() => {
    if (!open) { setCoords(null); return; }
    place();
    const onDoc = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const rep = () => place();
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', rep, true);
    window.addEventListener('resize', rep);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', rep, true);
      window.removeEventListener('resize', rep);
    };
  }, [open, place]);
  return (
    <div ref={btnRef} style={{ display: 'inline-flex' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title="More actions"
        style={{
          padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
          border: open ? '1px solid var(--teal)' : '1px solid var(--navy-border)',
          background: open ? 'var(--teal-dim)' : 'var(--navy-card)',
          color: open ? 'var(--teal)' : 'var(--gray-300)',
          display: 'flex', alignItems: 'center', gap: 6,
        }}
      >
        <span style={{ fontSize: 16, lineHeight: 1, letterSpacing: 1 }}>⋯</span> More
        {active && <span title="A filter in this menu is active" style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--teal)' }} />}
      </button>
      {open && coords && createPortal(
        <div ref={menuRef} onClick={() => setOpen(false)} style={{
          position: 'fixed', left: coords.left, top: coords.top, zIndex: 3000,
          minWidth: 210, background: 'var(--navy-card)', border: '1px solid var(--navy-border)',
          borderRadius: 10, padding: 8, boxShadow: '0 8px 24px rgba(26,23,20,0.18)',
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          {children}
        </div>,
        document.body
      )}
    </div>
  );
}

// ── FilterRail ──────────────────────────────────────────────────────────────
// Persistent, collapsible left parameter rail (Flexmls / ARMLS style). Every search
// parameter lives here, stacked vertically; the rail collapses to a slim spine so the
// map/list can take the full width. Provider-agnostic — the same rail renders for every
// org; only the underlying data (e.g. pool, distress) differs by market/provider.
const RAIL_W = 288, RAIL_COLLAPSED_W = 44;

function FilterRail({ filters, setFilters, typeOptions, typeFilter, setTypeFilter, statusFilter, setStatusFilter, statusCounts = {}, activeCount, onApply, appliedAll, collapsed, onToggleCollapse, savedSearchCriteria, onSearchSaved }) {
  const update = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const reset  = () => {
    setFilters({
      priceMin: '', priceMax: '', bedsMin: '', bathsMin: '',
      sqftMin: '', sqftMax: '', lotMin: '', lotMax: '',
      yearMin: '', yearMax: '', domMin: '', domMax: '', garageMin: '', garageMax: '', scoreMin: '',
      pool: '', distressMin: '', cashOnly: false, closeWithin: '',
      zips: [], propertyTypes: [],
    });
    setTypeFilter && setTypeFilter('all');
  };
  // Opportunity-type chips (single-select; ANDs with everything else).
  const TYPE_CHIPS = [
    { k: 'all', label: 'All' },
    { k: 'lots', label: '🏗️ Lots & Scrapes' },
    { k: 'new', label: '✨ New' },
    { k: 'well_priced', label: '✓ Well-Priced' },
    { k: 'hot', label: '🔥 Hot Market' },
  ];
  const toggleArr = (k, val) => setFilters(f => ({
    ...f, [k]: f[k].includes(val) ? f[k].filter(x => x !== val) : [...f[k], val],
  }));

  const QUICK_BEDS  = [1, 2, 3, 4, 5];
  const QUICK_BATHS = [1, 2, 3, 4];
  const QUICK_SCORE = [60, 70, 80, 90];
  const QUICK_DISTRESS = [25, 50, 75];
  const POOL_OPTS = [{ v: '', label: 'Any' }, { v: 'yes', label: 'Has Pool' }, { v: 'no', label: 'No Pool' }];

  // Collapsed spine — a slim clickable bar with a rotated label + active-count badge.
  if (collapsed) {
    return (
      <aside style={{
        width: RAIL_COLLAPSED_W, flexShrink: 0, height: '100%',
        background: 'var(--navy)', borderRight: '1px solid var(--navy-border)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 12, gap: 12,
        cursor: 'pointer',
      }}
        onClick={onToggleCollapse}
        title="Show filters"
      >
        {/* Visual chevron only — the whole spine is the click target (aside onClick). Giving the
            button its own handler double-fired the toggle, so expanding via the arrow was a no-op. */}
        <div aria-hidden style={{
          background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 8,
          width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--teal)',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
        </div>
        {activeCount > 0 && (
          <span style={{ background: '#14504A', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>{activeCount}</span>
        )}
        <div style={{
          writingMode: 'vertical-rl', transform: 'rotate(180deg)', marginTop: 4,
          fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
          color: 'var(--gray-500)', userSelect: 'none',
        }}>
          Filters
        </div>
      </aside>
    );
  }

  return (
    <aside style={{
      width: RAIL_W, flexShrink: 0, height: '100%',
      background: 'var(--navy)', borderRight: '1px solid var(--navy-border)',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Rail header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '12px 14px', borderBottom: '1px solid var(--navy-border)', flexShrink: 0,
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--white)', letterSpacing: '0.02em' }}>
          Filters {activeCount > 0 && <span style={{ color: 'var(--teal)' }}>· {activeCount}</span>}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {activeCount > 0 && <button onClick={reset} style={{ background: 'none', border: 'none', color: '#B85450', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>Clear all</button>}
          <button onClick={onToggleCollapse} title="Hide filters" style={{
            background: 'none', border: '1px solid var(--navy-border)', borderRadius: 6,
            width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--gray-300)', cursor: 'pointer',
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
        </div>
      </div>

      {/* Save this search — turns the filters currently set into a standing hunt that gets alerted on.
          Sits with Apply because they are the two things you do with a filter set you like: run it
          now, or keep it. Renders nothing when no filter is set (the server refuses such a search). */}
      <div style={{ padding: '0 14px', flexShrink: 0 }}>
        <SaveSearchButton criteria={savedSearchCriteria} activeCount={activeCount} onSaved={onSearchSaved} />
      </div>

      {/* Apply filters — the explicit action that runs the current filters against the WHOLE market
          ("Show All Results"). Filters below still narrow the current set live; this is the intentional
          full-market search (replaces the old surprising auto-switch on every keystroke). */}
      {onApply && (
        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--navy-border)', flexShrink: 0 }}>
          <button onClick={onApply} title="Search the whole market with the filters below"
            style={{
              width: '100%', padding: '10px', borderRadius: 8, border: 'none', cursor: 'pointer',
              fontSize: 12.5, fontWeight: 700,
              background: appliedAll ? 'var(--teal-dim)' : 'var(--teal)',
              color: appliedAll ? 'var(--teal)' : '#fff',
              boxShadow: appliedAll ? 'none' : '0 1px 3px rgba(20,80,74,0.25)',
            }}>
            {appliedAll ? '✓ Applied to all results' : 'Apply filters — search all results'}
          </button>
        </div>
      )}

      {/* Scrollable parameter list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 14px 28px' }}>
        {/* Status + Opportunity Type side-by-side — compact, so the MLS-native filters below are
            visible without scrolling (more intuitive / discoverable). */}
        {(setStatusFilter || setTypeFilter) && (
          <div style={{ paddingBottom: 14, marginBottom: 14, borderBottom: '1px solid var(--navy-border)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {setStatusFilter && (
              <div>
                <div style={railMiniLabel}>Status</div>
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={railCompactSelect(statusFilter !== 'Active')}>
                  <option value="All">All statuses</option>
                  {STATUS_PILLS.map(p => (
                    <option key={p.key} value={p.key}>{p.label}{statusCounts[p.key] != null ? ` (${statusCounts[p.key]})` : ''}</option>
                  ))}
                </select>
              </div>
            )}
            {setTypeFilter && (
              <div>
                <div style={railMiniLabel}>Type</div>
                <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={railCompactSelect(typeFilter !== 'all')}>
                  {TYPE_CHIPS.map(c => <option key={c.k} value={c.k}>{c.label}</option>)}
                </select>
              </div>
            )}
          </div>
        )}

        {/* Close Date — only meaningful when browsing Closed sales (for comps). */}
        {setStatusFilter && statusFilter === 'Closed' && (
          <RailSection title="Closed within">
            <select value={filters.closeWithin} onChange={e => update('closeWithin', e.target.value)} style={railCompactSelect(!!filters.closeWithin)}>
              <option value="">Any time (past year)</option>
              {[30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330, 365].map(d => (
                <option key={d} value={d}>Past {d} days</option>
              ))}
            </select>
          </RailSection>
        )}

        <RailSection title="Price">
          <RangeField label="" unit="$" min={filters.priceMin} max={filters.priceMax} onMin={v => update('priceMin', v)} onMax={v => update('priceMax', v)} step={10000} />
        </RailSection>

        <RailSection title="Beds & Baths">
          <QuickPick label="Beds (min)"  value={filters.bedsMin}  options={QUICK_BEDS}  onChange={v => update('bedsMin', v)} />
          <div style={{ height: 10 }} />
          <QuickPick label="Baths (min)" value={filters.bathsMin} options={QUICK_BATHS} onChange={v => update('bathsMin', v)} />
        </RailSection>

        <RailSection title="Square Feet">
          <RangeField label="" min={filters.sqftMin} max={filters.sqftMax} onMin={v => update('sqftMin', v)} onMax={v => update('sqftMax', v)} step={100} />
        </RailSection>

        <RailSection title="Lot (sqft)">
          <RangeField label="" min={filters.lotMin} max={filters.lotMax} onMin={v => update('lotMin', v)} onMax={v => update('lotMax', v)} step={500} />
        </RailSection>

        <RailSection title="Dwelling Type">
          <ChipMultiSelect label="" value={filters.propertyTypes} options={typeOptions} onToggle={v => toggleArr('propertyTypes', v)} />
          {typeOptions.length === 0 && <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>No types in the current set.</div>}
        </RailSection>

        <RailSection title="Year Built">
          <RangeField label="" min={filters.yearMin} max={filters.yearMax} onMin={v => update('yearMin', v)} onMax={v => update('yearMax', v)} step={1} />
        </RailSection>

        <RailSection title="Pool">
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {POOL_OPTS.map(o => (
              <button key={o.v || 'any'} onClick={() => update('pool', o.v)} style={pickBtn(filters.pool === o.v)}>{o.label}</button>
            ))}
          </div>
        </RailSection>

        <RailSection title="Distress (min)">
          <QuickPick label="" value={filters.distressMin} options={QUICK_DISTRESS} onChange={v => update('distressMin', v)} />
          <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 4 }}>AI condition score from listing photos (0–100).</div>
        </RailSection>

        <RailSection title="Financing">
          <button
            onClick={() => update('cashOnly', !filters.cashOnly)}
            style={{
              width: '100%', padding: '8px 10px', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
              display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 700,
              border: filters.cashOnly ? '1px solid var(--teal)' : '1px solid var(--navy-border)',
              background: filters.cashOnly ? 'var(--teal-dim)' : 'var(--navy-card)',
              color: filters.cashOnly ? 'var(--teal)' : 'var(--gray-300)',
            }}
          >
            <span style={{
              width: 16, height: 16, borderRadius: 4, flexShrink: 0,
              border: `1px solid ${filters.cashOnly ? 'var(--teal)' : 'var(--navy-border)'}`,
              background: filters.cashOnly ? 'var(--teal)' : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
            }}>{filters.cashOnly && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}</span>
            💵 Cash sales only
          </button>
          <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 4 }}>
            Listings flagged for cash terms. Structured closed-sale financing lands with the next sync.
          </div>
        </RailSection>

        <RailSection title="Days on Market">
          <RangeField label="" min={filters.domMin} max={filters.domMax} onMin={v => update('domMin', v)} onMax={v => update('domMax', v)} step={1} />
        </RailSection>

        <RailSection title="Garage (spaces)">
          <RangeField label="" min={filters.garageMin} max={filters.garageMax} onMin={v => update('garageMin', v)} onMax={v => update('garageMax', v)} step={1} />
        </RailSection>

        <RailSection title="Deal Score (min)">
          <QuickPick label="" value={filters.scoreMin} options={QUICK_SCORE} onChange={v => update('scoreMin', v)} />
        </RailSection>

        <RailSection last>
          <ZipTextFilter value={filters.zips} onChange={zips => update('zips', zips)} />
        </RailSection>
      </div>
    </aside>
  );
}

// One labeled block in the FilterRail. A thin divider separates sections (suppressed on the last).
const railMiniLabel = { fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 };
function railCompactSelect(active) {
  return {
    width: '100%', padding: '7px 8px', borderRadius: 8, cursor: 'pointer', outline: 'none',
    border: `1px solid ${active ? 'var(--teal)' : 'var(--navy-border)'}`,
    background: active ? 'var(--teal-dim)' : 'var(--navy-card)',
    color: active ? 'var(--teal)' : 'var(--gray-300)',
    fontSize: 12, fontWeight: 700,
  };
}
function RailSection({ title, children, last = false }) {
  return (
    <div style={{
      paddingBottom: last ? 0 : 14, marginBottom: last ? 0 : 14,
      borderBottom: last ? 'none' : '1px solid var(--navy-border)',
    }}>
      {title && <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>{title}</div>}
      {children}
    </div>
  );
}

// Free-text zip filter. Accepts comma- or space-separated 5-digit zips. We parse on
// every change so the filter applies live, but only keep valid 5-digit numerics —
// junk characters are silently dropped. Empty input clears the filter.
function ZipTextFilter({ value, onChange }) {
  // Display state mirrors the parsed array but lets the user type freely (including
  // partial / invalid input) without it getting wiped mid-typing.
  const [text, setText] = useState((value || []).join(', '));

  // If the parent resets filters (e.g. "Clear all"), reset our text too.
  useEffect(() => {
    const joined = (value || []).join(', ');
    if (joined !== text.replace(/\s+/g, ' ').trim().replace(/,\s*/g, ', ')) {
      setText(joined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value && value.length]);

  function commit(raw) {
    setText(raw);
    // Pull out every 5-digit run, dedupe, cap at 50 to prevent abuse.
    const zips = Array.from(new Set((raw.match(/\b\d{5}\b/g) || []))).slice(0, 50);
    onChange(zips);
  }

  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
        Zip Codes {value && value.length > 0 && <span style={{ color: 'var(--teal)' }}>· {value.length} selected</span>}
      </div>
      <input
        value={text}
        onChange={e => commit(e.target.value)}
        placeholder="78745, 78702, 78704…"
        style={{
          width: '100%', padding: '7px 10px', borderRadius: 8,
          border: '1px solid var(--navy-border)', background: 'var(--navy)',
          color: 'var(--white)', fontSize: 12, outline: 'none', boxSizing: 'border-box',
        }}
      />
      <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 4 }}>
        Type any 5-digit zip(s) — comma- or space-separated. Anything else is ignored.
      </div>
    </div>
  );
}

function RangeField({ label, unit = '', min, max, onMin, onMax, step }) {
  const inputStyle = {
    width: '100%', padding: '5px 8px', borderRadius: 6,
    border: '1px solid var(--navy-border)', background: 'var(--navy)',
    color: 'var(--white)', fontSize: 12, outline: 'none', boxSizing: 'border-box',
  };
  return (
    <div>
      {label !== '' && <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>}
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <input type="number" placeholder={`${unit}min`} value={min} step={step} onChange={e => onMin(e.target.value)} style={inputStyle} />
        <span style={{ color: 'var(--gray-500)', fontSize: 11 }}>–</span>
        <input type="number" placeholder={`${unit}max`} value={max} step={step} onChange={e => onMax(e.target.value)} style={inputStyle} />
      </div>
    </div>
  );
}

function QuickPick({ label, value, options, onChange }) {
  return (
    <div>
      {label !== '' && <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>}
      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <button onClick={() => onChange('')} style={pickBtn(value === '' || value == null)}>Any</button>
        {options.map(o => (
          <button key={o} onClick={() => onChange(String(o))} style={pickBtn(String(value) === String(o))}>{o}+</button>
        ))}
      </div>
    </div>
  );
}

function pickBtn(active) {
  return {
    padding: '4px 9px', borderRadius: 6,
    border: '1px solid ' + (active ? 'var(--teal)' : 'var(--navy-border)'),
    background: active ? 'rgba(20, 80, 74,0.18)' : 'var(--navy)',
    color: active ? 'var(--teal)' : 'var(--gray-300)',
    fontSize: 11, fontWeight: 600, cursor: 'pointer',
  };
}

function ChipMultiSelect({ label, value, options, onToggle }) {
  if (options.length === 0) return null;
  return (
    <div>
      {(label !== '' || value.length > 0) && (
        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
          {label} {value.length > 0 && <span style={{ color: 'var(--teal)' }}>· {value.length} selected</span>}
        </div>
      )}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxHeight: 84, overflowY: 'auto', padding: 4, background: 'var(--navy)', borderRadius: 6, border: '1px solid var(--navy-border)' }}>
        {options.map(o => {
          const active = value.includes(o);
          return (
            <button key={o} onClick={() => onToggle(o)} style={{
              padding: '3px 9px', borderRadius: 12,
              border: '1px solid ' + (active ? 'var(--teal)' : 'var(--navy-border)'),
              background: active ? 'rgba(20, 80, 74,0.2)' : 'transparent',
              color: active ? 'var(--teal)' : 'var(--gray-300)',
              fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
            }}>{o}</button>
          );
        })}
      </div>
    </div>
  );
}

// ── DetailPanel ───────────────────────────────────────────────────────────────

function DetailPanel({ listing: l, detailTab, setDetailTab, openArvTab, comps, marketComps, areaSales = [], compsArv, compsLoading, compsMeta = {}, recalculating, refinedArv, rehabItems, setRehabItems, matchedBuyers, buyersLoading, onBuyerClick, onSendEmail, onMetricEdit, onOpenContract, onPromote, navigate, isDemo, onDismiss, onRestore }) {
  const b  = BADGE[l.badge] || BADGE['Pass'];
  const sc = STATUS_COLOR[l.is_back_on_market ? 'Back on Market' : l.status] || '#6B6760';

  // Comp checklist: which comps are currently included in the ARV. Replaces the old thumbs
  // up/down flow — check/uncheck recomputes ARV/MAO live (avg $/sqft of the checked comps ×
  // subject sqft). Re-seed to "all on" whenever a new comp set loads.
  const [checkedComps, setCheckedComps] = useState(() => new Set());
  useEffect(() => { setCheckedComps(new Set((comps || []).map(c => c.id))); }, [comps]);
  const toggleComp = (id) => setCheckedComps(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  // Optional drawn boundary on the comp map — when set, only comps INSIDE it count toward the ARV
  // (in addition to the checklist). Reset whenever a fresh comp set loads.
  const [compBound, setCompBound] = useState(null);
  useEffect(() => { setCompBound(null); }, [comps]);
  const inBound = (c) => !compBound || insideBound({ lat: c.lat, lng: c.lng }, compBound);

  // Prefer a live ARV from the checked comps; fall back to a refined/cached ARV, then the live
  // comp-engine ARV from the /comps fetch so a value shows before the financials drain caches it.
  // Active/pending comps are selectable too (with a warning) — they contribute to the ARV once checked.
  // Area sales are selectable exactly like engine comps — ticking one folds it into the live ARV.
  const selectableComps = [...(comps || []), ...(marketComps || []), ...(areaSales || [])];
  const arvComps = selectableComps.filter(c => checkedComps.has(c.id) && inBound(c) && Number(c.price_per_sqft) > 0);
  const baselineArv = refinedArv || l.ai_arv || compsArv;
  const arv = (arvComps.length && l.sqft)
    ? Math.round((arvComps.reduce((s, c) => s + Number(c.price_per_sqft), 0) / arvComps.length) * l.sqft)
    : baselineArv;
  const mao = Math.round((arv || 0) * 0.70 - (l.repair_cost || 0));
  const compsLive = arvComps.length > 0;   // ARV is currently driven by the checklist
  // Comps pulled? Drives the Comps-section state and gates the ARV/Rehab/MAO boxes (hidden until run).
  const compsReady = (comps || []).length > 0 || arvComps.length > 0;

  // Facts for the Property Information / Tax Information grids — render a cell only when we have a value.
  const fact = (label, value) => (value == null || value === '') ? null : (
    <div key={label}>
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--gray-500)' }}>{label}</div>
      <div style={{ fontSize: 12.5, color: 'var(--white)', fontWeight: 600 }}>{value}</div>
    </div>
  );
  const closeDateStr = (() => { if (!l.close_date) return null; const d = new Date(l.close_date); return isNaN(d.getTime()) ? null : d.toLocaleDateString('en-US'); })();
  const tax = taxRecordsLink(l);
  const hasTaxInfo = l.owner_name || l.tax_annual_amount != null || l.tax_assessed_value != null || l.parcel_number || l.subdivision || l.legal_description || l.county || tax;

  return (
    <div style={{ padding: '24px', minHeight: '100%' }}>

      {/* Top: photo (left) + header & Property/Tax cards (right). The photo box stretches to the height of
          the info column so its bottom lines up with the cards. Full address lives here only. */}
      <div style={{ display: 'flex', gap: 20, marginBottom: 16, flexWrap: 'wrap', alignItems: 'stretch' }}>
        <PhotoCarousel photos={l.photo_urls || l.photos || []} fill />
        <div style={{ flex: 1, minWidth: 300, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              {l.deal_id ? (
                <span title="Already in your Deals" style={{ fontSize: 12, fontWeight: 700, padding: '7px 12px', borderRadius: 8, background: 'rgba(74,124,89,0.12)', border: '1px solid rgba(74,124,89,0.4)', color: '#4A7C59' }}>
                  ✓ In Deals · {l.deal_stage || 'Working'}
                </span>
              ) : (
                <button onClick={() => onPromote && onPromote(l, 'Working')}
                  title="Add this opportunity to your Deals → Working tab"
                  style={{ fontSize: 12, fontWeight: 700, padding: '7px 12px', borderRadius: 8, background: 'var(--teal)', border: '1px solid var(--teal)', color: '#fff', cursor: 'pointer' }}>
                  + Add to Working
                </button>
              )}
              <AddToListMenu items={[{ item_type: 'mls', item_id: String(l.mls_id) }]} label="Add to list" />
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 10, background: `${sc}15`, color: sc }}>
                {l.is_back_on_market ? 'Back on Market' : l.status}
              </span>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 10, background: b.bg, color: b.color, border: `1px solid ${b.border}` }}>{l.badge}</span>
              {l.under_contract
                ? <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 10, background: 'rgba(107, 103, 96,0.18)', color: '#6B6760' }}>UNDER CONTRACT</span>
                : l.contracted && <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 10, background: 'rgba(20, 80, 74,0.15)', color: '#14504A' }}>CONTRACT SENT</span>}
              {l.contacted && <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 10, background: 'rgba(224, 165, 38,0.15)', color: 'var(--gold)' }}>CONTACTED</span>}
              {l.previously_offered && <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 10, background: 'rgba(20, 80, 74,0.18)', color: '#14504A', border: '1px solid rgba(20, 80, 74,0.35)' }}>PREVIOUSLY OFFERED</span>}
              {(l.mls_number || l.mls_id) && <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>MLS# {l.mls_number || l.mls_id}</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 3, flexWrap: 'wrap' }}>
              <h2 style={{ fontSize: 19, fontWeight: 700, color: 'var(--white)', margin: 0 }}>{streetLine(l)}</h2>
              {/* Demo addresses aren't real MLS listings — Zillow would 404, so hide it. */}
              {!isDemo && <ExternalListingLinks listing={l} />}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--gray-500)' }}>{addressSub(l)}</div>
          </div>

          {/* Property Information + Tax Information side by side, filling the rest of the column height. */}
          <div style={{ flex: 1, display: 'grid', gridTemplateColumns: hasTaxInfo ? 'repeat(auto-fit, minmax(230px, 1fr))' : '1fr', gap: 12, alignContent: 'stretch' }}>
            <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Property Information</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: '12px 14px' }}>
                {fact('Beds', l.beds ?? null)}
                {fact('Baths', fmtBaths(l.baths))}
                {fact('Sqft', l.sqft ? Number(l.sqft).toLocaleString() : null)}
                {fact('Lot size', fmtLotSize(l))}
                {fact('Parking', fmtParking(l))}
                {fact('Pool', l.pool != null ? yesNoUnknown(l.pool) : null)}
                {fact('HOA', l.hoa != null ? fmtHoa(l) : null)}
                {fact('Year built', l.year_built ?? null)}
                {fact('Days on market', l.days_on_market != null ? String(l.days_on_market) : null)}
                {fact('Close date', closeDateStr)}
              </div>
            </div>
            {hasTaxInfo && (
              <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Tax Information</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '12px 14px' }}>
                  {fact('Owner name', l.owner_name)}
                  {fact('Annual tax', l.tax_annual_amount != null ? `$${Number(l.tax_annual_amount).toLocaleString()}${l.tax_year ? ` (${l.tax_year})` : ''}` : null)}
                  {fact('Tax assessed value', l.tax_assessed_value != null ? `$${Number(l.tax_assessed_value).toLocaleString()}` : null)}
                  {fact('APN #', l.parcel_number)}
                  {fact('Subdivision', l.subdivision)}
                  {fact('County', l.county)}
                </div>
                {l.legal_description && <div style={{ marginTop: 12 }}>{fact('Legal description', l.legal_description)}</div>}
                {tax && <div style={{ marginTop: 12 }}><a href={tax.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, fontWeight: 700, color: 'var(--teal)', textDecoration: 'none' }}>{tax.label}</a></div>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Public remarks — full width below the photo/info row. */}
      {l.description && (
        <PanelShell title="Public remarks">
          <ExpandableDescription text={l.description} />
        </PanelShell>
      )}

      {l.previously_offered && <PreviousOffersCard offers={l.previous_offers || []} navigate={navigate} />}

      {/* Listing Agent Contact */}
      {(l.agent_name || l.agent_email || l.agent_phone) && (
        <div style={{ marginBottom: 20, background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 10, padding: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            Listing Agent Information
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--teal-dim)', color: 'var(--teal)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
              {(l.agent_name || '?').charAt(0).toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)' }}>{l.agent_name || 'Unknown agent'}</div>
              <div style={{ display: 'flex', gap: 12, fontSize: 11, marginTop: 4, flexWrap: 'wrap' }}>
                {l.agent_phone && (
                  <a href={`tel:${l.agent_phone.replace(/\D/g, '')}`} style={{ color: 'var(--teal)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                    {l.agent_phone}
                  </a>
                )}
                {l.agent_email && (
                  <a href={`mailto:${l.agent_email}`} style={{ color: 'var(--teal)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    {l.agent_email}
                  </a>
                )}
                {!l.agent_phone && !l.agent_email && (
                  <span style={{ color: 'var(--gray-500)', fontStyle: 'italic' }}>No contact info available</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Comps — the underwriting checklist once pulled, or a "Run comps" prompt in its place (C11). */}
      {(compsReady || compsLoading || compsMeta.fetched)
        ? <ArvPanel comps={comps} marketComps={marketComps} areaSales={areaSales} compsLoading={compsLoading} checkedComps={checkedComps} onToggleComp={toggleComp} arv={arv} mao={mao} listing={l} recalculating={recalculating} compBound={compBound} onBoundChange={setCompBound} inBound={inBound} compsMeta={compsMeta} onModeChange={openArvTab} />
        : (
          <PanelShell title="Comps">
            <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
              <div style={{ fontSize: 12.5, color: 'var(--gray-500)', marginBottom: 12, lineHeight: 1.5 }}>
                Pull area comps from live sold data to estimate <strong style={{ color: 'var(--teal)' }}>ARV / MAO</strong> for this property.
              </div>
              {/* Arrow, not `onClick={openArvTab}`: the click event would arrive as forceMode and be sent
                  as mode=[object Object], forcing a refetch every press. */}
              <button onClick={() => openArvTab()} style={{ background: 'var(--gold)', color: '#3a2c07', padding: '10px 22px', borderRadius: 10, fontWeight: 700, border: 'none', cursor: 'pointer', fontSize: 13 }}>
                Run comps
              </button>
            </div>
          </PanelShell>
        )}

      {/* ARV / Rehab / MAO — appear ONLY after comps are run, and BELOW the comps section (B4). ARV is
          driven live by the comp checklist above; Rehab / MAO expand for the line-item + 70%-rule detail. */}
      {compsReady && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            {[
              { key: 'arv',   label: 'ARV',   value: arv ? fmtK(arv) : '—',  accent: 'var(--teal)', hint: 'Est. after-repair value', clickable: false },
              { key: 'rehab', label: 'Rehab', value: fmtK(l.repair_cost),    accent: 'var(--gold)', hint: 'Est. repair budget',      clickable: true },
              { key: 'mao',   label: 'MAO',   value: fmtK(mao),              accent: '#6B6760',     hint: 'Max allowable offer',     clickable: true },
            ].map(card => {
              const active = detailTab === card.key;
              const inner = (
                <>
                  <div style={{ fontSize: 10, color: 'var(--gray-500)', marginBottom: 4 }}>{card.label}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: card.accent }}>{card.value}</div>
                  <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2 }}>{card.hint}</div>
                  {card.key === 'arv' && compsLive && <div style={{ fontSize: 9, color: '#4A7C59', marginTop: 2, fontWeight: 700 }}>✓ {arvComps.length} comp{arvComps.length === 1 ? '' : 's'} checked</div>}
                </>
              );
              const baseStyle = {
                padding: '12px', borderRadius: 10, textAlign: 'left', transition: 'all 0.15s',
                border: `1px solid ${active ? card.accent : 'var(--navy-border)'}`,
                background: active ? `${card.accent}12` : 'var(--navy-card)',
              };
              return card.clickable
                ? <button key={card.key} onClick={() => setDetailTab(active ? null : card.key)} style={{ ...baseStyle, cursor: 'pointer' }}>{inner}</button>
                : <div key={card.key} style={baseStyle}>{inner}</div>;
            })}
          </div>
          {detailTab === 'rehab' && <div style={{ marginTop: 14 }}><RehabPanel items={rehabItems} setItems={setRehabItems} onMetricEdit={onMetricEdit} /></div>}
          {detailTab === 'mao' && <div style={{ marginTop: 14 }}><MaoPanel listing={l} arv={arv} /></div>}
        </div>
      )}

      {/* Matched Buyers */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
          Top Matched Buyers
        </div>
        {buyersLoading ? (
          <div style={{ fontSize: 12, color: 'var(--gray-500)', padding: '8px 0' }}>Matching buyers…</div>
        ) : matchedBuyers.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--gray-500)', padding: '8px 0' }}>No strong matches found.</div>
        ) : matchedBuyers.slice(0, 3).map((buyer, i) => (
          <BuyerRow key={buyer.id} buyer={buyer} rank={i + 1} onClick={() => onBuyerClick(buyer)} />
        ))}
      </div>

      {/* Pipeline — move this opportunity into the Deal Pipeline at a chosen stage. If it isn't a
          deal yet, picking a stage CREATES the deal record there; if it is, it's a manual move. */}
      <div style={{ marginBottom: 12, padding: '12px 14px', borderRadius: 10, background: l.deal_id ? 'rgba(107, 103, 96,0.10)' : 'rgba(20, 80, 74,0.08)', border: `1px solid ${l.deal_id ? 'rgba(107, 103, 96,0.35)' : 'rgba(20, 80, 74,0.3)'}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: l.deal_id ? '#6B6760' : '#14504A' }}>
              {l.deal_id ? `In pipeline · ${l.deal_stage || 'Working'}` : 'Move into the Deal Pipeline'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
              {l.deal_id ? 'Change its stage below.' : 'Creates a deal record at the stage you choose.'}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select
              value=""
              onChange={e => { if (e.target.value) onPromote && onPromote(l, e.target.value); e.target.value = ''; }}
              style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy-card)', color: 'var(--gray-300)', fontSize: 12, cursor: 'pointer', outline: 'none' }}
            >
              <option value="">{l.deal_id ? 'Move to stage…' : 'Add to pipeline as…'}</option>
              {['Working', 'Offer Sent', 'Under Contract', 'Offer Accepted', 'Closed', 'Dropped', 'Dead'].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            {l.deal_id && <button onClick={() => navigate(`/underwriting/${l.deal_id}`)} style={{ background: 'none', border: 'none', color: '#6B6760', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>View deal →</button>}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={onSendEmail} style={actionBtn('#14504A')}>
          <EmailIcon /> Email agent
        </button>
        <button onClick={onOpenContract} style={actionBtn('#E0A526')}>
          <DocIcon /> Generate Contract
        </button>
        <button
          onClick={() => {
            fetch('/api/listings/save', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(l) })
              .then(r => r.json()).then(data => { if (data.id) navigate(`/underwriting/${data.id}`); });
          }}
          style={actionBtn('var(--teal)')}
        >
          <AIIcon /> Full Underwriting
        </button>
        {/* Dismiss / Restore — "not for me". Persistent per-org; survives re-sync. */}
        {l.dismissed_at
          ? <button onClick={() => onRestore && onRestore(l)} style={actionBtn('#4A7C59')}>↩ Restore</button>
          : <button onClick={() => onDismiss && onDismiss(l)} style={actionBtn('#8A7969')}>✕ Dismiss</button>}
      </div>
    </div>
  );
}

function actionBtn(color) {
  return {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', borderRadius: 8, border: `1px solid ${color}40`,
    background: `${color}12`, color, fontSize: 12, fontWeight: 600, cursor: 'pointer',
  };
}

// ── ARV Panel ────────────────────────────────────────────────────────────────

// Comps CHECKLIST (replaces the old thumbs up/down). Each comp has a checkbox; checking/unchecking
// includes/excludes it from the ARV, and the parent recomputes ARV/MAO live (avg $/sqft of the
// checked comps × subject sqft). Click a comp row (not its checkbox) for its photos + details.
// Status → color/label for comp pins, table pills, and the visibility toggle.
const COMP_STAT = {
  closed:  { label: 'Sold',    color: '#DC2626' },  // red
  active:  { label: 'Active',  color: '#16A34A' },  // green
  pending: { label: 'Pending', color: '#E0A526' },  // yellow/gold
  // Nearby closed sales the comp engine did NOT qualify — shown on request for manual selection.
  area:    { label: 'Area sale', color: '#8B5CF6' },  // violet
};

// Which valuation basis the engine used, why, and a one-click switch to the other one.
// The engine auto-routes a listing to TEARDOWN comps (new-build sales on a like-size lot) whenever the
// description carries a teardown phrase. That completely changes which comps qualify — and in a
// comp-rich zip it can legitimately return NOTHING (a teardown comp must have a non-null lot size,
// which older synced comp rows lack). Showing the basis + the phrase that triggered it makes an empty
// result explicable instead of looking like a dead button.
function CompModeBar({ mode, signal, forced, onModeChange }) {
  if (!mode) return null;
  const teardown = mode === 'teardown';
  const other = teardown ? 'flip' : 'teardown';
  const accent = teardown ? 'var(--gold)' : 'var(--teal)';
  const why = forced
    ? 'pinned by you'
    : (signal ? <>auto-detected from the listing description: <em style={{ fontStyle: 'normal', fontWeight: 700 }}>“{signal}”</em></> : 'auto-detected');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 11px', marginBottom: 10,
                  borderRadius: 7, background: `${teardown ? 'rgba(224,165,38,0.08)' : 'rgba(45,143,138,0.07)'}`,
                  border: `1px solid ${teardown ? 'rgba(224,165,38,0.28)' : 'rgba(45,143,138,0.25)'}` }}>
      <span style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: accent,
                     padding: '2px 7px', borderRadius: 5, background: `${teardown ? 'rgba(224,165,38,0.16)' : 'rgba(45,143,138,0.14)'}` }}>
        {teardown ? 'Teardown comps' : 'Flip comps'}
      </span>
      <span style={{ fontSize: 11.5, color: 'var(--gray-500)', lineHeight: 1.45, flex: 1, minWidth: 200 }}>
        {teardown
          ? <>Valuing the <strong style={{ color: 'var(--white)' }}>lot</strong> from new-build sales on a like-size lot — {why}.</>
          : <>Valuing the <strong style={{ color: 'var(--white)' }}>existing house</strong> from resale comps — {why}.</>}
      </span>
      <button onClick={() => onModeChange(other)}
              style={{ background: 'transparent', color: accent, border: `1px solid ${accent}`, padding: '4px 10px',
                       borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
        Use {other} comps
      </button>
    </div>
  );
}

// The engine's chosen basis found nothing, but the OTHER basis would have. Reported rather than applied
// automatically — ARV/MAO drive real offers, so switching the valuation basis stays the user's call.
function FallbackBanner({ fallback, currentMode, onModeChange }) {
  if (!fallback) return null;
  const name = (m) => (m === 'teardown' ? 'Teardown' : 'Flip');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '9px 12px', marginBottom: 10,
                  borderRadius: 7, background: 'rgba(45,143,138,0.09)', border: '1px solid rgba(45,143,138,0.35)' }}>
      <span style={{ fontSize: 12, color: 'var(--gray-300)', lineHeight: 1.45, flex: 1, minWidth: 220 }}>
        <strong style={{ color: 'var(--white)' }}>{name(currentMode)} comps found nothing</strong>
        {' — '}{name(fallback.mode).toLowerCase()} comps found <strong style={{ color: 'var(--teal)' }}>{fallback.comp_count}</strong>
        {fallback.arv ? <> (ARV {fmtMoney(fallback.arv)})</> : null}.
      </span>
      <button onClick={() => onModeChange(fallback.mode)}
              style={{ background: 'var(--teal)', color: '#08201f', border: 'none', padding: '6px 13px', borderRadius: 6,
                       fontSize: 11.5, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
        Use {fallback.mode} comps
      </button>
    </div>
  );
}

// Cash sales (migration 089). BuyerFinancing is how the buyer actually paid — ["Cash"],
// ["Conventional"], ["VA"] — comma-joined on the row. Not ListingTerms, which is what the seller
// would ACCEPT and says "Cash, Conventional" on nearly every listing.
//
// ⚠️ MIRROR of backend/lib/compFinancing.js. CRA cannot import from backend/ (same constraint as
// savedSearchMatch.js), so the rules live in both places on purpose. Change one, change both:
// scripts/verify-comp-financing.js reads THIS file as text and fails if they disagree.
const CASH_COLOR = '#0E7C66';
// BuyerFinancing is a LIST of terms, so a TERM must be cash — not the string containing the word.
// ARMLS uses "Cash to New Loan" (the buyer took a loan), which a word match would count as cash.
const CASH_TERMS = new Set(['cash']);
const financingTerms = (row) => String((row && row.buyer_financing) || '').split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
const isCashSale = (row) => financingTerms(row).some(t => CASH_TERMS.has(t));
const hasFinancing = (row) => String((row && row.buyer_financing) || '').trim() !== '';
const isUnrepresentedBuyer = (row) => /^non[\s-]?member$/i.test(String((row && row.buyer_office_name) || '').trim());
const UNKNOWN_FINANCING_TITLE = "Financing hasn't synced for this sale yet — not the same as \u201Cnot a cash sale\u201D.";
function financingLabel(row) {
  if (!hasFinancing(row)) return { kind: 'unknown', text: '—', title: UNKNOWN_FINANCING_TITLE };
  const raw = String(row.buyer_financing);
  if (isCashSale(row)) return { kind: 'cash', text: 'Cash', title: raw };
  return { kind: 'other', text: raw.split(',')[0].trim(), title: raw };
}
function buyerOfficeLabel(row) {
  const raw = String((row && row.buyer_office_name) || '').trim();
  if (!raw) return { text: '—', title: UNKNOWN_FINANCING_TITLE };
  if (isUnrepresentedBuyer(row)) return { text: 'No buyer agent', title: 'The buyer had no MLS agent (listed as "Non Member")' };
  return { text: raw, title: raw };
}
const psfOfComp = (c) => c.price_per_sqft || (c.price && c.sqft ? Math.round(c.price / c.sqft) : null);

// The Financing cell. An unknown financing reads as a dash with a tooltip saying what the dash
// means — "no data yet" and "not a cash sale" look identical otherwise, and only one of them is
// a fact about the sale.
function financingPill(c) {
  const f = financingLabel(c);
  if (f.kind === 'cash') {
    return <span title={f.title} style={{ fontSize: 8.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.02em', padding: '2px 6px', borderRadius: 5, color: CASH_COLOR, background: `${CASH_COLOR}1f` }}>{f.text}</span>;
  }
  return <span title={f.title} style={{ color: 'var(--gray-500)', borderBottom: f.kind === 'unknown' ? '1px dotted var(--gray-500)' : 'none', cursor: f.kind === 'unknown' ? 'help' : 'inherit' }}>{f.text}</span>;
}

function ArvPanel({ comps, marketComps = [], areaSales = [], compsLoading, checkedComps, onToggleComp, arv, mao, listing, recalculating, compBound, onBoundChange, inBound, compsMeta = {}, onModeChange = () => {} }) {
  const areaCount = (areaSales || []).length;
  const [compDetail, setCompDetail] = useState(null);
  // Status visibility — CLOSED is the ARV basis and pre-selected; Active/Pending are shown when toggled.
  const [show, setShow] = useState({ closed: true, active: false, pending: false, area: false });
  // When the engine qualified nothing, open the area-sales list by default — it is the only thing on
  // screen the user can act on, and making them hunt for a toggle recreates the original dead end.
  useEffect(() => {
    setShow(v => ({ ...v, area: (comps || []).length === 0 && areaCount > 0 }));
  }, [comps, areaCount]);
  // Cash sales (migration 089): every nearby CLOSED sale the buyer paid cash for — engine comps and area
  // sales together — i.e. what property trades for on the investor market. A view, not a valuation:
  // it never checks or unchecks anything, so the ARV stays exactly what the ticked comps say.
  const [cashOnly, setCashOnly] = useState(false);
  const [showWarn, setShowWarn] = useState(false);   // active/pending-as-comp advisory
  const warnedRef = useRef(false);
  const isIn = inBound || (() => true);

  // Toggle a comp into/out of the ARV. Selecting an ACTIVE/PENDING comp raises a one-time advisory
  // (using not-yet-closed sales to justify a higher offer is not advisable).
  const toggleComp = (c) => {
    const wasChecked = checkedComps.has(c.id);
    onToggleComp(c.id);
    if (!wasChecked && (c.comp_status === 'active' || c.comp_status === 'pending') && !warnedRef.current) {
      warnedRef.current = true;
      setShowWarn(true);
    }
  };

  if (compsLoading) return <PanelShell title="Comps · Comparable sales (last 365d)"><div style={{ fontSize: 12, color: 'var(--gray-500)', padding: '12px 0' }}>Loading comps…</div></PanelShell>;
  if (comps.length === 0 && areaCount === 0) return (
    <PanelShell title="Comps · Comparable sales (last 365d)">
      <CompModeBar mode={compsMeta.mode} signal={compsMeta.signal} forced={compsMeta.forced} onModeChange={onModeChange} />
      <FallbackBanner fallback={compsMeta.fallback} currentMode={compsMeta.mode} onModeChange={onModeChange} />
      <div style={{ padding: '10px 12px', background: 'rgba(224, 165, 38,0.08)', border: '1px solid rgba(224, 165, 38,0.3)', borderRadius: 6, fontSize: 12, color: 'var(--gold)', lineHeight: 1.5 }}>
        {/* The engine's own reason — it distinguishes a teardown miss from a genuinely empty zip. */}
        {compsMeta.warning || `No qualifying MLS-sold comps in zip ${listing.zip || '?'} within last 365 days.`}
        <div style={{ marginTop: 6, color: 'var(--gray-500)' }}>
          {compsMeta.mode === 'teardown'
            ? 'Teardown comps must be new construction on a like-size lot. Switch to flip comps above to value the existing house instead.'
            : 'Manual ARV required.'}
        </div>
      </div>
    </PanelShell>
  );

  // ARV uses every CHECKED comp inside the drawn boundary (closed by default; active/pending only if the
  // user opts in past the advisory).
  // Area sales ride the same lane as active/pending market context: shown per the visibility toggle,
  // never auto-checked, and folded into the ARV only once the user ticks one.
  const market = [...(marketComps || []), ...(areaSales || [])];
  const allSelectable = [...comps, ...market];
  const checkedList = allSelectable.filter(c => checkedComps.has(c.id) && isIn(c) && Number(c.price_per_sqft) > 0);
  const avgPsf = checkedList.length ? Math.round(checkedList.reduce((s, c) => s + Number(c.price_per_sqft), 0) / checkedList.length) : 0;
  const nonClosedChecked = checkedList.some(c => c.comp_status === 'active' || c.comp_status === 'pending');

  // Every closed sale on screen, engine comps first, de-duplicated (an area sale can also be a comp).
  const closedAll = [];
  { const seen = new Set();
    for (const c of [...comps.map(c => ({ ...c, comp_status: c.comp_status || 'closed' })), ...(areaSales || [])]) {
      if (seen.has(c.id)) continue; seen.add(c.id); closedAll.push(c);
    } }
  // BuyerFinancing is only on comps synced since 089. "Unknown" and "not cash" must not read the same:
  // a market whose comps have no financing yet says so rather than reporting zero cash sales.
  const financingKnown = closedAll.some(c => c.buyer_financing);
  const cashSales = closedAll.filter(c => isCashSale(c));
  // The investor-price estimate averages LIKE-KIND cash sales only: area sales are unfiltered, and a
  // $157k one-bed condo averaged with a $3M new build describes neither. The list still shows every
  // cash sale — who is buying nearby is worth seeing whatever they bought.
  const subjType = dwellingType(listing.property_subtype);
  const cashLikeKind = cashSales.filter(c => !c.property_subtype || dwellingType(c.property_subtype) === subjType);
  const cashPsf = cashLikeKind.map(c => Number(psfOfComp(c))).filter(n => n > 0);
  const cashAvgPsf = cashPsf.length ? Math.round(cashPsf.reduce((a, b) => a + b, 0) / cashPsf.length) : 0;

  // Displayed rows: closed (ARV basis) + active/pending market context, per the visibility toggle —
  // or, with Cash sales on, every cash closed sale instead. Numbered in list order — the same number
  // labels the map pins.
  const displayComps = (cashOnly ? cashSales : [
    ...(show.closed ? comps.map(c => ({ ...c, comp_status: c.comp_status || 'closed' })) : []),
    ...market.filter(c => show[c.comp_status]),
  ]).map((c, i) => ({ ...c, _num: i + 1 }));
  const hiddenChecked = cashOnly ? checkedList.filter(c => !isCashSale(c)).length : 0;

  // Map: base color by status; any comp unchecked or outside the boundary renders translucent.
  const mapVotes = {};
  displayComps.forEach(c => { if (!(checkedComps.has(c.id) && isIn(c))) mapVotes[c.id] = 'down'; });

  const counts = {
    closed: comps.length,
    active: market.filter(c => c.comp_status === 'active').length,
    pending: market.filter(c => c.comp_status === 'pending').length,
    area: areaCount,
  };

  const th = { textAlign: 'left', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--gray-500)', fontWeight: 700, padding: '6px 7px', borderBottom: '1px solid var(--navy-border)', whiteSpace: 'nowrap' };
  const cellS = { padding: '7px', borderBottom: '1px solid var(--navy-border)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
  const poolTxt = (v) => v === 1 || v === true ? 'Yes' : v === 0 || v === false ? 'No' : '—';
  // Interior levels (migration 082). Prefer the integer; fall back to the MLS's own wording, which
  // is what carries answers like "Multi/Split" that do not reduce to a number. Reads '—' on any row
  // synced before 082 — those backfill on the next active/comp sync, not in place.
  const storiesTxt = (r) => (r?.stories != null ? String(r.stories)
    : (r?.stories_label ? String(r.stories_label).split(/[,/]/)[0].trim() : '—'));
  const psfOf = (c) => c.price_per_sqft || (c.price && c.sqft ? Math.round(c.price / c.sqft) : null);
  const closeTxt = (c) => { const d = c.closed_date || c.close_date; if (!d) return '—'; const t = new Date(d); return isNaN(t) ? '—' : t.toLocaleDateString('en-US'); };
  const statPill = (k) => { const s = COMP_STAT[k] || COMP_STAT.closed; return <span style={{ fontSize: 8.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.02em', padding: '2px 6px', borderRadius: 5, color: s.color, background: `${s.color}1f` }}>{s.label}</span>; };

  // Subject property — shown as a BOLD row above the comps for a direct column-by-column comparison.
  const subjPsf = (listing.sqft && listing.list_price) ? Math.round(listing.list_price / listing.sqft) : null;

  return (
    <PanelShell title="Comps · Comparable sales (last 365d)">
      {recalculating && <div style={{ fontSize: 11, color: 'var(--teal)', marginBottom: 8, fontWeight: 600 }}>Recalculating ARV…</div>}
      <CompModeBar mode={compsMeta.mode} signal={compsMeta.signal} forced={compsMeta.forced} onModeChange={onModeChange} />
      <FallbackBanner fallback={compsMeta.fallback} currentMode={compsMeta.mode} onModeChange={onModeChange} />
      {comps.length === 0 && areaCount > 0 && (
        <div style={{ padding: '9px 12px', marginBottom: 10, borderRadius: 7, fontSize: 12, lineHeight: 1.5,
                      background: 'rgba(139,92,246,0.09)', border: '1px solid rgba(139,92,246,0.32)', color: 'var(--gray-300)' }}>
          The comp engine qualified <strong style={{ color: 'var(--white)' }}>no</strong> comps for this property, so every
          sale within <strong style={{ color: '#8B5CF6' }}>{compsMeta.area?.radius_mi ?? 1} mi</strong> from the last{' '}
          {Math.round((compsMeta.area?.days ?? 365) / 30)} months is listed below ({areaCount}). Tick the ones you consider
          comparable — ARV and MAO update live.
        </div>
      )}
      {compsMeta.warning && (
        <div style={{ padding: '7px 11px', marginBottom: 10, borderRadius: 6, fontSize: 11.5, lineHeight: 1.45,
                      background: 'rgba(224,165,38,0.08)', border: '1px solid rgba(224,165,38,0.28)', color: 'var(--gold)' }}>
          {compsMeta.warning}
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <CompMap
          subject={{ address: listing.address, lat: listing.lat, lng: listing.lng, sqft: listing.sqft, list_price: listing.list_price }}
          comps={displayComps} votes={mapVotes} height={250}
          numbered colorByStatus enableDraw bound={compBound} onBoundChange={onBoundChange}
        />
      </div>

      {/* Status visibility toggle. Closed is the ARV basis; Active/Pending overlay market context. */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Show</span>
        {['closed', 'active', 'pending', 'area'].map(k => {
          const s = COMP_STAT[k]; const on = show[k];
          return (
            <button key={k} onClick={() => setShow(v => ({ ...v, [k]: !v[k] }))}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 8, fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
                border: `1px solid ${on ? s.color : 'var(--navy-border)'}`, background: on ? `${s.color}18` : 'var(--navy-card)', color: on ? s.color : 'var(--gray-500)' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, opacity: on ? 1 : 0.4 }} />
              {s.label} <span style={{ opacity: 0.75 }}>({counts[k]})</span>
            </button>
          );
        })}
        <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--navy-border)', margin: '0 4px' }} />
        <button onClick={() => setCashOnly(v => !v)}
          title={financingKnown ? 'Only sales the buyer paid cash for — the investor market' : 'Financing has not synced for these comps yet'}
          style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 8, fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
            border: `1px solid ${cashOnly ? CASH_COLOR : 'var(--navy-border)'}`, background: cashOnly ? `${CASH_COLOR}18` : 'var(--navy-card)', color: cashOnly ? CASH_COLOR : 'var(--gray-500)' }}>
          💵 Cash sales <span style={{ opacity: 0.75 }}>({financingKnown ? cashSales.length : '—'})</span>
        </button>
        {compBound && (
          <button onClick={() => onBoundChange && onBoundChange(null)} title="Remove the drawn boundary"
            style={{ marginLeft: 'auto', padding: '5px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer', border: '1px solid var(--navy-border)', background: 'var(--navy-card)', color: 'var(--gray-300)' }}>
            ✕ Clear boundary
          </button>
        )}
      </div>

      {cashOnly && (
        <div style={{ padding: '9px 12px', marginBottom: 10, borderRadius: 7, fontSize: 12, lineHeight: 1.5,
                      background: `${CASH_COLOR}12`, border: `1px solid ${CASH_COLOR}55`, color: 'var(--gray-300)' }}>
          {!financingKnown ? (
            <>Financing hasn't synced for these comps yet, so cash sales can't be picked out. It fills in on the next full comp sync.</>
          ) : cashSales.length === 0 ? (
            <>No cash sales among the {closedAll.length} closed sales nearby.</>
          ) : (
            <>
              <strong style={{ color: 'var(--white)' }}>{cashSales.length}</strong> of {closedAll.length} nearby sales were <strong style={{ color: CASH_COLOR }}>cash</strong>
              {cashAvgPsf ? <> · {cashPsf.length} {subjType.toLowerCase()} cash sale{cashPsf.length === 1 ? '' : 's'} avg <strong style={{ color: 'var(--white)' }}>${cashAvgPsf}/sqft</strong>
                {listing.sqft ? <> × {Number(listing.sqft).toLocaleString()} sqft = <strong style={{ color: CASH_COLOR }}>{fmtMoney(cashAvgPsf * Number(listing.sqft))}</strong> at investor prices</> : null}</> : null}.
              <span style={{ color: 'var(--gray-500)' }}> The brokerage column is who represented the buyer. The buyer's own name isn't in the MLS.</span>
            </>
          )}
          {hiddenChecked > 0 && <div style={{ marginTop: 4, color: 'var(--gray-500)' }}>ARV below still uses all {checkedList.length} checked comps, including {hiddenChecked} not shown here.</div>}
        </div>
      )}

      <div style={{ fontSize: 12.5, color: 'var(--gray-300)', marginBottom: 8, fontWeight: 600 }}>
        Select the comps to use for the ARV — checking/unchecking updates ARV &amp; MAO live.
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginBottom: 10, lineHeight: 1.5 }}>
        <strong style={{ color: 'var(--teal)' }}>{checkedList.length}</strong> comp{checkedList.length === 1 ? '' : 's'} used · avg <strong style={{ color: 'var(--gray-300)' }}>${avgPsf}/sqft</strong>
        {listing.sqft ? <> × {Number(listing.sqft).toLocaleString()} sqft = <strong style={{ color: 'var(--teal)' }}>{fmtMoney(arv || 0)}</strong> ARV · MAO <strong style={{ color: 'var(--gray-300)' }}>{fmtMoney(mao || 0)}</strong></> : null}.
        {compBound ? <span style={{ color: 'var(--teal)' }}> · limited to the drawn area</span> : null}
        {nonClosedChecked ? <span style={{ color: 'var(--gold)', fontWeight: 700 }}> · ⚠ includes not-yet-closed comps</span> : null}
        {' '}Draw a polygon on the map to bound the comps. Click a comp for photos.
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid var(--navy-border)', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5, minWidth: 1000 }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 26 }}>#</th>
              <th style={{ ...th, width: 30 }}></th>
              <th style={th}>Address</th>
              <th style={th}>Status</th>
              <th style={th}>Bd</th>
              <th style={th}>Ba</th>
              <th style={th}>Prk</th>
              <th style={th}>Sq Ft</th>
              {/* Year built and interior levels: a 1960 single-storey and a 2005 two-storey of the
                  same sqft are different products and should not read as the same comp. */}
              <th style={th}>Yr Blt</th>
              <th style={th}>Lvl</th>
              <th style={th}>Lot</th>
              <th style={th}>Pool</th>
              <th style={th}>Price</th>
              <th style={th}>$/sqft</th>
              <th style={th}>Close</th>
              {/* Buyer side of the sale (migration 089): how they paid, and who represented them. */}
              <th style={th}>Financing</th>
              <th style={th}>Buyer brokerage</th>
            </tr>
          </thead>
          <tbody>
            {/* Subject row — BOLD, for direct comparison against the comps below. */}
            <tr style={{ background: 'var(--teal-dim)', fontWeight: 700 }}>
              <td style={{ ...cellS, textAlign: 'center' }}><span style={{ width: 18, height: 18, borderRadius: '50%', background: '#16A34A', color: '#fff', display: 'inline-grid', placeItems: 'center', fontSize: 10, fontWeight: 800 }}>S</span></td>
              <td style={cellS}></td>
              <td style={{ ...cellS, whiteSpace: 'normal', color: 'var(--white)' }}>Subject — {streetLine(listing)}</td>
              <td style={cellS}><span style={{ fontSize: 8.5, fontWeight: 800, textTransform: 'uppercase', padding: '2px 6px', borderRadius: 5, color: 'var(--teal)', background: 'rgba(20,80,74,0.12)' }}>{listing.status || 'Subject'}</span></td>
              <td style={{ ...cellS, color: 'var(--white)' }}>{listing.beds ?? '—'}</td>
              <td style={{ ...cellS, color: 'var(--white)' }}>{fmtBaths(listing.baths) ?? '—'}</td>
              <td style={{ ...cellS, color: 'var(--white)' }}>{listing.garage_spaces ?? '—'}</td>
              <td style={{ ...cellS, color: 'var(--white)' }}>{listing.sqft ? Number(listing.sqft).toLocaleString() : '—'}</td>
              <td style={{ ...cellS, color: 'var(--white)' }}>{listing.year_built || '—'}</td>
              <td style={{ ...cellS, color: 'var(--white)' }}>{storiesTxt(listing)}</td>
              <td style={{ ...cellS, color: 'var(--white)' }}>{listing.lot_size_sqft ? Math.round(Number(listing.lot_size_sqft)).toLocaleString() : '—'}</td>
              <td style={{ ...cellS, color: 'var(--white)' }}>{poolTxt(listing.pool)}</td>
              <td style={{ ...cellS, color: 'var(--white)' }}>{fmtK(listing.list_price)}</td>
              <td style={{ ...cellS, color: 'var(--white)' }}>{subjPsf ? `$${subjPsf}` : '—'}</td>
              <td style={{ ...cellS, color: 'var(--gray-500)' }}>—</td>
              <td style={{ ...cellS, color: 'var(--gray-500)' }}>—</td>
              <td style={{ ...cellS, color: 'var(--gray-500)' }}>—</td>
            </tr>
            {displayComps.map(c => {
              const isClosed = (c.comp_status || 'closed') === 'closed';
              const on = checkedComps.has(c.id) && isIn(c);
              const psf = psfOf(c);
              return (
                <tr key={c.id} onClick={() => setCompDetail(c)} style={{ cursor: 'pointer', opacity: on ? 1 : 0.5, transition: 'opacity 0.2s' }}>
                  <td style={{ ...cellS, textAlign: 'center' }}>
                    <span style={{ width: 18, height: 18, borderRadius: '50%', background: (COMP_STAT[c.comp_status] || COMP_STAT.closed).color, color: '#fff', display: 'inline-grid', placeItems: 'center', fontSize: 10, fontWeight: 800 }}>{c._num}</span>
                  </td>
                  <td style={cellS} onClick={e => { e.stopPropagation(); toggleComp(c); }} title={isClosed ? 'Include in ARV' : 'Include in ARV (not-yet-closed — see advisory)'}>
                    <span style={{ width: 18, height: 18, borderRadius: 5, display: 'grid', placeItems: 'center', fontSize: 11, color: '#fff', cursor: 'pointer', border: `2px solid ${on ? 'var(--teal)' : 'var(--navy-border)'}`, background: on ? 'var(--teal)' : 'transparent' }}>{on ? '✓' : ''}</span>
                  </td>
                  <td style={{ ...cellS, whiteSpace: 'normal' }}>
                    <span style={{ color: 'var(--gray-300)', fontWeight: 500 }}>{streetLine(c)}</span>
                    {c.tier === 1 && <span style={{ fontSize: 8, fontWeight: 800, color: 'var(--gold)', background: 'rgba(224,165,38,0.15)', padding: '2px 5px', borderRadius: 4, marginLeft: 6, textTransform: 'uppercase' }}>Best</span>}
                  </td>
                  <td style={cellS}>{statPill(c.comp_status || 'closed')}</td>
                  <td style={{ ...cellS, color: 'var(--gray-300)' }}>{c.beds ?? '—'}</td>
                  <td style={{ ...cellS, color: 'var(--gray-300)' }}>{fmtBaths(c.baths) ?? '—'}</td>
                  <td style={{ ...cellS, color: 'var(--gray-500)' }}>{c.garage_spaces ?? '—'}</td>
                  <td style={{ ...cellS, color: 'var(--gray-300)' }}>{c.sqft ? c.sqft.toLocaleString() : '—'}</td>
                  <td style={{ ...cellS, color: 'var(--gray-300)' }}>{c.year_built || '—'}</td>
                  <td style={{ ...cellS, color: 'var(--gray-500)' }}>{storiesTxt(c)}</td>
                  <td style={{ ...cellS, color: 'var(--gray-500)' }}>{c.lot_size_sqft ? Math.round(Number(c.lot_size_sqft)).toLocaleString() : '—'}</td>
                  <td style={{ ...cellS, color: 'var(--gray-500)' }}>{poolTxt(c.pool)}</td>
                  <td style={{ ...cellS, color: 'var(--white)', fontWeight: 700 }}>{fmtK(c.price != null ? c.price : c.sold_price)}</td>
                  <td style={{ ...cellS, color: 'var(--gray-300)' }}>{psf ? `$${Math.round(Number(psf))}` : '—'}</td>
                  <td style={{ ...cellS, color: 'var(--gray-500)' }}>{closeTxt(c)}</td>
                  <td style={cellS}>{financingPill(c)}</td>
                  <td style={{ ...cellS, color: 'var(--gray-500)', maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis' }} title={buyerOfficeLabel(c).title}>{buyerOfficeLabel(c).text}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {compDetail && (
        <CompDetailModal
          comp={compDetail}
          subject={{ address: listing.address, lat: listing.lat, lng: listing.lng, sqft: listing.sqft }}
          onClose={() => setCompDetail(null)}
        />
      )}

      {/* Advisory shown the first time an active/pending listing is added to the ARV. */}
      {showWarn && (
        <div onClick={() => setShowWarn(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(20,18,15,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ maxWidth: 460, background: 'var(--navy-card)', border: '1px solid var(--gold)', borderRadius: 14, padding: 22, boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 22 }}>⚠️</span>
              <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--gold)' }}>Be advised</div>
            </div>
            <div style={{ fontSize: 13, color: 'var(--gray-300)', lineHeight: 1.6 }}>
              Selecting active or pending properties as comps will alter your ARV and MAO math. Using
              properties that have not closed to justify a higher purchase price is <strong style={{ color: 'var(--white)' }}>not advisable</strong>.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
              <button onClick={() => setShowWarn(false)} style={{ padding: '9px 18px', borderRadius: 9, border: 'none', background: 'var(--gold)', color: '#3a2c07', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                I understand
              </button>
            </div>
          </div>
        </div>
      )}
    </PanelShell>
  );
}

// ── Rehab Panel ───────────────────────────────────────────────────────────────

function RehabPanel({ items, setItems, onMetricEdit }) {
  const total = items.reduce((s, r) => s + (parseInt(r.amount) || 0), 0);
  return (
    <PanelShell title="Repair Estimate · Click to Edit">
      <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>Adjust line items below — total updates live</span>
        <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--gold)' }}>{fmtMoney(total)}</span>
      </div>
      {items.map((item, idx) => (
        <div key={item.category} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid rgba(232, 227, 218, 0.7)' }}>
          <span style={{ fontSize: 12, color: 'var(--gray-300)', flex: 1 }}>{item.category}</span>
          <div style={{ flex: 1, height: 4, background: 'var(--navy-border)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ width: `${total > 0 ? (item.amount / total) * 100 : 0}%`, height: '100%', background: 'var(--gold)', borderRadius: 2, transition: 'width 0.3s' }} />
          </div>
          <input
            type="number"
            value={item.amount}
            onChange={e => {
              const val = Math.max(0, parseInt(e.target.value) || 0);
              setItems(prev => prev.map((it, i) => i === idx ? { ...it, amount: val } : it));
              onMetricEdit && onMetricEdit();
            }}
            style={{ width: 80, textAlign: 'right', padding: '3px 6px', borderRadius: 6, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 12, outline: 'none' }}
          />
        </div>
      ))}
    </PanelShell>
  );
}

// ── MAO Panel ────────────────────────────────────────────────────────────────

function MaoPanel({ listing: l, arv }) {
  const repairs = l.repair_cost || 0;
  const mao70 = Math.round(arv * 0.70 - repairs);
  const mao65 = Math.round(arv * 0.65 - repairs);
  const mao75 = Math.round(arv * 0.75 - repairs);
  const margin = l.list_price && mao70 > 0 ? Math.round(((mao70 - l.list_price) / l.list_price) * 100) : null;

  return (
    <PanelShell title="Max Allowable Offer — 70% Rule">
      <div style={{ fontFamily: 'monospace', fontSize: 13, lineHeight: 2.2 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: 'var(--gray-500)' }}>ARV</span>
          <span style={{ color: 'var(--teal)', fontWeight: 700 }}>{fmtMoney(arv)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: 'var(--gray-500)' }}>× 70%</span>
          <span style={{ color: 'var(--gray-300)' }}>{fmtMoney(Math.round(arv * 0.70))}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: 'var(--gray-500)' }}>− Repairs</span>
          <span style={{ color: '#B85450' }}>({fmtMoney(repairs)})</span>
        </div>
        <div style={{ borderTop: '1px solid var(--navy-border)', marginTop: 4, paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: 'var(--white)', fontWeight: 700 }}>= MAO (Standard)</span>
          <span style={{ color: 'var(--gold)', fontWeight: 700, fontSize: 16 }}>{fmtMoney(mao70)}</span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        {[{ label: 'Conservative 65%', val: mao65, c: '#B85450' }, { label: 'Aggressive 75%', val: mao75, c: 'var(--teal)' }].map(({ label, val, c }) => (
          <div key={label} style={{ flex: 1, background: 'var(--navy)', borderRadius: 8, padding: '10px', textAlign: 'center', border: '1px solid var(--navy-border)' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: c }}>{fmtMoney(val)}</div>
            <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>
      {l.list_price && (
        <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 8, background: mao70 >= l.list_price ? 'rgba(74, 124, 89,0.08)' : 'rgba(184, 84, 80,0.08)', border: `1px solid ${mao70 >= l.list_price ? '#4A7C5940' : '#B8545040'}` }}>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 3 }}>List Price vs MAO</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: mao70 >= l.list_price ? '#4A7C59' : '#B85450' }}>
            {mao70 >= l.list_price
              ? `✓ Under MAO by ${fmtMoney(mao70 - l.list_price)} — strong deal`
              : `${fmtMoney(l.list_price - mao70)} above MAO${margin ? ` (${Math.abs(margin)}% over)` : ''} — negotiate down`}
          </div>
        </div>
      )}
    </PanelShell>
  );
}

// ── Photo Carousel ────────────────────────────────────────────────────────────

function PhotoCarousel({ photos, fill = false }) {
  const [idx, setIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  if (!photos || photos.length === 0) return null;
  const prev = (e) => { e?.stopPropagation(); setIdx(i => (i === 0 ? photos.length - 1 : i - 1)); };
  const next = (e) => { e?.stopPropagation(); setIdx(i => (i === photos.length - 1 ? 0 : i + 1)); };
  // fill: stretch the box to the height of the adjacent info column (align-items: stretch on the row),
  // so the photo's bottom lines up with the Property/Tax cards. Default: a fixed square.
  const boxStyle = fill
    ? { position: 'relative', width: 320, maxWidth: '100%', minHeight: 300, borderRadius: 10, overflow: 'hidden', background: 'var(--navy)', cursor: 'zoom-in', flexShrink: 0, alignSelf: 'stretch' }
    : { position: 'relative', width: '100%', maxWidth: 320, aspectRatio: '1 / 1', borderRadius: 10, overflow: 'hidden', background: 'var(--navy)', cursor: 'zoom-in', flexShrink: 0 };
  return (
    <>
      <div
        onClick={() => setLightbox(true)}
        title="Click to open full size"
        style={boxStyle}
      >
        <img src={proxyPhoto(photos[idx])} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        {photos.length > 1 && (
          <>
            <button onClick={prev} style={navBtn('left')}>‹</button>
            <button onClick={next} style={navBtn('right')}>›</button>
            <div style={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 4 }}>
              {photos.slice(0, 20).map((_, i) => (
                <span key={i} onClick={(e) => { e.stopPropagation(); setIdx(i); }} style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: i === idx ? '#fff' : 'rgba(255,255,255,0.4)',
                  cursor: 'pointer', transition: 'background 0.2s',
                }} />
              ))}
              {photos.length > 20 && <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 9, marginLeft: 4 }}>+{photos.length - 20}</span>}
            </div>
            <div style={{ position: 'absolute', top: 8, right: 8, padding: '3px 8px', borderRadius: 6, background: 'rgba(26, 23, 20, 0.7)', color: '#FAF7F2', fontSize: 10, fontWeight: 600 }}>
              {idx + 1} / {photos.length}
            </div>
          </>
        )}
      </div>
      {lightbox && <PhotoLightbox photos={photos} startIdx={idx} onClose={() => setLightbox(false)} />}
    </>
  );
}

// ── Fullscreen Lightbox ───────────────────────────────────────────────────────

function PhotoLightbox({ photos, startIdx = 0, onClose }) {
  const [idx, setIdx] = useState(startIdx);
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft')  setIdx(i => (i === 0 ? photos.length - 1 : i - 1));
      if (e.key === 'ArrowRight') setIdx(i => (i === photos.length - 1 ? 0 : i + 1));
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [photos.length, onClose]);
  const prev = (e) => { e.stopPropagation(); setIdx(i => (i === 0 ? photos.length - 1 : i - 1)); };
  const next = (e) => { e.stopPropagation(); setIdx(i => (i === photos.length - 1 ? 0 : i + 1)); };
  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <img
        src={proxyPhoto(photos[idx])}
        alt=""
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '92vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 6, boxShadow: '0 10px 60px rgba(0,0,0,0.6)' }}
      />
      {photos.length > 1 && (
        <>
          <button onClick={prev} style={lightBtn('left')}>‹</button>
          <button onClick={next} style={lightBtn('right')}>›</button>
        </>
      )}
      <button onClick={onClose} style={{
        position: 'fixed', top: 18, right: 22,
        background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)',
        color: '#fff', fontSize: 24, width: 40, height: 40, borderRadius: '50%',
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
      }}>×</button>
      <div style={{
        position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
        padding: '6px 14px', borderRadius: 20, background: 'rgba(0,0,0,0.55)',
        color: '#fff', fontSize: 13, fontWeight: 600,
      }}>{idx + 1} / {photos.length}</div>
    </div>
  );
}

function lightBtn(side) {
  return {
    position: 'fixed', [side]: 24, top: '50%', transform: 'translateY(-50%)',
    width: 52, height: 52, borderRadius: '50%',
    background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)',
    color: '#fff', fontSize: 28, fontWeight: 700, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, paddingBottom: 4,
  };
}

// ── External listing links (Zillow / Redfin / Realtor) ──────────────────────

function ExternalListingLinks({ listing: l }) {
  if (!l.address) return null;
  // Build a URL-friendly slug from the address
  const slug = `${l.address}-${l.city || 'Austin'}-${l.state || 'TX'}-${l.zip || ''}`
    .replace(/[^a-zA-Z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
  const zillow  = `https://www.zillow.com/homes/${slug}_rb/`;
  const linkStyle = {
    fontSize: 10, padding: '3px 9px', borderRadius: 6,
    background: 'rgba(20, 80, 74,0.1)', color: 'var(--teal)',
    border: '1px solid rgba(20, 80, 74,0.3)', textDecoration: 'none',
    fontWeight: 600, letterSpacing: '0.02em',
    display: 'inline-flex', alignItems: 'center', gap: 4,
  };
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <a href={zillow}  target="_blank" rel="noreferrer noopener" style={linkStyle} title="Open on Zillow">
        Zillow <ExtIcon />
      </a>
    </div>
  );
}

function ExtIcon() {
  return <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>;
}

// ── Previous Offers Card ─────────────────────────────────────────────────────

function PreviousOffersCard({ offers, navigate }) {
  if (!offers || offers.length === 0) return null;
  return (
    <div style={{
      marginTop: 12, padding: '12px 14px',
      background: 'rgba(20, 80, 74,0.08)', border: '1px solid rgba(20, 80, 74,0.35)',
      borderRadius: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 16 }}>⚠</span>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#14504A', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          You've Offered on This Property Before
        </div>
      </div>
      <div style={{ fontSize: 11, color: 'var(--gray-300)', marginBottom: 8 }}>
        {offers.length} prior offer{offers.length > 1 ? 's' : ''} found in your Salesforce history. Review before reaching out again.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {offers.map(o => (
          <div key={o.id}
               onClick={() => navigate(`/underwriting/${o.id}`)}
               style={{
                 padding: '8px 10px', borderRadius: 8,
                 background: 'rgba(250, 247, 242,0.5)', border: '1px solid var(--navy-border)',
                 cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
               }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-300)' }}>
                {o.stage || 'Past Deal'}
                {o.closed_date && <span style={{ color: 'var(--gray-500)', fontWeight: 400 }}> · {String(o.closed_date).split('T')[0].split(' ')[0]}</span>}
                {!o.closed_date && o.created_at && <span style={{ color: 'var(--gray-500)', fontWeight: 400 }}> · {String(o.created_at).split(' ')[0]}</span>}
              </div>
              <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 1 }}>
                Source: {o.deal_source || 'Salesforce'}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              {o.asking_price && <div style={{ fontSize: 12, color: 'var(--white)', fontWeight: 600 }}>Asked ${(o.asking_price / 1000).toFixed(0)}k</div>}
              {o.max_offer && <div style={{ fontSize: 10, color: 'var(--teal)' }}>MAO was ${(o.max_offer / 1000).toFixed(0)}k</div>}
              {o.assignment_fee > 0 && <div style={{ fontSize: 10, color: 'var(--gold)' }}>Closed fee ${(o.assignment_fee / 1000).toFixed(0)}k</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Expandable Description ───────────────────────────────────────────────────

function ExpandableDescription({ text }) {
  const [open, setOpen] = useState(false);
  const LIMIT = 220;
  const long = text.length > LIMIT;
  const shown = open || !long ? text : text.slice(0, LIMIT) + '…';
  return (
    <div
      onClick={() => long && setOpen(o => !o)}
      style={{
        fontSize: 12, color: 'var(--gray-300)', lineHeight: 1.55,
        background: 'var(--navy-card)', borderRadius: 8, padding: '10px 12px',
        margin: 0, cursor: long ? 'pointer' : 'default', position: 'relative',
        transition: 'all 0.15s',
      }}
      title={long ? (open ? 'Click to collapse' : 'Click to read full description') : ''}
    >
      <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{shown}</p>
      {long && (
        <button onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }} style={{
          marginTop: 6, padding: '2px 8px', borderRadius: 6,
          background: 'rgba(20, 80, 74,0.12)', border: '1px solid rgba(20, 80, 74,0.3)',
          color: 'var(--teal)', fontSize: 10, fontWeight: 700, cursor: 'pointer',
          letterSpacing: '0.04em', textTransform: 'uppercase',
        }}>
          {open ? '— Show less' : '+ Read more'}
        </button>
      )}
    </div>
  );
}

function navBtn(side) {
  return {
    position: 'absolute', [side]: 8, top: '50%', transform: 'translateY(-50%)',
    width: 32, height: 32, borderRadius: '50%',
    background: 'rgba(250, 247, 242,0.7)', border: '1px solid rgba(255,255,255,0.2)',
    color: '#fff', fontSize: 18, fontWeight: 700, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    lineHeight: 1, paddingBottom: 2,
  };
}

function PanelShell({ title, children }) {
  return (
    <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 10, padding: '16px', marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}

// ── Buyer Row ────────────────────────────────────────────────────────────────

function BuyerRow({ buyer, rank, onClick }) {
  const scoreColor = buyer.match_score >= 70 ? '#4A7C59' : buyer.match_score >= 50 ? '#E0A526' : 'var(--gray-500)';
  return (
    <div onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', marginBottom: 4, background: 'var(--navy-card)', border: '1px solid var(--navy-border)', transition: 'border-color 0.15s' }}
      onMouseOver={e => e.currentTarget.style.borderColor = 'var(--teal)'}
      onMouseOut={e => e.currentTarget.style.borderColor = 'var(--navy-border)'}
    >
      <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--teal-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: 'var(--teal)', flexShrink: 0 }}>{rank}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--white)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{buyer.name}</div>
        {buyer.company && <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>{buyer.company}</div>}
        {buyer.tags && buyer.tags.length > 0 && (
          <div style={{ display: 'flex', gap: 4, marginTop: 3, flexWrap: 'wrap' }}>
            {buyer.tags.slice(0, 3).map(tag => (
              <span key={tag} style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: 'rgba(20, 80, 74,0.1)', color: 'var(--teal)' }}>{tag}</span>
            ))}
          </div>
        )}
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: scoreColor }}>{buyer.match_score}%</div>
        <div style={{ width: 40, height: 3, background: 'var(--navy-border)', borderRadius: 2, overflow: 'hidden', marginTop: 3 }}>
          <div style={{ width: `${buyer.match_score}%`, height: '100%', background: scoreColor, borderRadius: 2 }} />
        </div>
      </div>
    </div>
  );
}


// ── Buyer Detail Modal ────────────────────────────────────────────────────────

function BuyerDetailModal({ buyer, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 16, width: '100%', maxWidth: 480, padding: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--white)', marginBottom: 3 }}>{buyer.name}</div>
            {buyer.company && <div style={{ fontSize: 12, color: 'var(--teal)' }}>{buyer.company}</div>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--gray-500)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          {(buyer.tags || []).map(tag => (
            <span key={tag} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 10, background: 'rgba(20, 80, 74,0.1)', color: 'var(--teal)', border: '1px solid rgba(20, 80, 74,0.2)' }}>{tag}</span>
          ))}
        </div>
        {buyer.notes && (
          <div style={{ fontSize: 12, color: 'var(--gray-300)', lineHeight: 1.6, background: 'var(--navy)', borderRadius: 8, padding: '12px', marginBottom: 16, maxHeight: 150, overflowY: 'auto' }}>{buyer.notes}</div>
        )}
        <div style={{ display: 'flex', gap: 16 }}>
          {[
            ['Match Score', `${buyer.match_score}%`, buyer.match_score >= 70 ? '#4A7C59' : '#E0A526'],
            ['Max Budget', buyer.buy_box_max_price ? fmtMoney(buyer.buy_box_max_price) : '—', 'var(--white)'],
            ['Past Buys', buyer.past_purchases || 0, 'var(--white)'],
          ].map(([label, value, color]) => (
            <div key={label} style={{ flex: 1, background: 'var(--navy)', borderRadius: 8, padding: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
              <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2 }}>{label}</div>
            </div>
          ))}
        </div>
        {(buyer.email || buyer.phone) && (
          <div style={{ marginTop: 16, fontSize: 12, color: 'var(--gray-500)', display: 'flex', gap: 16 }}>
            {buyer.email && <span>✉ {buyer.email}</span>}
            {buyer.phone && <span>📞 {buyer.phone}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Icons ────────────────────────────────────────────────────────────────────

function ListIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>;
}
function MapPinIcon({ size = 14 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>;
}
function DetailIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="18" rx="1"/><line x1="13" y1="7" x2="21" y2="7"/><line x1="13" y1="12" x2="21" y2="12"/><line x1="13" y1="17" x2="18" y2="17"/></svg>;
}
function EmailIcon() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>;
}
function DocIcon() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>;
}
function AIIcon() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>;
}
function CheckBadgeIcon() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>;
}
